
function $parcel$interopDefault(a) {
  return a && a.__esModule ? a.default : a;
}
// Thin WebGL2 helpers — context, program linking, static mesh (pos + normal + indices).
function $1659abb3fbe87a4d$export$fd42f52fd3ae1109(canvas) {
    // alpha: the canvas composites over the page's sky-gradient background.
    const gl = canvas.getContext("webgl2", {
        antialias: true,
        alpha: true,
        premultipliedAlpha: true,
        powerPreference: "high-performance"
    });
    if (!gl) throw new Error("WebGL2 not supported");
    return gl;
}
function $1659abb3fbe87a4d$var$compileShader(gl, type, src) {
    const sh = gl.createShader(type);
    if (!sh) throw new Error("Failed to create shader");
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const info = gl.getShaderInfoLog(sh) ?? "(no log)";
        gl.deleteShader(sh);
        throw new Error(`Shader compile error:\n${info}\n\n--- source ---\n${src}`);
    }
    return sh;
}
function $1659abb3fbe87a4d$export$327d24a04cd0dc17(gl, vsSrc, fsSrc) {
    const vs = $1659abb3fbe87a4d$var$compileShader(gl, gl.VERTEX_SHADER, vsSrc);
    const fs = $1659abb3fbe87a4d$var$compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
    const p = gl.createProgram();
    if (!p) throw new Error("Failed to create program");
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        const info = gl.getProgramInfoLog(p) ?? "(no log)";
        throw new Error(`Program link error:\n${info}`);
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return p;
}
function $1659abb3fbe87a4d$var$attribBuffer(gl, location, data, size) {
    const buf = gl.createBuffer();
    if (!buf) throw new Error("Failed to create buffer");
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
    return buf;
}
function $1659abb3fbe87a4d$export$d959e36e59c59ccb(gl, data) {
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("Failed to create VAO");
    gl.bindVertexArray(vao);
    const buffers = [
        $1659abb3fbe87a4d$var$attribBuffer(gl, 0, data.positions, 3),
        $1659abb3fbe87a4d$var$attribBuffer(gl, 1, data.normals, 3),
        $1659abb3fbe87a4d$var$attribBuffer(gl, 2, data.uvs, 2),
        $1659abb3fbe87a4d$var$attribBuffer(gl, 3, data.materials, 1),
        $1659abb3fbe87a4d$var$attribBuffer(gl, 4, data.tints, 1)
    ];
    const iBuf = gl.createBuffer();
    if (!iBuf) throw new Error("Failed to create buffer");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, iBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, data.indices, gl.STATIC_DRAW);
    buffers.push(iBuf);
    gl.bindVertexArray(null);
    return {
        vao: vao,
        indexCount: data.indices.length,
        dispose () {
            gl.deleteVertexArray(vao);
            for (const b of buffers)gl.deleteBuffer(b);
        }
    };
}


const $545dedaa33bafe10$export$74fce6f44befb8e8 = `#version 300 es
layout(location = 0) in vec3 aPosition;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;

uniform mat4 uMvp;
uniform mat4 uModel;

out vec3 vNormal;
out vec2 vUv;

void main() {
  vNormal = mat3(uModel) * aNormal;
  vUv = aUv;
  gl_Position = uMvp * vec4(aPosition, 1.0);
}
`;
const $545dedaa33bafe10$export$577de5190a76ff43 = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
out vec4 outColor;

uniform vec3 uColor;
uniform bool uUseTexture;
// Alpha cutout for the menu text planes (plan 17): their RGBA textures carry
// hard-edged bubble letters on a transparent background, and discarding below
// 0.5 keeps depth correct with no blend sorting. Gated so bugs are untouched.
uniform bool uAlphaCutout;
uniform sampler2D uTexture;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));

void main() {
  vec4 texel = texture(uTexture, vUv);
  if (uAlphaCutout && texel.a < 0.5) discard;
  vec3 albedo = uUseTexture ? texel.rgb : uColor;
  vec3 n = normalize(vNormal);
  // Two-sided lambert: culling is off for models, so flip the normal to face
  // the light when a back face shows (thin legs, single-sided bits).
  float diffuse = abs(dot(n, LIGHT_DIR));
  float ambient = 0.5;
  outColor = vec4(albedo * (ambient + 0.65 * diffuse), 1.0);
}
`;
const $545dedaa33bafe10$export$e8c2250f9a0c6e24 = `#version 300 es
layout(location = 0) in vec3 aPosition;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;
layout(location = 3) in float aMaterial;
layout(location = 4) in float aTint;

uniform mat4 uMvp;
uniform mat4 uModel;

out vec3 vNormal;
out vec2 vUv;
out float vMaterial;
out float vTint;

void main() {
  vNormal = mat3(uModel) * aNormal;
  vUv = aUv;
  vMaterial = aMaterial;
  vTint = aTint;
  gl_Position = uMvp * vec4(aPosition, 1.0);
}
`;
const $545dedaa33bafe10$export$e5cd4f3fc4aedb53 = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
in float vMaterial;
in float vTint;
out vec4 outColor;

// Original block atlas (subblock_1536_tex.png): 3x3 grid of 512px tiles.
// Tile coordinates count from the bottom (the texture is flipped on upload).
// The atlas rows are ONION DEPTHS, not block states (unity-overview.md \xa76):
// row 2 is the grass crust, row 1 the magma layer beneath it, and (0,0) is the
// stone used for faces pointing inward. The index is a TileId from
// render/onion.ts, which mirrors what the original's per-zone block meshes
// bake into their own UVs. (No backticks in here: this is a template literal.)
uniform sampler2D uAtlas;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));

const vec2 TILES[5] = vec2[5](
  vec2(1.0, 2.0), // 0 grass top   - depth 0, outward
  vec2(0.0, 2.0), // 1 grass side  - depth 0, tangential
  vec2(0.0, 1.0), // 2 magma top   - depth 1+, outward
  vec2(1.0, 1.0), // 3 magma side  - depth 1, tangential
  vec2(0.0, 0.0)  // 4 stone       - any depth, inward
);

// An infected block keeps its depth's tile and is scorched instead. The
// original shows infection not at all (unity-overview.md \xa76: no material swap
// exists, and its Infected stage lasts one frame), so this is a port of the
// scrapped intent rather than of shipped behaviour: our infection spans three
// bites over several seconds and has to be legible.
const vec3 INFECTED_TINT = vec3(0.62, 0.30, 0.22);

vec2 atlasUv(vec2 uv, vec2 tile) {
  // Inset each tile slightly so mipmapped sampling does not bleed neighbours.
  return (tile + 0.012 + uv * 0.976) / 3.0;
}

void main() {
  vec2 uv = atlasUv(vUv, TILES[int(vMaterial + 0.5)]);
  vec3 albedo = texture(uAtlas, uv).rgb;
  albedo = mix(albedo, albedo * INFECTED_TINT, vTint);

  // Production-matched lighting: the original renders the grass tile at
  // ~1.65x its raw brightness on the facing side (measured off a production
  // screenshot), with no seam or edge darkening \u{2014} the punchy, almost unlit
  // look comes from over-driving the texture.
  vec3 n = normalize(vNormal);
  float diffuse = max(dot(n, LIGHT_DIR), 0.0);
  outColor = vec4(albedo * (1.15 + 0.85 * diffuse), 1.0);
}
`;
const $545dedaa33bafe10$export$bdbe9d546ec08ec1 = `#version 300 es
precision highp float;

in vec3 vNormal;
in vec2 vUv;
in float vMaterial;
in float vTint;
out vec4 outColor;

const vec3 LIGHT_DIR = normalize(vec3(0.4, 0.7, 0.6));
const vec4 GHOST_COLOR = vec4(0.137, 0.682, 0.0, 0.463);
const vec3 GHOST_EMISSIVE = vec3(0.0, 0.682, 0.860);

void main() {
  vec3 n = normalize(vNormal);
  float diffuse = max(dot(n, LIGHT_DIR), 0.0);
  // The colour and alpha are measured off the original's material; this weight
  // on the emissive is not \u{2014} it is the one free parameter here. Keep it low:
  // the shell has to stay glass. At 0.35 two stacked shells turned the magma
  // behind them into cyan mud, which is the failure mode to watch for.
  vec3 rgb = GHOST_COLOR.rgb * (0.7 + 0.3 * diffuse) + GHOST_EMISSIVE * 0.12;
  outColor = vec4(rgb, GHOST_COLOR.a);
}
`;


// What the player tapped. View-side by necessity (00-architecture.md §1): it
// needs the camera, the canvas and the bugs' world transforms — the sim only
// ever sees the `Command` that comes out of it.
//
// The ray is cast from the camera through a screen point into *cube-local*
// space, where both tests live: a sphere per living bug, and the grid march
// (Amanatides & Woo DDA) that finds the first solid block.
//
// Ports the picking half of `Cube.MouseInteractionWithObjects`, which raycasts
// the whole scene, sorts the hits by distance and takes the first `bug*`,
// `powerup*` or `hit{xyz}` collider. Apples are deliberately not pickable —
// the original only ever names those three.
// Camera framing shared by rendering (main.ts) and picking (render/picking.ts).
//
// The design FOV applies to the *shorter* screen axis: in landscape it is the
// vertical FOV as-is; in portrait the vertical FOV widens so the horizontal
// FOV stays at the design value and the cube never overflows the narrow side.
// The camera itself never moves, so the cube's on-screen size relative to the
// shorter axis is identical in both orientations.
const $238457a12dbae9c5$var$FOV = Math.PI / 4; // design FOV along the shorter screen axis
const $238457a12dbae9c5$var$DIST = 2.2; // chosen so the cube nearly fills the shorter screen axis
function $238457a12dbae9c5$export$8b7b81ac271094c6(aspect) {
    const fovY = aspect >= 1 ? $238457a12dbae9c5$var$FOV : 2 * Math.atan(Math.tan($238457a12dbae9c5$var$FOV / 2) / aspect);
    return {
        fovY: fovY,
        aspect: aspect,
        dist: $238457a12dbae9c5$var$DIST
    };
}


// Bug entities and their lifecycle state machine (Unity `Bug.cs` and the
// per-bug state handling in `BugsModel.cs`; see `unity-overview.md` §3
// "Bug types"). Pure logic: no WebGL, no DOM — the renderer reads this state
// and the view subscribes to the bus for spawn/state/removal notifications.
// Surface kinematics: the single source of truth for where a walker is on the
// cube and how it moves. Positions are face-local `(face, u, v)` cell
// coordinates plus a 4-way heading; movement crosses face edges by folding the
// 3D geometry (never a hand-written remap table — those are a sign-bug farm).
//
// Pure math: no WebGL, no DOM (00-architecture.md §1).
/** Face order is canonical (00-architecture.md §7): +X, -X, +Y, -Y, +Z, -Z. */ const $733d112f9a19cd2b$export$a07804094c3ecf2d = 5;
/** World size of one cell; the cube spans [-0.5, 0.5]. */ const $733d112f9a19cd2b$var$CELL = 1 / $733d112f9a19cd2b$export$a07804094c3ecf2d;
const $733d112f9a19cd2b$var$HALF = 0.5;
/** Rounding radius of the cube's outer edges, world units (§7, matches grid.ts). */ const $733d112f9a19cd2b$var$ROUND_RADIUS = 0.1;
/** Half-size of the box the rounding is swept around: 0.5 - r = 0.4. */ const $733d112f9a19cd2b$var$INNER = $733d112f9a19cd2b$var$HALF - $733d112f9a19cd2b$var$ROUND_RADIUS;
/** Sampling distance (cells) used to finite-difference the forward vector. */ const $733d112f9a19cd2b$var$FORWARD_EPS = 0.01;
const $733d112f9a19cd2b$export$6349a8ed6fe8449e = [
    0,
    1,
    2,
    3,
    4,
    5
];
const $733d112f9a19cd2b$export$a2cefd738759134a = [
    0,
    1,
    2,
    3
];
// Per-face bases, chosen so uAxis x vAxis = normal (right-handed) and the six
// faces tile the cube consistently. THIS TABLE IS THE CONVENTION — everything
// else in the file is derived from it.
const $733d112f9a19cd2b$var$AXES = [
    {
        normal: [
            1,
            0,
            0
        ],
        uAxis: [
            0,
            0,
            -1
        ],
        vAxis: [
            0,
            1,
            0
        ]
    },
    {
        normal: [
            -1,
            0,
            0
        ],
        uAxis: [
            0,
            0,
            1
        ],
        vAxis: [
            0,
            1,
            0
        ]
    },
    {
        normal: [
            0,
            1,
            0
        ],
        uAxis: [
            1,
            0,
            0
        ],
        vAxis: [
            0,
            0,
            -1
        ]
    },
    {
        normal: [
            0,
            -1,
            0
        ],
        uAxis: [
            1,
            0,
            0
        ],
        vAxis: [
            0,
            0,
            1
        ]
    },
    {
        normal: [
            0,
            0,
            1
        ],
        uAxis: [
            1,
            0,
            0
        ],
        vAxis: [
            0,
            1,
            0
        ]
    },
    {
        normal: [
            0,
            0,
            -1
        ],
        uAxis: [
            -1,
            0,
            0
        ],
        vAxis: [
            0,
            1,
            0
        ]
    }
];
const $733d112f9a19cd2b$export$c03c71dce639df5e = $733d112f9a19cd2b$var$AXES.map((a)=>({
        ...a,
        origin: [
            $733d112f9a19cd2b$var$HALF * a.normal[0] - $733d112f9a19cd2b$var$HALF * a.uAxis[0] - $733d112f9a19cd2b$var$HALF * a.vAxis[0],
            $733d112f9a19cd2b$var$HALF * a.normal[1] - $733d112f9a19cd2b$var$HALF * a.uAxis[1] - $733d112f9a19cd2b$var$HALF * a.vAxis[1],
            $733d112f9a19cd2b$var$HALF * a.normal[2] - $733d112f9a19cd2b$var$HALF * a.uAxis[2] - $733d112f9a19cd2b$var$HALF * a.vAxis[2]
        ]
    }));
function $733d112f9a19cd2b$var$clamp(x, lo, hi) {
    return x < lo ? lo : x > hi ? hi : x;
}
/** World direction of a face-local heading. */ function $733d112f9a19cd2b$var$dirVector(face, dir) {
    const f = $733d112f9a19cd2b$export$c03c71dce639df5e[face];
    switch(dir){
        case 0:
            return f.uAxis;
        case 1:
            return f.vAxis;
        case 2:
            return [
                -f.uAxis[0],
                -f.uAxis[1],
                -f.uAxis[2]
            ];
        case 3:
            return [
                -f.vAxis[0],
                -f.vAxis[1],
                -f.vAxis[2]
            ];
    }
}
/** True when `dir` moves along u (rather than v). */ function $733d112f9a19cd2b$var$isAlongU(dir) {
    return dir === 0 || dir === 2;
}
function $733d112f9a19cd2b$var$sharpPoint(face, u, v) {
    const f = $733d112f9a19cd2b$export$c03c71dce639df5e[face];
    const su = u * $733d112f9a19cd2b$var$CELL;
    const sv = v * $733d112f9a19cd2b$var$CELL;
    return [
        f.origin[0] + su * f.uAxis[0] + sv * f.vAxis[0],
        f.origin[1] + su * f.uAxis[1] + sv * f.vAxis[1],
        f.origin[2] + su * f.uAxis[2] + sv * f.vAxis[2]
    ];
}
function $733d112f9a19cd2b$export$d31b767ecbdaacde(p) {
    return $733d112f9a19cd2b$var$sharpPoint(p.face, p.u, p.v);
}
function $733d112f9a19cd2b$export$3e4c838ee1c8be8a(sharp) {
    const cx = $733d112f9a19cd2b$var$clamp(sharp[0], -$733d112f9a19cd2b$var$INNER, $733d112f9a19cd2b$var$INNER);
    const cy = $733d112f9a19cd2b$var$clamp(sharp[1], -$733d112f9a19cd2b$var$INNER, $733d112f9a19cd2b$var$INNER);
    const cz = $733d112f9a19cd2b$var$clamp(sharp[2], -$733d112f9a19cd2b$var$INNER, $733d112f9a19cd2b$var$INNER);
    const dx = sharp[0] - cx;
    const dy = sharp[1] - cy;
    const dz = sharp[2] - cz;
    const len = Math.hypot(dx, dy, dz);
    const nx = dx / len;
    const ny = dy / len;
    const nz = dz / len;
    return {
        pos: [
            cx + $733d112f9a19cd2b$var$ROUND_RADIUS * nx,
            cy + $733d112f9a19cd2b$var$ROUND_RADIUS * ny,
            cz + $733d112f9a19cd2b$var$ROUND_RADIUS * nz
        ],
        normal: [
            nx,
            ny,
            nz
        ]
    };
}
function $733d112f9a19cd2b$export$fae1f8cf88817f3a(p) {
    const here = $733d112f9a19cd2b$export$3e4c838ee1c8be8a($733d112f9a19cd2b$export$d31b767ecbdaacde(p));
    const ahead = $733d112f9a19cd2b$export$3e4c838ee1c8be8a($733d112f9a19cd2b$export$d31b767ecbdaacde($733d112f9a19cd2b$export$35d233f3b2dbf62a(p, $733d112f9a19cd2b$var$FORWARD_EPS)));
    const dx = ahead.pos[0] - here.pos[0];
    const dy = ahead.pos[1] - here.pos[1];
    const dz = ahead.pos[2] - here.pos[2];
    const len = Math.hypot(dx, dy, dz);
    // Degenerate only if the projection collapses the step, which the rounded
    // box never does; fall back to the sharp tangent rather than emit NaNs.
    const forward = len > 1e-12 ? [
        dx / len,
        dy / len,
        dz / len
    ] : $733d112f9a19cd2b$var$dirVector(p.face, p.dir);
    return {
        pos: here.pos,
        normal: here.normal,
        forward: forward
    };
}
function $733d112f9a19cd2b$export$17fffb2ca18f2877(face, u, v, du, dv) {
    const here = $733d112f9a19cd2b$export$3e4c838ee1c8be8a($733d112f9a19cd2b$var$sharpPoint(face, u, v));
    const ahead = $733d112f9a19cd2b$export$3e4c838ee1c8be8a($733d112f9a19cd2b$var$sharpPoint(face, u + du * $733d112f9a19cd2b$var$FORWARD_EPS, v + dv * $733d112f9a19cd2b$var$FORWARD_EPS));
    const dx = ahead.pos[0] - here.pos[0];
    const dy = ahead.pos[1] - here.pos[1];
    const dz = ahead.pos[2] - here.pos[2];
    const len = Math.hypot(dx, dy, dz);
    const forward = len > 1e-12 ? [
        dx / len,
        dy / len,
        dz / len
    ] : $733d112f9a19cd2b$export$c03c71dce639df5e[face].uAxis;
    return {
        pos: here.pos,
        normal: here.normal,
        forward: forward
    };
}
const $733d112f9a19cd2b$export$1c1343d814ee6703 = [
    [
        1,
        0
    ],
    [
        0,
        1
    ],
    [
        -1,
        0
    ],
    [
        0,
        -1
    ]
];
function $733d112f9a19cd2b$export$50bbbfe3556a9553(p) {
    const along = $733d112f9a19cd2b$var$isAlongU(p.dir) ? p.u : p.v;
    const frac = along - Math.floor(along);
    const forward = p.dir === 0 || p.dir === 1;
    if (frac === 0) return 1;
    return forward ? 1 - frac : frac;
}
function $733d112f9a19cd2b$export$1a4cb2db2ce65a31(p) {
    const f = $733d112f9a19cd2b$export$c03c71dce639df5e[p.face];
    const s = $733d112f9a19cd2b$var$sharpPoint(p.face, Math.floor(p.u) + 0.5, Math.floor(p.v) + 0.5);
    const inward = $733d112f9a19cd2b$var$CELL / 2;
    return {
        x: $733d112f9a19cd2b$var$cellIndex(s[0] - f.normal[0] * inward),
        y: $733d112f9a19cd2b$var$cellIndex(s[1] - f.normal[1] * inward),
        z: $733d112f9a19cd2b$var$cellIndex(s[2] - f.normal[2] * inward)
    };
}
function $733d112f9a19cd2b$var$cellIndex(world) {
    return $733d112f9a19cd2b$var$clamp(Math.floor((world + $733d112f9a19cd2b$var$HALF) / $733d112f9a19cd2b$var$CELL), 0, $733d112f9a19cd2b$export$a07804094c3ecf2d - 1);
}
/**
 * Fold one crossing in 3D: a walker on `face` heading `dir` reaches the edge at
 * transverse coordinate `t`. The face it steps onto is the one whose normal is
 * the *heading* it arrived with, and its new heading is the old face's outward
 * normal reversed (it is now walking away from the face it came from).
 */ function $733d112f9a19cd2b$var$foldCrossing(face, dir, t) {
    const alongU = $733d112f9a19cd2b$var$isAlongU(dir);
    const atMax = dir === 0 || dir === 1;
    const u = alongU ? atMax ? $733d112f9a19cd2b$export$a07804094c3ecf2d : 0 : t;
    const v = alongU ? t : atMax ? $733d112f9a19cd2b$export$a07804094c3ecf2d : 0;
    const p = $733d112f9a19cd2b$var$sharpPoint(face, u, v);
    const heading = $733d112f9a19cd2b$var$dirVector(face, dir);
    const nf = $733d112f9a19cd2b$export$c03c71dce639df5e[face].normal;
    const newFace = $733d112f9a19cd2b$var$faceWithNormal(heading);
    const newDir = $733d112f9a19cd2b$var$dirWithVector(newFace, [
        -nf[0],
        -nf[1],
        -nf[2]
    ]);
    const g = $733d112f9a19cd2b$export$c03c71dce639df5e[newFace];
    const rx = p[0] - g.origin[0];
    const ry = p[1] - g.origin[1];
    const rz = p[2] - g.origin[2];
    const gu = (rx * g.uAxis[0] + ry * g.uAxis[1] + rz * g.uAxis[2]) / $733d112f9a19cd2b$var$CELL;
    const gv = (rx * g.vAxis[0] + ry * g.vAxis[1] + rz * g.vAxis[2]) / $733d112f9a19cd2b$var$CELL;
    // The entry point lies on the shared edge, so the coordinate along `newDir`
    // is 0 by construction; only the transverse one carries information.
    return {
        face: newFace,
        dir: newDir,
        t: $733d112f9a19cd2b$var$isAlongU(newDir) ? gv : gu
    };
}
function $733d112f9a19cd2b$var$faceWithNormal(n) {
    for (const f of $733d112f9a19cd2b$export$6349a8ed6fe8449e){
        const m = $733d112f9a19cd2b$export$c03c71dce639df5e[f].normal;
        if (m[0] * n[0] + m[1] * n[1] + m[2] * n[2] > 0.9) return f;
    }
    throw new Error(`no face with normal ${n.join(",")}`);
}
function $733d112f9a19cd2b$var$dirWithVector(face, w) {
    for (const d of $733d112f9a19cd2b$export$a2cefd738759134a){
        const v = $733d112f9a19cd2b$var$dirVector(face, d);
        if (v[0] * w[0] + v[1] * w[1] + v[2] * w[2] > 0.9) return d;
    }
    throw new Error(`no dir on face ${face} along ${w.join(",")}`);
}
/**
 * `ADJACENCY[face][exitDir]` — built once by folding the geometry above, so the
 * 24 entries are derived, not typed in. The transverse remap is affine with
 * scale +/-1, recovered by folding two sample coordinates and fitting.
 */ const $733d112f9a19cd2b$var$ADJACENCY = $733d112f9a19cd2b$export$6349a8ed6fe8449e.map((face)=>$733d112f9a19cd2b$export$a2cefd738759134a.map((dir)=>{
        const a = $733d112f9a19cd2b$var$foldCrossing(face, dir, 0);
        const b = $733d112f9a19cd2b$var$foldCrossing(face, dir, $733d112f9a19cd2b$export$a07804094c3ecf2d);
        const scale = (b.t - a.t) / $733d112f9a19cd2b$export$a07804094c3ecf2d;
        return {
            face: a.face,
            dir: a.dir,
            scale: scale,
            offset: a.t
        };
    }));
/** Cells left on the current face before `dir` runs off its edge. */ function $733d112f9a19cd2b$var$distanceToEdge(p) {
    switch(p.dir){
        case 0:
            return $733d112f9a19cd2b$export$a07804094c3ecf2d - p.u;
        case 1:
            return $733d112f9a19cd2b$export$a07804094c3ecf2d - p.v;
        case 2:
            return p.u;
        case 3:
            return p.v;
    }
}
function $733d112f9a19cd2b$var$stepOnFace(p, d) {
    switch(p.dir){
        case 0:
            return {
                ...p,
                u: p.u + d
            };
        case 1:
            return {
                ...p,
                v: p.v + d
            };
        case 2:
            return {
                ...p,
                u: p.u - d
            };
        case 3:
            return {
                ...p,
                v: p.v - d
            };
    }
}
/** Step onto the neighbouring face, entering at the shared edge. */ function $733d112f9a19cd2b$var$crossEdge(p) {
    const link = $733d112f9a19cd2b$var$ADJACENCY[p.face][p.dir];
    const t = $733d112f9a19cd2b$var$clamp(link.scale * ($733d112f9a19cd2b$var$isAlongU(p.dir) ? p.v : p.u) + link.offset, 0, $733d112f9a19cd2b$export$a07804094c3ecf2d);
    const dir = link.dir;
    // Entering along `dir` means the along-coordinate starts at the edge: 0 for a
    // +U/+V heading, N for a -U/-V one. Either way a full face-run lies ahead.
    if ($733d112f9a19cd2b$var$isAlongU(dir)) return {
        face: link.face,
        dir: dir,
        u: dir === 0 ? 0 : $733d112f9a19cd2b$export$a07804094c3ecf2d,
        v: t
    };
    return {
        face: link.face,
        dir: dir,
        u: t,
        v: dir === 1 ? 0 : $733d112f9a19cd2b$export$a07804094c3ecf2d
    };
}
function $733d112f9a19cd2b$export$35d233f3b2dbf62a(p, distance) {
    if (distance < 0) return $733d112f9a19cd2b$export$adc5b71740b34e28($733d112f9a19cd2b$export$35d233f3b2dbf62a($733d112f9a19cd2b$export$adc5b71740b34e28(p), -distance));
    let pose = p;
    let left = distance;
    // Every crossing leaves a full face-run (N cells) ahead, so this terminates.
    while(left > 0){
        const run = $733d112f9a19cd2b$var$distanceToEdge(pose);
        if (left < run) return $733d112f9a19cd2b$var$stepOnFace(pose, left);
        left -= run;
        pose = $733d112f9a19cd2b$var$crossEdge($733d112f9a19cd2b$var$stepOnFace(pose, run));
    }
    return pose;
}
// Quarter turns. +V is 90 degrees counter-clockwise from +U seen from outside
// the face (uAxis x vAxis = normal), so turning left advances the dir index.
const $733d112f9a19cd2b$var$LEFT = [
    1,
    2,
    3,
    0
];
const $733d112f9a19cd2b$var$RIGHT = [
    3,
    0,
    1,
    2
];
const $733d112f9a19cd2b$var$BACK = [
    2,
    3,
    0,
    1
];
function $733d112f9a19cd2b$export$feaf0c0bac3a05b(p) {
    return {
        ...p,
        dir: $733d112f9a19cd2b$var$LEFT[p.dir]
    };
}
function $733d112f9a19cd2b$export$af85df91db0258da(p) {
    return {
        ...p,
        dir: $733d112f9a19cd2b$var$RIGHT[p.dir]
    };
}
function $733d112f9a19cd2b$export$adc5b71740b34e28(p) {
    return {
        ...p,
        dir: $733d112f9a19cd2b$var$BACK[p.dir]
    };
}


// Surface-tile occupancy: which walker claims which cell of which face. The
// Unity original keeps per-block occupant lists (`BlockOccupant`,
// `AdaptBlockOccupationData` in `Assets/Code/BugsModel.cs`); this port needs
// only the surface half of that, so one flat map keyed by tile is enough.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

// Tuning data (00-architecture.md §6). Values come from `unity-overview.md`,
// cited by section; later plans extend this file.
// Type-only import: erased at build time, so the defs <-> bugs cycle never
// exists at runtime.
const $0f82c75150dd11fd$export$fb189d3247a5c5ae = 0.33;
const $0f82c75150dd11fd$export$711e4dd88b9f3387 = 1;
const $0f82c75150dd11fd$export$70d15fa4974a234f = 20;
const $0f82c75150dd11fd$export$cb53ca73bf6aa717 = 0.8;
const $0f82c75150dd11fd$export$a0cba50fed652a94 = {
    standard: {
        speed: 1,
        points: 100,
        affectedByFreeze: true
    },
    lady: {
        speed: 1,
        points: 1000,
        affectedByFreeze: true
    },
    fat: {
        speed: 2 / 3,
        points: 800,
        affectedByFreeze: true
    },
    speed: {
        speed: 5 / 3,
        points: 200,
        affectedByFreeze: true
    },
    spy: {
        speed: 4 / 3,
        points: 500,
        affectedByFreeze: true
    },
    spike: {
        speed: 4 / 3,
        points: 500,
        affectedByFreeze: true
    },
    black: {
        speed: 4 / 3,
        points: 0,
        affectedByFreeze: false
    }
};
const $0f82c75150dd11fd$export$7d2b4216cca8b699 = 1;
const $0f82c75150dd11fd$export$c98bbe58eb39170d = 0.55;
const $0f82c75150dd11fd$export$17923b10fb784872 = 10 / 3;
const $0f82c75150dd11fd$export$3ff1482ae9cd1fde = 121.5;
const $0f82c75150dd11fd$export$c9aa3efc1c3b1e53 = 3;
const $0f82c75150dd11fd$export$fdebad4c864d02b2 = 0.01;
const $0f82c75150dd11fd$export$97c0383a8185747f = 1.8;
const $0f82c75150dd11fd$export$89a9536059633afb = [
    "standard",
    "lady",
    "fat",
    "speed",
    "spy",
    "spike",
    "black"
];
const $0f82c75150dd11fd$export$834c2ad6e999b78f = [
    {
        activeAfterSeconds: 1,
        occurrenceWeights: [
            100,
            0,
            0,
            0,
            0,
            0,
            0
        ],
        maxBugs: 12,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.0,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            100,
            0
        ]
    },
    {
        activeAfterSeconds: 30,
        occurrenceWeights: [
            70,
            0,
            30,
            0,
            0,
            0,
            0
        ],
        maxBugs: 12,
        meanTimeBetweenBugSpawn: 4,
        spawnPoints: 2,
        speedFactor: 1.01,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            100,
            0
        ]
    },
    {
        activeAfterSeconds: 60,
        occurrenceWeights: [
            40,
            15,
            35,
            0,
            0,
            0,
            10
        ],
        maxBugs: 12,
        meanTimeBetweenBugSpawn: 3.5,
        spawnPoints: 2,
        speedFactor: 1.02,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            5,
            5,
            0,
            0,
            0,
            0,
            0,
            0,
            40,
            50
        ]
    },
    {
        activeAfterSeconds: 90,
        occurrenceWeights: [
            30,
            10,
            25,
            25,
            0,
            0,
            10
        ],
        maxBugs: 12,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.0,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            0,
            0,
            0,
            0,
            0,
            0,
            45,
            40
        ]
    },
    {
        activeAfterSeconds: 120,
        occurrenceWeights: [
            20,
            10,
            15,
            15,
            15,
            15,
            10
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.03,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            0,
            0,
            0,
            0,
            20,
            0,
            0,
            0,
            40,
            40
        ]
    },
    {
        activeAfterSeconds: 150,
        occurrenceWeights: [
            0,
            85,
            0,
            0,
            0,
            0,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.04,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            30,
            30,
            0,
            0,
            0,
            0,
            0,
            0,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 180,
        occurrenceWeights: [
            30,
            20,
            0,
            20,
            15,
            0,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.05,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            10,
            10,
            10,
            10,
            0,
            0,
            0,
            20,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 210,
        occurrenceWeights: [
            0,
            15,
            35,
            25,
            10,
            0,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.06,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            10,
            0,
            10,
            0,
            10,
            0,
            10,
            20,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 240,
        occurrenceWeights: [
            5,
            10,
            30,
            0,
            20,
            20,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.07,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            10,
            10,
            10,
            0,
            0,
            0,
            10,
            20,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 270,
        occurrenceWeights: [
            0,
            10,
            0,
            0,
            75,
            0,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.08,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            20,
            10,
            0,
            10,
            0,
            0,
            10,
            20,
            10,
            20
        ]
    },
    {
        activeAfterSeconds: 300,
        occurrenceWeights: [
            0,
            60,
            0,
            25,
            0,
            0,
            15
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.09,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            30,
            30,
            0,
            0,
            0,
            0,
            0,
            0,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 330,
        occurrenceWeights: [
            0,
            10,
            0,
            0,
            0,
            70,
            20
        ],
        maxBugs: 13,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.1,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            0,
            10,
            0,
            10,
            0,
            0,
            10,
            20,
            30,
            20
        ]
    },
    {
        activeAfterSeconds: 360,
        occurrenceWeights: [
            5,
            10,
            15,
            15,
            15,
            20,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.11,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            0,
            5,
            10,
            0,
            0,
            5,
            10,
            20,
            30,
            20
        ]
    },
    {
        activeAfterSeconds: 390,
        occurrenceWeights: [
            10,
            10,
            15,
            15,
            20,
            15,
            15
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.12,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            20,
            0,
            0,
            10,
            0,
            5,
            20,
            0,
            25,
            20
        ]
    },
    {
        activeAfterSeconds: 420,
        occurrenceWeights: [
            0,
            10,
            15,
            15,
            20,
            20,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.13,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            0,
            10,
            10,
            10,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    },
    {
        activeAfterSeconds: 450,
        occurrenceWeights: [
            0,
            80,
            0,
            0,
            0,
            0,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.14,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            30,
            30,
            0,
            0,
            0,
            0,
            0,
            0,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 480,
        occurrenceWeights: [
            10,
            5,
            10,
            15,
            25,
            25,
            10
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.08,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            10,
            10,
            10,
            0,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    },
    {
        activeAfterSeconds: 510,
        occurrenceWeights: [
            0,
            5,
            25,
            10,
            20,
            20,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.15,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            10,
            5,
            0,
            20,
            10,
            5,
            10,
            10,
            20,
            10
        ]
    },
    {
        activeAfterSeconds: 540,
        occurrenceWeights: [
            5,
            5,
            20,
            15,
            20,
            20,
            15
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 2,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            5,
            10,
            10,
            20,
            10,
            5,
            10,
            10,
            10,
            10
        ]
    },
    {
        activeAfterSeconds: 570,
        occurrenceWeights: [
            5,
            10,
            15,
            15,
            20,
            20,
            15
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 3,
        powerUpWeights: [
            0,
            10,
            10,
            20,
            10,
            5,
            10,
            10,
            15,
            10
        ]
    },
    {
        activeAfterSeconds: 600,
        occurrenceWeights: [
            0,
            10,
            30,
            30,
            10,
            0,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            0,
            10,
            10,
            20,
            10,
            5,
            10,
            10,
            15,
            10
        ]
    },
    {
        activeAfterSeconds: 630,
        occurrenceWeights: [
            0,
            10,
            0,
            0,
            40,
            30,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.12,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            0,
            10,
            10,
            10,
            10,
            5,
            10,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 660,
        occurrenceWeights: [
            10,
            5,
            30,
            30,
            0,
            0,
            25
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.12,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            0,
            10,
            10,
            10,
            10,
            5,
            10,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 690,
        occurrenceWeights: [
            30,
            10,
            0,
            0,
            10,
            30,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.12,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 720,
        occurrenceWeights: [
            10,
            10,
            30,
            0,
            30,
            0,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.12,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 750,
        occurrenceWeights: [
            0,
            10,
            0,
            30,
            0,
            40,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 780,
        occurrenceWeights: [
            10,
            10,
            15,
            10,
            20,
            15,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 810,
        occurrenceWeights: [
            5,
            10,
            20,
            15,
            15,
            20,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 840,
        occurrenceWeights: [
            10,
            10,
            15,
            15,
            15,
            20,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 2,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    },
    {
        activeAfterSeconds: 870,
        occurrenceWeights: [
            10,
            10,
            10,
            10,
            10,
            20,
            30
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.16,
        meanTimeBetweenPowerUpCreation: 5,
        powerUpWeights: [
            5,
            10,
            10,
            10,
            10,
            5,
            5,
            10,
            25,
            10
        ]
    }
];
const $0f82c75150dd11fd$export$be50db5c8ae88515 = $0f82c75150dd11fd$export$834c2ad6e999b78f[0];
const $0f82c75150dd11fd$export$c30172bbd239dab7 = 10;
const $0f82c75150dd11fd$export$47c13fcaa458852e = [
    {
        name: "easy",
        speedMultiplier: 1.0,
        winAfterLevel: $0f82c75150dd11fd$export$c30172bbd239dab7
    },
    {
        name: "normal",
        speedMultiplier: 1.0,
        winAfterLevel: 15
    },
    {
        name: "hard",
        speedMultiplier: 1.1,
        winAfterLevel: 20
    }
];
const $0f82c75150dd11fd$export$fd8ee1a0605533d4 = [
    {
        activeAfterSeconds: 1,
        occurrenceWeights: [
            100,
            0,
            0,
            0,
            0,
            0,
            0
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.0,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            50,
            50
        ]
    },
    {
        activeAfterSeconds: 30,
        occurrenceWeights: [
            60,
            0,
            40,
            0,
            0,
            0,
            0
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.0,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            0,
            25,
            0,
            25,
            0,
            0,
            0,
            0,
            25,
            25
        ]
    },
    {
        activeAfterSeconds: 60,
        occurrenceWeights: [
            40,
            30,
            30,
            0,
            0,
            0,
            0
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.05,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            0,
            20,
            0,
            20,
            20,
            0,
            0,
            0,
            20,
            20
        ]
    },
    {
        activeAfterSeconds: 90,
        occurrenceWeights: [
            30,
            30,
            30,
            0,
            0,
            0,
            10
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.05,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            5,
            10,
            5,
            10,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    },
    {
        activeAfterSeconds: 120,
        occurrenceWeights: [
            20,
            20,
            20,
            30,
            0,
            0,
            10
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.1,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            5,
            10,
            5,
            10,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    },
    {
        activeAfterSeconds: 150,
        occurrenceWeights: [
            0,
            0,
            30,
            0,
            30,
            30,
            10
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.1,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            5,
            10,
            5,
            10,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    },
    {
        activeAfterSeconds: 180,
        occurrenceWeights: [
            0,
            10,
            0,
            20,
            25,
            25,
            20
        ],
        maxBugs: 14,
        meanTimeBetweenBugSpawn: 3,
        spawnPoints: 2,
        speedFactor: 1.2,
        meanTimeBetweenPowerUpCreation: 4,
        powerUpWeights: [
            5,
            10,
            5,
            10,
            10,
            5,
            10,
            10,
            20,
            15
        ]
    }
];
const $0f82c75150dd11fd$export$db95b8c10274b52f = 0.08;
const $0f82c75150dd11fd$export$d3f85a7f6ac0f49e = 0.01;
const $0f82c75150dd11fd$export$a432f4c95d0c5c38 = 1;
const $0f82c75150dd11fd$export$ecc24cbb1055bc3e = 2;
const $0f82c75150dd11fd$export$943f87761ed966d = 0.5;
const $0f82c75150dd11fd$export$976c8f9b7547c13c = $0f82c75150dd11fd$export$7d2b4216cca8b699;
const $0f82c75150dd11fd$export$a19430ecf44d09b9 = [
    "mushroom1",
    "mushroom2",
    "mushroom3",
    "mushroom4",
    "mushroom5",
    "flower1",
    "flower2",
    "flower3",
    "flower5",
    "flower4"
];
const $0f82c75150dd11fd$export$8c930e56a7009e81 = [
    {
        model: "mushroom1",
        points: 3000,
        effect: "killHalf"
    },
    {
        model: "mushroom2",
        points: 10000,
        effect: "doubleSpawn"
    },
    {
        model: "mushroom3",
        points: 1000,
        effect: "halfSpeed"
    },
    {
        model: "mushroom4",
        points: 10000,
        effect: "doubleSpeed"
    },
    {
        model: "mushroom5",
        points: 1000,
        effect: "freeze"
    },
    {
        model: "flower1",
        points: 10000,
        effect: "repairAll"
    },
    {
        model: "flower2",
        points: 5000,
        effect: "repairSeven"
    },
    {
        model: "flower3",
        points: 1000,
        effect: "repairThree"
    },
    {
        model: "flower5",
        points: 1000,
        effect: "points"
    },
    {
        model: "flower4",
        points: 5000,
        effect: "points"
    }
];
const $0f82c75150dd11fd$export$68f63888e48ad900 = 2;
const $0f82c75150dd11fd$export$12dc221805986839 = 2;
const $0f82c75150dd11fd$export$ec9e3adaf570a53a = 0.5;
const $0f82c75150dd11fd$export$7c0779c4c48ee9c3 = 30;
const $0f82c75150dd11fd$export$ee5d8e34c3ab016a = 15;
const $0f82c75150dd11fd$export$508d0d6670f34f94 = 10;
const $0f82c75150dd11fd$export$e70391d3a41b7825 = 7;
const $0f82c75150dd11fd$export$3fe855f8e7bf3528 = 3;
const $0f82c75150dd11fd$export$71908db6f942d5a2 = 3.3;
const $0f82c75150dd11fd$export$85ea86153841c066 = 29 / 30;
const $0f82c75150dd11fd$export$456464c042c8fcb2 = 0.8;
const $0f82c75150dd11fd$export$7947fc3c8329e1c3 = 3;
const $0f82c75150dd11fd$export$d3d608cbae003bec = 29 / 30;
const $0f82c75150dd11fd$export$72b6529f4e0001ff = [
    {
        under: 3,
        multiplier: 3
    },
    {
        under: 6,
        multiplier: 2
    }
];
const $0f82c75150dd11fd$export$a23cf2326c1866f2 = 3;
const $0f82c75150dd11fd$export$c5da828031ab50c2 = 3;
const $0f82c75150dd11fd$export$6eb7f743008d3af4 = [
    [
        0,
        1
    ],
    [
        1.4,
        1.4
    ],
    [
        1,
        0
    ],
    [
        1.4,
        -1.4
    ],
    [
        0,
        -1
    ],
    [
        -1.4,
        -1.4
    ],
    [
        -1,
        0
    ],
    [
        -1.4,
        1.4
    ]
];
const $0f82c75150dd11fd$export$297516b5fbcb97cf = 20;
const $0f82c75150dd11fd$export$fc3750dd29efecc0 = 0.5;
const $0f82c75150dd11fd$export$86e9e857dfb55419 = [
    1000,
    2000,
    5000,
    10000,
    20000,
    50000
];


/**
 * Surface coordinates run over the closed range [0, N]: a walker sitting
 * exactly on a face edge has `u === N`, which belongs to the last tile, not to
 * a seventh row that does not exist.
 */ function $45485603a00f3ba6$var$tileIndex(coord) {
    const i = Math.floor(coord);
    return i < 0 ? 0 : i > (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) - 1 ? (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) - 1 : i;
}
function $45485603a00f3ba6$export$51ae54019e6625fa(face, u, v) {
    return (face * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) + $45485603a00f3ba6$var$tileIndex(v)) * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) + $45485603a00f3ba6$var$tileIndex(u);
}
function $45485603a00f3ba6$export$2a0231a2f2b1d85f(key) {
    const face = (0, $733d112f9a19cd2b$export$6349a8ed6fe8449e)[Math.floor(key / ((0, $733d112f9a19cd2b$export$a07804094c3ecf2d) * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d)))];
    const rest = key - face * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d);
    const v = Math.floor(rest / (0, $733d112f9a19cd2b$export$a07804094c3ecf2d));
    const c = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)({
        face: face,
        u: rest - v * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) + 0.5,
        v: v + 0.5,
        dir: 0
    });
    return [
        c.x,
        c.y,
        c.z
    ];
}
class $45485603a00f3ba6$export$76afec25c076d535 {
    byTile = new Map();
    /** Reverse index so `releaseAll` costs the walker's claims, not the map. */ byWalker = new Map();
    /** Take the tile; false (and no change) if someone else already holds it. */ claim(key, id) {
        const holder = this.byTile.get(key);
        if (holder !== undefined && holder !== id) return false;
        this.byTile.set(key, id);
        let mine = this.byWalker.get(id);
        if (!mine) {
            mine = new Set();
            this.byWalker.set(id, mine);
        }
        mine.add(key);
        return true;
    }
    /** Drop the tile. Releasing someone else's claim is a no-op. */ release(key, id) {
        if (this.byTile.get(key) !== id) return;
        this.byTile.delete(key);
        this.byWalker.get(id)?.delete(key);
    }
    holder(key) {
        return this.byTile.get(key) ?? null;
    }
    /** Every tile `id` currently holds. Empty for an unknown walker. */ claimsOf(id) {
        return this.byWalker.get(id) ?? $45485603a00f3ba6$var$EMPTY;
    }
    /** Drop all of a walker's claims — it died, or left the simulation. */ releaseAll(id) {
        const mine = this.byWalker.get(id);
        if (!mine) return;
        for (const key of mine)this.byTile.delete(key);
        this.byWalker.delete(id);
    }
    /**
   * Replace `id`'s claims with exactly `tiles`, but only if every one of them
   * is free or already its own; returns false and changes nothing otherwise.
   * This all-or-nothing form is what the movement system needs — a walker must
   * never end up holding half of the tiles its body spans.
   */ reclaim(id, tiles) {
        for (const key of tiles){
            const holder = this.byTile.get(key);
            if (holder !== undefined && holder !== id) return false;
        }
        const mine = this.byWalker.get(id);
        if (mine) {
            for (const key of mine)if (!tiles.includes(key)) this.byTile.delete(key);
            mine.clear();
        }
        for (const key of tiles)this.claim(key, id);
        return true;
    }
    /**
   * Every walker whose claim covers a grid cell — the port of
   * `GetCubeWalkerBlockingPlaneRepair`, the gate `BlocksModel.RepairBlock`
   * refuses a repair on. Bugs *and* apples count: both share this claim space.
   *
   * A linear scan, as plan 08 allows: at most 6·N² = 150 tiles exist and only
   * the claimed ones are in the map, so a reverse index would cost more to keep
   * current than the walk saves on the handful of taps a second this sees.
   */ holdersByCell(cell) {
        const out = [];
        for (const [key, id] of this.byTile){
            const c = $45485603a00f3ba6$export$2a0231a2f2b1d85f(key);
            if (c[0] === cell[0] && c[1] === cell[1] && c[2] === cell[2]) out.push(id);
        }
        return out;
    }
    /** Read-only view for debugging (`?debug=occupancy` in `main.ts`). */ get tiles() {
        return this.byTile;
    }
}
const $45485603a00f3ba6$var$EMPTY = new Set();
function $45485603a00f3ba6$export$3fe8347445b8cd97(pose) {
    const here = $45485603a00f3ba6$export$51ae54019e6625fa(pose.face, pose.u, pose.v);
    const lead = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, (0, $0f82c75150dd11fd$export$c98bbe58eb39170d));
    const ahead = $45485603a00f3ba6$export$51ae54019e6625fa(lead.face, lead.u, lead.v);
    return here === ahead ? [
        here
    ] : [
        here,
        ahead
    ];
}
function $45485603a00f3ba6$export$a15fb836434290c4(pose) {
    return [
        $45485603a00f3ba6$export$51ae54019e6625fa(pose.face, pose.u, pose.v)
    ];
}



// The three global fields power-up effects write and the rest of the sim
// reads — the original's `globalSpeedFactor`, `_bugSpawnRate` and `_bugsFrozen`
// on `BugsModel` (plan 16 §3). A plain mutable record owned by `World`, created
// before both managers: it is what keeps `BugManager` and `PowerUpManager` from
// needing each other for these (the kill-half effect needs `BugManager`, so
// that dependency cannot go both ways).
//
// Pure data: no WebGL, no DOM (00-architecture.md §1).
/** Written by power-up effects, read by the bug and spawner systems. */ function $aae4ef88cc18edf2$export$1a5e2253a67e66c4() {
    return {
        speedFactor: 1,
        spawnRate: 1,
        frozen: false
    };
}


/**
 * Summing `dt / duration` over exactly `duration` seconds of fixed steps lands
 * a hair under 1 in floating point (60 x 1/60 = 0.9999999999999999), which
 * would stretch every timed state by one extra step. Close the gap explicitly.
 */ const $267a74da153328b6$var$PROGRESS_EPSILON = 1e-9;
// ---------------------------------------------------------------------------
// Collision, speed and turning (plan 05)
// ---------------------------------------------------------------------------
/** Distance in cells scanned ahead for walkers in the way. */ const $267a74da153328b6$var$LOOK_AHEAD = 2;
/** Slack for comparing distances against tile boundaries, in cells. */ const $267a74da153328b6$var$EPS = 1e-9;
/**
 * A walker stops this far short of the tile it may not enter. Coming to rest
 * with the leading edge *exactly* on the boundary would bin that edge into the
 * forbidden tile — `floor(3.0)` is 3 — and the claim would be refused after the
 * brake had already done its job.
 */ const $267a74da153328b6$var$BOUNDARY_SLACK = 1e-6;
const $267a74da153328b6$var$QUARTER_TURN = Math.PI / 2;
const $267a74da153328b6$var$TURN_RADIANS_PER_SPEED = (0, $0f82c75150dd11fd$export$3ff1482ae9cd1fde) * Math.PI / 180;
/**
 * Why a walker may not enter a tile: someone else holds it, or the block behind
 * it has been eaten away and there is no surface left to walk on.
 *
 * The tile the walker currently stands on is always passable to it — a bug that
 * has just eaten the block out from under itself must be able to step off the
 * hole it made rather than brake against it and starve to death.
 *
 * "No surface" is `!isWalkable`, which rejects only `empty` — the original's
 * `CheckPositionIsWalkable2` (`BugsModel.cs:2278-2295`). `healthy`, `infected`
 * and `ghost` are all walkable, so a bug crosses the translucent shell an eaten
 * block leaves without deflecting. Plan 07 had this backwards and treated every
 * destroyed block as an obstacle; plan 12 corrected it.
 *
 * Nothing produces `empty` until plan 13 lands slice collapse, so in practice
 * only occupancy blocks a walker today. The surface test stays because it is
 * the correct rule, not because it currently fires.
 */ function $267a74da153328b6$var$isBlocked(world, bug, pose, standing) {
    const key = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(pose.face, pose.u, pose.v);
    const holder = world.occupancy.holder(key);
    if (holder !== null && holder !== bug.id) return true;
    if (key === standing) return false;
    const cell = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)(pose);
    return !world.blocks.isWalkable(cell.x, cell.y, cell.z);
}
/**
 * How far the walker may still advance before its leading edge would enter a
 * tile it may not have; `null` when the way is clear for `LOOK_AHEAD` cells.
 * This is the distance the brake has to work with, so it is measured from the
 * leading edge rather than the body centre.
 */ function $267a74da153328b6$var$distanceToBlocker(world, bug, from) {
    const standing = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(from.face, from.u, from.v);
    let probe = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(from, (0, $0f82c75150dd11fd$export$c98bbe58eb39170d));
    // The nose normally sits in a tile the walker holds itself; when it does not
    // — a fence dropped in front of it, a neighbour that turned into the lane —
    // there is no room left at all.
    if ($267a74da153328b6$var$isBlocked(world, bug, probe, standing)) return 0;
    let travelled = 0;
    while(travelled < $267a74da153328b6$var$LOOK_AHEAD){
        const step = (0, $733d112f9a19cd2b$export$50bbbfe3556a9553)(probe);
        probe = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(probe, step + $267a74da153328b6$var$EPS);
        travelled += step;
        if ($267a74da153328b6$var$isBlocked(world, bug, probe, standing)) return travelled;
    }
    return null;
}
/**
 * Speed a walker may carry and still stop within `gap` at `ACCELERATION`
 * (`v = sqrt(2·a·d)`), capped by the kind's top speed. Braking then falls out
 * of the same ramp that accelerates it, so an approach reads as a slowdown and
 * the walker comes to rest exactly at the obstruction — the smooth-stop half of
 * the original's `BrakeToStopInDistance`, without its frame-rate-dependent
 * fudge factors.
 */ function $267a74da153328b6$var$targetSpeed(maxSpeed, gap) {
    if (gap === null) return maxSpeed;
    return Math.min(maxSpeed, Math.sqrt(2 * (0, $0f82c75150dd11fd$export$17923b10fb784872) * Math.max(gap, 0)));
}
/** Move `value` toward `goal` by at most `step`. */ function $267a74da153328b6$var$approach(value, goal, step) {
    if (value < goal) return Math.min(goal, value + step);
    return Math.max(goal, value - step);
}
/** Tile centre one cell along `(du, dv)` from a pose sitting on a boundary. */ function $267a74da153328b6$var$sideCentre(pose, forward, side) {
    return {
        u: pose.u + forward[0] * 0.5 + side[0],
        v: pose.v + forward[1] * 0.5 + side[1]
    };
}
/** True while a face-local point is on the face rather than past one of its edges. */ function $267a74da153328b6$var$onFace(u, v) {
    return u >= 0 && u <= (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) && v >= 0 && v <= (0, $733d112f9a19cd2b$export$a07804094c3ecf2d);
}
/**
 * Decide, standing on a tile boundary, whether to turn aside — the original's
 * `CalculateNextActionOnEnterNewBlock`, which looks a block and a half ahead on
 * entering a new block and turns into a free neighbour rather than walking into
 * an occupant. Turning back is never an option; a walker with nowhere to go
 * brakes to a stop instead and waits for the way to clear.
 *
 * The horizon is deliberately the same `LOOK_AHEAD` the brake uses. Boundaries
 * are the only places a turn may start (an arc that starts anywhere else lands
 * off the lane centres), so a shorter horizon lets a walker brake to a halt
 * mid-tile and sit there with no decision point left to reach.
 *
 * APPROX: the original picks randomly when both sides are free; right-first
 * keeps the sim deterministic (00-architecture.md §3).
 */ function $267a74da153328b6$var$planTurn(world, bug, pose) {
    if ($267a74da153328b6$var$distanceToBlocker(world, bug, pose) === null) return null;
    const standing = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(pose.face, pose.u, pose.v);
    const forward = (0, $733d112f9a19cd2b$export$1c1343d814ee6703)[pose.dir];
    for (const turn of [
        (0, $733d112f9a19cd2b$export$af85df91db0258da),
        (0, $733d112f9a19cd2b$export$feaf0c0bac3a05b)
    ]){
        const toDir = turn(pose).dir;
        const side = (0, $733d112f9a19cd2b$export$1c1343d814ee6703)[toDir];
        const exit = $267a74da153328b6$var$sideCentre(pose, forward, side);
        // A turn is a half-cell sweep sideways, so the tile it exits into has to be
        // on this same face: the original refuses to turn where it cannot walk.
        if (!$267a74da153328b6$var$onFace(exit.u, exit.v)) continue;
        const key = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(pose.face, exit.u, exit.v);
        if ($267a74da153328b6$var$isBlocked(world, bug, {
            face: pose.face,
            u: exit.u,
            v: exit.v,
            dir: toDir
        }, standing)) continue;
        // Reserve both tiles the arc needs for its whole sweep; if either is gone
        // by now, try the other side rather than committing to half a turn.
        const ahead = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, 0.5);
        const aheadKey = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(ahead.face, ahead.u, ahead.v);
        if (!world.occupancy.reclaim(bug.id, [
            aheadKey,
            key
        ])) continue;
        return {
            face: pose.face,
            pivot: [
                pose.u + side[0] * 0.5,
                pose.v + side[1] * 0.5
            ],
            s: [
                -side[0] * 0.5,
                -side[1] * 0.5
            ],
            e: [
                forward[0] * 0.5,
                forward[1] * 0.5
            ],
            toDir: toDir,
            angle: 0
        };
    }
    return null;
}
/** Face-local point and tangent of an arc at its current angle. */ function $267a74da153328b6$var$arcPoint(arc) {
    const c = Math.cos(arc.angle);
    const s = Math.sin(arc.angle);
    return {
        u: arc.pivot[0] + arc.s[0] * c + arc.e[0] * s,
        v: arc.pivot[1] + arc.s[1] * c + arc.e[1] * s,
        du: -arc.s[0] * s + arc.e[0] * c,
        dv: -arc.s[1] * s + arc.e[1] * c
    };
}
/** Sweep an in-progress turn; the arc's tiles are already reserved. */ function $267a74da153328b6$var$stepTurn(bug, arc, dt, maxSpeed) {
    bug.blockedFor = 0; // sweeping through a turn is progress, not obstruction
    bug.speed = $267a74da153328b6$var$approach(bug.speed, maxSpeed, (0, $0f82c75150dd11fd$export$17923b10fb784872) * dt);
    arc.angle = Math.min($267a74da153328b6$var$QUARTER_TURN, arc.angle + $267a74da153328b6$var$TURN_RADIANS_PER_SPEED * bug.speed * dt);
    const at = $267a74da153328b6$var$arcPoint(arc);
    const done = arc.angle >= $267a74da153328b6$var$QUARTER_TURN;
    bug.pose = {
        face: arc.face,
        u: at.u,
        v: at.v,
        dir: done ? arc.toDir : bug.pose.dir
    };
    if (done) bug.motion = {
        kind: "cruising"
    };
}
/**
 * One step of walking: ramp the speed toward whatever the way ahead allows,
 * then spend it, stopping at every tile boundary crossed to decide whether to
 * turn aside. Splitting the step at boundaries is what keeps walkers on lane
 * centres — a turn may only start where the arc lands on one.
 */ function $267a74da153328b6$var$walk(world, bug, dt, maxSpeed) {
    const reach = $267a74da153328b6$var$distanceToBlocker(world, bug, bug.pose);
    const gap = reach === null ? null : Math.max(0, reach - $267a74da153328b6$var$BOUNDARY_SLACK);
    bug.speed = $267a74da153328b6$var$approach(bug.speed, $267a74da153328b6$var$targetSpeed(maxSpeed, gap), (0, $0f82c75150dd11fd$export$17923b10fb784872) * dt);
    let budget = Math.min(bug.speed * dt, gap ?? Number.POSITIVE_INFINITY);
    let pose = bug.pose;
    while(budget > $267a74da153328b6$var$EPS){
        const toBoundary = (0, $733d112f9a19cd2b$export$50bbbfe3556a9553)(pose);
        if (toBoundary > budget) {
            pose = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, budget);
            break;
        }
        pose = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, toBoundary);
        budget -= toBoundary;
        const arc = $267a74da153328b6$var$planTurn(world, bug, pose);
        if (arc) {
            // The rest of this step's budget goes into the sweep on the next update;
            // at 1/60 s the pause is not visible.
            bug.pose = pose;
            bug.motion = {
                kind: "turning",
                arc: arc
            };
            return;
        }
    }
    // The gap above already guarantees the body stays clear, so this only ever
    // fails if someone claimed a tile mid-step; standing still is then correct.
    if (world.occupancy.reclaim(bug.id, (0, $45485603a00f3ba6$export$3fe8347445b8cd97)(pose))) bug.pose = pose;
    else bug.speed = 0;
    bug.blockedFor = bug.speed < (0, $0f82c75150dd11fd$export$fdebad4c864d02b2) ? bug.blockedFor + dt : 0;
}
// ---------------------------------------------------------------------------
// Infection: the decision chain and what each kind actually eats (plan 14)
// ---------------------------------------------------------------------------
/** The grid cell under a pose, as the `Cell` tuple the block model speaks. */ function $267a74da153328b6$var$cellAt(pose) {
    const c = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)(pose);
    return [
        c.x,
        c.y,
        c.z
    ];
}
/**
 * Whether a spy standing here has found a block worth marking, and mark it.
 * `BugsModel.cs:1355-1374`: every non-empty block it enters gets marked once,
 * with no randomness at all. Returns whether the list changed.
 */ function $267a74da153328b6$var$markBlock(world, bug) {
    const cell = $267a74da153328b6$var$cellAt(bug.pose);
    if (!world.blocks.isWalkable(cell[0], cell[1], cell[2])) return false;
    for (const m of bug.markedBlocks){
        if (m[0] === cell[0] && m[1] === cell[1] && m[2] === cell[2]) return false;
    }
    bug.markedBlocks.push(cell);
    return true;
}
/** Drop a spy's marks — a kill forfeits them — and announce it if any existed. */ function $267a74da153328b6$var$forfeitMarks(world, bug) {
    if (bug.markedBlocks.length === 0) return;
    bug.markedBlocks.length = 0;
    world.marksChanged(bug);
}
/**
 * Whether the walker stops on the tile it has just entered to eat — the
 * original's infect-decision chain (`BugsModel.cs:1652-1677`). The chain's shape
 * matters as much as its rules:
 *
 * - the spy's test is a plain `if`, **not** an `else if`, so a spy falls through
 *   into the generic branch as well and can be tipped into an early cash-in by
 *   the coin flip with fewer than `SPY_MARKS_TO_INFECT` marks;
 * - **lady and black are excluded outright** — they never infect anything;
 * - the generic branch is a **coin flip re-rolled on every tile entered** once
 *   the distance budget has run out, so a bite is deferred by two blocks on
 *   average rather than taken the instant the counter hits zero;
 * - every branch requires a `healthy` block. The *destroy* step guards on
 *   `!= Empty`, but the decision does not: a bug never stops to chew a ghost.
 *
 * Spike tests the block **ahead** (the original's forward probe at `:1647`)
 * while the generic branch tests the one just entered, which is what makes a
 * spike stop *before* stepping onto healthy ground.
 */ function $267a74da153328b6$var$isBiteDue(world, bug) {
    const kind = bug.kindDef;
    const healthyAt = (pose)=>{
        const c = $267a74da153328b6$var$cellAt(pose);
        return world.blocks.state(c[0], c[1], c[2]) === "healthy";
    };
    let infect = false;
    if (kind === "spy" && bug.markedBlocks.length >= (0, $0f82c75150dd11fd$export$a23cf2326c1866f2)) infect = true;
    if (kind === "spike") {
        if (healthyAt((0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(bug.pose, 1))) infect = true;
    } else if (kind !== "lady" && kind !== "black") // `Random.Range(0, 2) == 0` — draw unconditionally so the RNG stream does
    // not depend on the block state, as the original's does not either.
    {
        if (bug.tilesUntilInfect <= 0) {
            const heads = Math.floor(world.rng() * 2) === 0;
            if (heads && healthyAt(bug.pose)) infect = true;
        }
    }
    return infect;
}
/**
 * The fat bug's area bite (`BugsModel.cs:1900-1923`). It walks the `EIGHT_STAR`
 * ring forward from a random start with wraparound and stops as soon as the bite
 * accounts for `FAT_STAR_INFECTS` blocks — a count that starts at 1 for the
 * block underneath, so the star adds **at most two** more.
 *
 * The count increments on any **non-empty** block, while `infect()` itself
 * no-ops on anything but a healthy one: a ghost neighbour therefore uses up one
 * of the two slots without being infected. Faithful to the original, whose
 * `amountInfected++` sits outside `InfectBlock`'s own `Healthy` guard.
 */ function $267a74da153328b6$var$fatStarInfect(world, bug) {
    // `Random.Range(0, 7)` is exclusive, so index 7 is never a start. Kept.
    const start = Math.floor(world.rng() * ((0, $0f82c75150dd11fd$export$6eb7f743008d3af4).length - 1));
    let index = start;
    let accounted = 1;
    do {
        const [forward, left] = (0, $0f82c75150dd11fd$export$6eb7f743008d3af4)[index];
        const cell = $267a74da153328b6$var$cellAt($267a74da153328b6$export$47c2f16b3dd0bb1c(bug.pose, forward, left));
        if (world.blocks.isWalkable(cell[0], cell[1], cell[2])) {
            world.blocks.infect(cell[0], cell[1], cell[2]);
            accounted++;
        }
        index = (index + 1) % (0, $0f82c75150dd11fd$export$6eb7f743008d3af4).length;
    }while (index !== start && accounted !== (0, $0f82c75150dd11fd$export$c5da828031ab50c2));
}
function $267a74da153328b6$export$47c2f16b3dd0bb1c(pose, forward, left) {
    const ahead = forward === 0 ? pose : (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, forward);
    return left === 0 ? ahead : (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)((0, $733d112f9a19cd2b$export$feaf0c0bac3a05b)(ahead), left);
}
/**
 * What a bite actually destroys, once the eating animation has run out.
 * `BugDestroy` (`BugsModel.cs:1869-1929`) splits hard on the spy: it cashes in
 * its marks and — being the `if` half of an `if/else` — **never** touches the
 * block under itself. Every other kind eats what it stands on, and the fat bug
 * then adds its ring.
 */ function $267a74da153328b6$var$takeBite(world, bug) {
    if (bug.kindDef === "spy") {
        for (const m of bug.markedBlocks)world.blocks.infect(m[0], m[1], m[2]);
        $267a74da153328b6$var$forfeitMarks(world, bug);
        return;
    }
    const cell = $267a74da153328b6$var$cellAt(bug.pose);
    if (world.blocks.isWalkable(cell[0], cell[1], cell[2])) world.blocks.infect(cell[0], cell[1], cell[2]);
    if (bug.kindDef === "fat") $267a74da153328b6$var$fatStarInfect(world, bug);
}
/** Seconds this bug tolerates being stuck; the id term staggers give-ups. */ function $267a74da153328b6$var$blockedLimit(id) {
    return (0, $0f82c75150dd11fd$export$c9aa3efc1c3b1e53) + id % 10 / 2;
}
function $267a74da153328b6$export$93c280f99766aeb9(bug) {
    if (bug.motion.kind !== "turning") return (0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)(bug.pose);
    const arc = bug.motion.arc;
    const at = $267a74da153328b6$var$arcPoint(arc);
    return (0, $733d112f9a19cd2b$export$17fffb2ca18f2877)(arc.face, at.u, at.v, at.du, at.dv);
}
/**
 * The one place bug transitions live (00-architecture.md §5): an exhaustive
 * switch with a `never` check. Mutates the bug; the caller emits the events.
 */ function $267a74da153328b6$var$updateBug(world, bug, dt) {
    const state = bug.state;
    // The black bug's own clock (`BugsModel.cs:1239-1249`), checked before the
    // state machine because it applies to running and eating alike. It is a
    // `NotCounting` kill: the full death animation, no score.
    if (bug.kindDef === "black" && $267a74da153328b6$export$3ab83a34b537c934(state) && bug.lifeTime >= (0, $0f82c75150dd11fd$export$297516b5fbcb97cf)) {
        bug.state = {
            kind: "dying",
            progress: 0
        };
        return "changed";
    }
    switch(state.kind){
        case "spawning":
            state.progress += dt / (0, $0f82c75150dd11fd$export$7d2b4216cca8b699);
            if (state.progress < 1 - $267a74da153328b6$var$PROGRESS_EPSILON) return "none";
            bug.state = {
                kind: "running"
            };
            return "changed";
        case "running":
            {
                // The bug-freeze power-up: the port of `CubeWalk`'s `if (Frozen) return;`
                // (`BugsModel.cs:2018`), gating exactly what CubeWalk gates — movement.
                // CORRECTION: a per-step condition rather than the original's flag
                // stamped by `CheckBugsFreeze`, so a bug spawned mid-freeze freezes too
                // (see `DIVERGENCES.md`). `affectedByFreeze` is true for every kind but
                // black — the reason `BUG_DEFS` has carried it since plan 04. Life time
                // still accrues (a frozen bug tapped young still scores its multiplier);
                // `blockedFor` does not — frozen is not stuck, and the give-up cull must
                // not fire from it.
                if (world.modifiers.frozen && (0, $0f82c75150dd11fd$export$a0cba50fed652a94)[bug.kindDef].affectedByFreeze) {
                    bug.lifeTime += dt;
                    return "none";
                }
                // The one place the level's speed ramp enters bug movement — see
                // `Surroundings.speedFactor` for why it scales the top speed rather than
                // the per-frame travel the original scales.
                const maxSpeed = (0, $0f82c75150dd11fd$export$a0cba50fed652a94)[bug.kindDef].speed * world.speedFactor;
                const before = (0, $45485603a00f3ba6$export$51ae54019e6625fa)(bug.pose.face, bug.pose.u, bug.pose.v);
                if (bug.motion.kind === "turning") $267a74da153328b6$var$stepTurn(bug, bug.motion.arc, dt, maxSpeed);
                else $267a74da153328b6$var$walk(world, bug, dt, maxSpeed);
                bug.lifeTime += dt;
                // Comparing the tile before and after the step catches every entry —
                // a boundary crossed mid-walk and one an arc sweeps over alike — and can
                // never double-count a walker resting on a boundary. Everything the
                // original does "on entering a new block" hangs off this one test.
                if ((0, $45485603a00f3ba6$export$51ae54019e6625fa)(bug.pose.face, bug.pose.u, bug.pose.v) !== before) {
                    // Bugs do not avoid power-ups — they destroy them: the original's
                    // `HandleOccupationOnEnteredBlock` flips one on the entered block to
                    // `Killed` (`BugsModel.cs:1752-1760`), and this tile-entry hook is the
                    // exact same moment.
                    if (world.powerups !== null) world.powerups.killAt($267a74da153328b6$var$cellAt(bug.pose));
                    bug.tilesUntilInfect--;
                    // Marking runs *before* the decision — "other way round doesnt work",
                    // as the comment at `BugsModel.cs:1357` puts it: the block just entered
                    // has to be able to be the third mark that triggers the cash-in.
                    if (bug.kindDef === "spy" && $267a74da153328b6$var$markBlock(world, bug)) world.marksChanged(bug);
                    if ($267a74da153328b6$var$isBiteDue(world, bug)) {
                        bug.state = {
                            kind: "infecting",
                            progress: 0
                        };
                        bug.speed = 0; // it comes to a halt to eat, and ramps up again after
                        return "changed";
                    }
                }
                // Walkers that can neither pass nor turn aside give up, as in the
                // original — otherwise two of them face off on the same lane forever.
                // `KillBug` clears a spy's marks whatever killed it, this cull included.
                if (bug.blockedFor >= $267a74da153328b6$var$blockedLimit(bug.id)) {
                    $267a74da153328b6$var$forfeitMarks(world, bug);
                    bug.state = {
                        kind: "dying",
                        progress: 0
                    };
                    return "changed";
                }
                return "none";
            }
        case "infecting":
            // Stationary while eating, keeping its tile claims, then the kind's own
            // bite — the original's `BugDestroy` (`BugsModel.cs:1869-1929`).
            bug.lifeTime += dt;
            state.progress += dt / (0, $0f82c75150dd11fd$export$cb53ca73bf6aa717);
            if (state.progress < 1 - $267a74da153328b6$var$PROGRESS_EPSILON) return "none";
            $267a74da153328b6$var$takeBite(world, bug);
            bug.tilesUntilInfect = world.nextBiteDistance();
            bug.state = {
                kind: "running"
            };
            return "changed";
        case "dying":
            state.progress += dt / (0, $0f82c75150dd11fd$export$97c0383a8185747f);
            return state.progress < 1 - $267a74da153328b6$var$PROGRESS_EPSILON ? "none" : "removed";
        case "takenByPlayer":
            // Picked up rather than killed: the same shape as `dying`, on the `pick`
            // clip's own duration.
            state.progress += dt / (0, $0f82c75150dd11fd$export$d3d608cbae003bec);
            return state.progress < 1 - $267a74da153328b6$var$PROGRESS_EPSILON ? "none" : "removed";
        default:
            {
                const unreachable = state;
                throw new Error(`unhandled bug state ${JSON.stringify(unreachable)}`);
            }
    }
}
function $267a74da153328b6$export$3ab83a34b537c934(state) {
    return state.kind === "running" || state.kind === "infecting";
}
/**
 * Score multiplier for how long the bug had been alive — `unity-overview.md` §5
 * "Score multipliers" (`BugLifeTimeIndicators`, applied in `KillBug`).
 */ function $267a74da153328b6$var$lifeTimeMultiplier(lifeTime) {
    for (const step of (0, $0f82c75150dd11fd$export$72b6529f4e0001ff))if (lifeTime < step.under) return step.multiplier;
    return 1;
}
/**
 * The lifecycle a fatal tap sends a bug into. The lady is *taken* rather than
 * killed — the original's `KillBug(..., killedByPlayer: true)` puts Lady and
 * Black into `TakenByPlayer`, which plays their `pick` clip.
 *
 * TODO(black): the black bug belongs on the same branch, but tapping it ends
 * the game (`ConductGameOver`) and scores nothing. Until game over exists it
 * simply dies like the rest, which keeps it out of the sim's way. Plan 14
 * landed the *other* half of the black bug — its `BLACK_DESPAWN_SECONDS` timer
 * in `updateBug` — so this is all that is left of it.
 */ function $267a74da153328b6$var$fatalState(kind) {
    if (kind === "lady") return {
        kind: "takenByPlayer",
        progress: 0
    };
    return {
        kind: "dying",
        progress: 0
    };
}
class $267a74da153328b6$export$6535a4bc347cd13e {
    events;
    blocks;
    rng;
    levels;
    modifiers;
    map;
    nextId;
    /** Tile claims of every live walker; tests reach in to fence a bug off. */ occupancy;
    /**
   * The trample hook, late-bound by `createWorld` — the construction order
   * cannot satisfy both directions (`PowerUpManager` takes this manager for
   * the kill-half effect). Null is a live configuration: suites not about
   * power-ups build a `BugManager` alone and nothing is trampled.
   */ powerups;
    // `blocks` is read-only from here: walkers avoid holes and bite what they
    // stand on, and the block model emits its own `blockChanged` events.
    // `modifiers` defaults to a fresh record so those same suites stay at 1x/unfrozen.
    constructor(events, blocks, rng, levels, modifiers = (0, $aae4ef88cc18edf2$export$1a5e2253a67e66c4)()){
        this.events = events;
        this.blocks = blocks;
        this.rng = rng;
        this.levels = levels;
        this.modifiers = modifiers;
        this.map = new Map();
        this.nextId = 1;
        this.occupancy = new (0, $45485603a00f3ba6$export$76afec25c076d535)();
        this.powerups = null;
    }
    /**
   * `Surroundings.speedFactor` — the level's walker time dilation times the
   * power-up speed effect. The original multiplies the two side by side at
   * every movement site (`BugsModel.cs:2299`, `:703`, `:1054`, `:1956`,
   * `:2444`); plan 15 funnelled them all through this one getter.
   */ get speedFactor() {
        return this.levels.speedFactor * this.modifiers.speedFactor;
    }
    /** Announce a spy's marks after `markedBlocks` was mutated (`Surroundings`). */ marksChanged(bug) {
        // Copied: the list keeps being mutated in place, and a listener that stored
        // the payload would otherwise watch its own snapshot change underneath it.
        this.events.emit("bugMarksChanged", {
            id: bug.id,
            marks: [
                ...bug.markedBlocks
            ]
        });
    }
    /**
   * Tiles a walker covers before its next bite — the original's
   * `DistanceLeft = Random.Range(1, 20)` (`BugsModel.cs:1927`). Unity's integer
   * overload has an exclusive upper bound, so this draws 1..19.
   */ nextBiteDistance() {
        return (0, $0f82c75150dd11fd$export$711e4dd88b9f3387) + Math.floor(this.rng() * ((0, $0f82c75150dd11fd$export$70d15fa4974a234f) - (0, $0f82c75150dd11fd$export$711e4dd88b9f3387)));
    }
    get bugs() {
        return this.map;
    }
    /** Add a bug in `spawning` at `pose`; it starts moving after SPAWN_SECONDS. */ spawn(kind, pose) {
        const id = this.nextId++;
        const bug = {
            id: id,
            kindDef: kind,
            pose: pose,
            state: {
                kind: "spawning",
                progress: 0
            },
            motion: {
                kind: "cruising"
            },
            speed: 0,
            lifeTime: 0,
            blockedFor: 0,
            tilesUntilInfect: this.nextBiteDistance(),
            hitsTaken: 0,
            markedBlocks: []
        };
        this.map.set(id, bug);
        // A spawning bug is not moving yet, so it reserves only the tile it stands
        // on — that keeps a spawn from being blocked by a neighbour and lets the
        // walker take its leading tile on its first running step.
        this.occupancy.reclaim(id, (0, $45485603a00f3ba6$export$a15fb836434290c4)(pose));
        this.events.emit("bugSpawned", {
            id: id,
            kind: kind
        });
        return bug;
    }
    /**
   * A player tap — the port of `BugsModel.HurtBug`. The fat bug absorbs
   * `FAT_HITS` taps and only announces the non-fatal ones; every other kind
   * dies to the first. The fatal tap scores the kind's points at the life-time
   * multiplier, which is what a killing-spree tracker will later chain onto
   * (`world.time` is queryable, so this event carries all it needs).
   */ hurt(id) {
        const bug = this.map.get(id);
        if (!bug || !$267a74da153328b6$export$3ab83a34b537c934(bug.state)) return;
        bug.hitsTaken++;
        if (bug.hitsTaken < (bug.kindDef === "fat" ? (0, $0f82c75150dd11fd$export$7947fc3c8329e1c3) : 1)) {
            this.events.emit("bugHit", {
                id: id,
                hitsTaken: bug.hitsTaken
            });
            return;
        }
        // `KillBug` clears the marks before it scores and without consulting
        // `killedByPlayer` (`BugsModel.cs:2804`): a spy tapped one mark short of
        // cashing in destroys nothing at all.
        $267a74da153328b6$var$forfeitMarks(this, bug);
        bug.state = $267a74da153328b6$var$fatalState(bug.kindDef);
        this.events.emit("bugStateChanged", {
            id: id,
            state: bug.state.kind
        });
        this.events.emit("scored", {
            points: (0, $0f82c75150dd11fd$export$a0cba50fed652a94)[bug.kindDef].points,
            multiplier: $267a74da153328b6$var$lifeTimeMultiplier(bug.lifeTime)
        });
    }
    /** Send a running/infecting bug into its death animation; otherwise a no-op. */ kill(id) {
        const bug = this.map.get(id);
        if (!bug || !$267a74da153328b6$export$3ab83a34b537c934(bug.state)) return;
        $267a74da153328b6$var$forfeitMarks(this, bug);
        bug.state = {
            kind: "dying",
            progress: 0
        };
        this.events.emit("bugStateChanged", {
            id: id,
            state: "dying"
        });
    }
    /** Advance every bug by `dt` seconds, emitting transitions and removals. */ update(dt) {
        // Deleting the current entry while iterating a Map is well defined, so the
        // dying bug can be dropped in place without copying the collection.
        for (const bug of this.map.values()){
            const result = $267a74da153328b6$var$updateBug(this, bug, dt);
            if (result === "changed") this.events.emit("bugStateChanged", {
                id: bug.id,
                state: bug.state.kind
            });
            else if (result === "removed") {
                this.map.delete(bug.id);
                this.occupancy.releaseAll(bug.id);
                this.events.emit("bugRemoved", {
                    id: bug.id
                });
            }
        }
    }
}


// Power-ups: the ten plants that appear on the surface, and their effects.
// Ports `PowerUp.cs` (four state constants and nothing else — a power-up is a
// `CubeWalker` that never walks) plus the power-up half of `BugsModel.cs`:
// `CheckForPowerUpCreation`/`AddPowerUp2` (spawning), `TakePowerUp` (the ten
// effects), `HandleOccupationOnEnteredBlock` (bugs trample them) and
// `RepairBlocksAround`/`GetSpiral`/`RepairCube` from `BlocksModel.cs`.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).
// Seedable RNG (00-architecture.md §3): src/game/ never calls Math.random().
/** Returns a number in [0, 1). */ function $8c55fc03ff988801$export$653e707a21062548(rng, weights) {
    const r = Math.floor(rng() * 100);
    let upper = 0;
    for(let i = 0; i < weights.length; i++){
        const lower = upper;
        upper += weights[i];
        if (lower <= r && r < upper) return i;
    }
    return null;
}
function $8c55fc03ff988801$export$d22a027dada4117a(seed) {
    let a = seed >>> 0;
    return ()=>{
        a = a + 0x6d2b79f5 >>> 0;
        let t = a;
        t = Math.imul(t ^ t >>> 15, t | 1);
        t ^= t + Math.imul(t ^ t >>> 7, t | 61);
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}




function $08dfdfd795a13c32$export$ceb0abbf2403c9de(state) {
    return state.kind === "growing" || state.kind === "idle";
}
/** Same guard as bug states: 60 x 1/60 sums to a hair under 1. */ const $08dfdfd795a13c32$var$PROGRESS_EPSILON = 1e-9;
/** Linear index of a grid cell, for the manager's cell index. */ function $08dfdfd795a13c32$var$cellKey(cell) {
    return (cell[2] * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) + cell[1]) * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) + cell[0];
}
/** The axis a face's normal runs along: faces 0,1 → x; 2,3 → y; 4,5 → z. */ function $08dfdfd795a13c32$var$faceAxis(face) {
    return Math.floor(face / 2);
}
function* $08dfdfd795a13c32$export$a70866d5e0158cdc(a, b) {
    const dirs = [
        [
            0,
            -1
        ],
        [
            1,
            0
        ],
        [
            0,
            1
        ],
        [
            -1,
            0
        ]
    ];
    let direction = 0;
    let walked = 0;
    let run = 1;
    let turns = 0;
    for(let i = 0; i < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) * 4; i++){
        if (a >= 0 && b >= 0 && a < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d) && b < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d)) yield [
            a,
            b
        ];
        a += dirs[direction][0];
        b += dirs[direction][1];
        walked++;
        if (walked === run) {
            walked = 0;
            turns++;
            direction = (direction + 1) % 4;
            if (turns === 2) {
                turns = 0;
                run++;
            }
        }
    }
}
function $08dfdfd795a13c32$export$94b2b7bc7de2d125(p) {
    const surface = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)(p.pose);
    if (surface.x === p.cell[0] && surface.y === p.cell[1] && surface.z === p.cell[2]) return (0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)(p.pose);
    const f = (0, $733d112f9a19cd2b$export$c03c71dce639df5e)[p.pose.face];
    const cellSize = 1 / (0, $733d112f9a19cd2b$export$a07804094c3ecf2d);
    const centre = (c)=>-0.5 + (c + 0.5) * cellSize;
    const half = cellSize / 2;
    const pos = [
        centre(p.cell[0]) + f.normal[0] * half,
        centre(p.cell[1]) + f.normal[1] * half,
        centre(p.cell[2]) + f.normal[2] * half
    ];
    const [du, dv] = (0, $733d112f9a19cd2b$export$1c1343d814ee6703)[p.pose.dir];
    const forward = [
        f.uAxis[0] * du + f.vAxis[0] * dv,
        f.uAxis[1] * du + f.vAxis[1] * dv,
        f.uAxis[2] * du + f.vAxis[2] * dv
    ];
    return {
        pos: pos,
        normal: f.normal,
        forward: forward
    };
}
class $08dfdfd795a13c32$export$8ce0593c091cd29c {
    events;
    rng;
    blocks;
    bugs;
    levels;
    modifiers;
    map;
    /** The power-up standing on a cell. At most one per cell; only `growing` and
   * `idle` ones are indexed — taken and killed ones stop counting at once. */ byCell;
    nextId;
    /** Seconds until the next placement attempt; stays negative across failed
   * attempts — that non-reset is the game's real spawn throttle (plan 16 §2). */ nextIn;
    // Countdowns of the timed effects; each collection restarts the full window.
    speedLeft;
    spawnLeft;
    freezeLeft;
    constructor(events, rng, blocks, // Needed for the kill-half effect and (via its occupancy) the placement
    // and repair gates; the reverse link is the narrow `PowerUpSink` in bugs.ts.
    bugs, levels, modifiers){
        this.events = events;
        this.rng = rng;
        this.blocks = blocks;
        this.bugs = bugs;
        this.levels = levels;
        this.modifiers = modifiers;
        this.map = new Map();
        this.byCell = new Map();
        this.nextId = -1000;
        this.nextIn = (0, $0f82c75150dd11fd$export$508d0d6670f34f94);
        this.speedLeft = 0;
        this.spawnLeft = 0;
        this.freezeLeft = 0;
    }
    get powerups() {
        return this.map;
    }
    /** The collectable power-up standing on a cell, or null. */ at(cell) {
        const id = this.byCell.get($08dfdfd795a13c32$var$cellKey(cell));
        return id === undefined ? null : this.map.get(id) ?? null;
    }
    /**
   * Place a power-up through face cell `(u, v)` (integers), marching inward
   * past `empty` blocks; the first non-empty block must be `healthy` and
   * unoccupied or nothing is placed — `AddPowerUp2` (`BugsModel.cs:1840-1865`).
   * Public so tests and the debug hook can place deterministically; the spawn
   * timer path draws its arguments from the RNG and calls this.
   */ place(slot, face, u, v) {
        const cell = this.marchInward(face, u, v);
        if (cell === null) return null;
        const pose = {
            face: face,
            u: u + 0.5,
            v: v + 0.5,
            dir: 0
        };
        const powerUp = {
            id: this.nextId--,
            slot: slot,
            pose: pose,
            cell: cell,
            state: {
                kind: "growing",
                progress: 0
            }
        };
        this.map.set(powerUp.id, powerUp);
        this.byCell.set($08dfdfd795a13c32$var$cellKey(cell), powerUp.id);
        this.events.emit("powerUpSpawned", {
            id: powerUp.id,
            slot: slot
        });
        return powerUp;
    }
    /**
   * A tap — `TakePowerUp` (`BugsModel.cs:2919-2990`). Collectable while still
   * growing; awards the slot's points unmultiplied and **before** the effect
   * (`Cube.cs:1166-1169` keeps power-up points out of the combo multiplier),
   * then applies the effect **while the power-up is still in the cell index**,
   * so a repair effect vetoes the repair of its own block, and only then drops
   * it from the index. Anything else — already taken, killed, unknown — is a
   * no-op, which is what makes a second tap during the `pick` clip safe.
   */ take(id) {
        const powerUp = this.map.get(id);
        if (!powerUp || !$08dfdfd795a13c32$export$ceb0abbf2403c9de(powerUp.state)) return;
        powerUp.state = {
            kind: "taken",
            progress: 0
        };
        this.events.emit("powerUpStateChanged", {
            id: id,
            state: "taken"
        });
        const def = (0, $0f82c75150dd11fd$export$8c930e56a7009e81)[powerUp.slot];
        this.events.emit("scored", {
            points: def.points,
            multiplier: 1
        });
        this.events.emit("powerUpTaken", {
            id: id,
            slot: powerUp.slot,
            points: def.points
        });
        this.applyEffect(def.effect, powerUp);
        this.byCell.delete($08dfdfd795a13c32$var$cellKey(powerUp.cell));
    }
    /**
   * A walker entered this cell — `HandleOccupationOnEnteredBlock`
   * (`BugsModel.cs:1752-1760`): the power-up is destroyed, plays its `die`
   * clip, and awards nothing.
   */ killAt(cell) {
        const key = $08dfdfd795a13c32$var$cellKey(cell);
        const id = this.byCell.get(key);
        if (id === undefined) return;
        const powerUp = this.map.get(id);
        if (!powerUp) return;
        powerUp.state = {
            kind: "killed",
            progress: 0
        };
        this.byCell.delete(key);
        this.events.emit("powerUpStateChanged", {
            id: id,
            state: "killed"
        });
    }
    /** Advance timers, lifecycle states, and the spawn attempt by `dt` seconds. */ update(dt) {
        this.tickModifiers(dt);
        for (const powerUp of this.map.values()){
            const result = this.step(powerUp, dt);
            if (result === "changed") this.events.emit("powerUpStateChanged", {
                id: powerUp.id,
                state: powerUp.state.kind
            });
            else if (result === "removed") {
                this.map.delete(powerUp.id);
                this.events.emit("powerUpRemoved", {
                    id: powerUp.id
                });
            }
        }
        this.nextIn -= dt;
        if (this.nextIn < 0) this.tryCreate();
    }
    /** The three effect countdowns — `CheckForBugsFrozen`, `CheckForGlobalSpeed
   * Timer`, `CheckForSpawnRateTimer` (`BugsModel.cs:791-830`). */ tickModifiers(dt) {
        if (this.speedLeft > 0) {
            this.speedLeft -= dt;
            if (this.speedLeft <= 0) this.modifiers.speedFactor = 1;
        }
        if (this.spawnLeft > 0) {
            this.spawnLeft -= dt;
            if (this.spawnLeft <= 0) this.modifiers.spawnRate = 1;
        }
        if (this.freezeLeft > 0) {
            this.freezeLeft -= dt;
            if (this.freezeLeft <= 0) this.modifiers.frozen = false;
        }
    }
    /**
   * The one place power-up transitions live (00-architecture.md §5). The
   * original drives these off `Animation.isPlaying` (`UpdatePowerUps`,
   * `BugsModel.cs:832-859`); the port has no such signal, so the cooked clip
   * durations time them, exactly as `DIE_SECONDS` does for bugs.
   */ step(powerUp, dt) {
        const state = powerUp.state;
        switch(state.kind){
            case "growing":
                state.progress += dt / (0, $0f82c75150dd11fd$export$71908db6f942d5a2);
                if (state.progress < 1 - $08dfdfd795a13c32$var$PROGRESS_EPSILON) return "none";
                powerUp.state = {
                    kind: "idle"
                };
                return "changed";
            case "idle":
                return "none";
            case "taken":
                state.progress += dt / (0, $0f82c75150dd11fd$export$85ea86153841c066);
                return state.progress < 1 - $08dfdfd795a13c32$var$PROGRESS_EPSILON ? "none" : "removed";
            case "killed":
                state.progress += dt / (0, $0f82c75150dd11fd$export$456464c042c8fcb2);
                return state.progress < 1 - $08dfdfd795a13c32$var$PROGRESS_EPSILON ? "none" : "removed";
            default:
                {
                    const unreachable = state;
                    throw new Error(`unhandled power-up state ${JSON.stringify(unreachable)}`);
                }
        }
    }
    /**
   * One placement attempt — `CheckForPowerUpCreation`: a uniform face and a
   * uniform cell on it. Only success resets the timer (`SetPowerUpCreationTime`
   * is on the success path alone), so a failure retries **next step** — the
   * original re-rolls every frame, and this attempt-per-step reproduction is
   * what keeps a damaged cube spawning fewer power-ups. It also couples every
   * other system's RNG stream to how damaged the cube is; that coupling is the
   * original's own (see `DIVERGENCES.md`).
   */ tryCreate() {
        const face = Math.floor(this.rng() * 6);
        const u = Math.floor(this.rng() * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d));
        const v = Math.floor(this.rng() * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d));
        if (this.marchInward(face, u, v) === null) return;
        const slot = (0, $8c55fc03ff988801$export$653e707a21062548)(this.rng, this.levels.current.powerUpWeights);
        // Unreachable with the shipped tables (every row sums to 100, asserted in
        // tests); the original resets the timer on this branch, so this does too.
        if (slot === null) {
            this.resetTimer();
            return;
        }
        this.place(slot, face, u, v);
        this.resetTimer();
    }
    /** `mean ± 10%`, uniform (`SetPowerUpCreationTime`, `BugsModel.cs:1833-1838`). */ resetTimer() {
        const mean = this.levels.current.meanTimeBetweenPowerUpCreation;
        const tenPercent = mean * 0.1;
        this.nextIn = mean - tenPercent + this.rng() * tenPercent * 2;
    }
    /**
   * The ray-march inward from face cell `(u, v)`: skip `empty` blocks; the
   * first non-empty one is the candidate, accepted only if `healthy` with no
   * walker claim and no power-up on it. Returns the cell, or null.
   */ marchInward(face, u, v) {
        const outer = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)({
            face: face,
            u: u + 0.5,
            v: v + 0.5,
            dir: 0
        });
        const normal = (0, $733d112f9a19cd2b$export$c03c71dce639df5e)[face].normal;
        for(let depth = 0; depth < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); depth++){
            const cell = [
                outer.x - normal[0] * depth,
                outer.y - normal[1] * depth,
                outer.z - normal[2] * depth
            ];
            const state = this.blocks.state(cell[0], cell[1], cell[2]);
            if (state === "empty") continue;
            if (state !== "healthy") return null;
            if (this.bugs.occupancy.holdersByCell(cell).length > 0) return null;
            if (this.byCell.has($08dfdfd795a13c32$var$cellKey(cell))) return null;
            return cell;
        }
        return null; // the whole column is gone
    }
    /**
   * The ten effects — the switch in `TakePowerUp` (`BugsModel.cs:2933-2985`).
   * The point-bonus slots are deliberately empty: every power-up's points are
   * already awarded before the switch, and "point bonus" simply means "a
   * power-up with no side effect".
   */ applyEffect(effect, powerUp) {
        switch(effect){
            case "killHalf":
                {
                    // "50% bugs die" is neither 50% nor a kill: every **even list index**
                    // is hurt (deterministic), black bugs are skipped (tapping one would
                    // be game over), and `hurt` means the fat bug takes one of its three.
                    let index = 0;
                    for (const bug of [
                        ...this.bugs.bugs.values()
                    ]){
                        if (bug.kindDef !== "black" && index % 2 === 0) this.bugs.hurt(bug.id);
                        index++;
                    }
                    return;
                }
            case "doubleSpawn":
                this.modifiers.spawnRate = (0, $0f82c75150dd11fd$export$68f63888e48ad900);
                this.spawnLeft = (0, $0f82c75150dd11fd$export$7c0779c4c48ee9c3);
                return;
            case "halfSpeed":
                // Assign, never multiply: the two speed effects are mutually exclusive
                // and each collection restarts the full window (`BugsModel.cs:2953-2963`).
                this.modifiers.speedFactor = (0, $0f82c75150dd11fd$export$ec9e3adaf570a53a);
                this.speedLeft = (0, $0f82c75150dd11fd$export$7c0779c4c48ee9c3);
                return;
            case "doubleSpeed":
                this.modifiers.speedFactor = (0, $0f82c75150dd11fd$export$12dc221805986839);
                this.speedLeft = (0, $0f82c75150dd11fd$export$7c0779c4c48ee9c3);
                return;
            case "freeze":
                this.modifiers.frozen = true;
                this.freezeLeft = (0, $0f82c75150dd11fd$export$ee5d8e34c3ab016a);
                return;
            case "repairAll":
                // `RepairCube` routes through the full `RepairBlock`, so every cell
                // goes through the blocker gate: a walker on a hole vetoes part of the
                // "complete" repair, and this power-up vetoes its own block.
                for(let z = 0; z < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); z++)for(let y = 0; y < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); y++){
                    for(let x = 0; x < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); x++)if (this.blocks.state(x, y, z) !== "healthy") this.tryRepair([
                        x,
                        y,
                        z
                    ]);
                }
                return;
            case "repairSeven":
                this.repairAround(powerUp, (0, $0f82c75150dd11fd$export$e70391d3a41b7825));
                return;
            case "repairThree":
                this.repairAround(powerUp, (0, $0f82c75150dd11fd$export$3fe855f8e7bf3528));
                return;
            case "points":
                return;
            default:
                {
                    const unreachable = effect;
                    throw new Error(`unhandled power-up effect ${JSON.stringify(unreachable)}`);
                }
        }
    }
    /**
   * The N-block spiral repair — `RepairAroundPowerUp` + `RepairBlocksAround`
   * (`BlocksModel.cs:286-330`). The spiral runs over the two coordinates other
   * than the face's normal axis, at the power-up's **own** depth (a recessed
   * power-up repairs its recessed layer for free). Candidates are `ghost` and
   * `empty` only — infected blocks are not healed by this effect, unlike
   * `repairAll` — and only *successful* repairs count toward `amount`, so a
   * veto does not use up a slot and the spiral may manage fewer than N.
   */ repairAround(powerUp, amount) {
        const axis = $08dfdfd795a13c32$var$faceAxis(powerUp.pose.face);
        const [cx, cy, cz] = powerUp.cell;
        const start = axis === 0 ? [
            cy,
            cz
        ] : axis === 1 ? [
            cx,
            cz
        ] : [
            cx,
            cy
        ];
        let left = amount;
        for (const [a, b] of $08dfdfd795a13c32$export$a70866d5e0158cdc(start[0], start[1])){
            const cell = axis === 0 ? [
                cx,
                a,
                b
            ] : axis === 1 ? [
                a,
                cy,
                b
            ] : [
                a,
                b,
                cz
            ];
            const state = this.blocks.state(cell[0], cell[1], cell[2]);
            if (state !== "ghost" && state !== "empty") continue;
            if (this.tryRepair(cell)) {
                left--;
                if (left === 0) return;
            }
        }
    }
    /**
   * A repair through the same blocker gate a player repair uses — walkers
   * whose claims cover the cell, plus any power-up standing on it (this is
   * where a repair power-up vetoes its own block). Effect repairs do not feed
   * the combo chain and, unlike the player path, do not announce their
   * blockers — the original calls `RepairBlock(x, y, z, false)`.
   */ tryRepair(cell) {
        if (this.bugs.occupancy.holdersByCell(cell).length > 0) return false;
        if (this.at(cell) !== null) return false;
        return this.blocks.repair(cell[0], cell[1], cell[2]);
    }
}



// Every cooked model the view can draw, with the per-model placement data the
// renderer needs. Parcel resolves `import` specifiers statically, so both the
// JSON-module manifests and the `new URL(..., import.meta.url)` asset URLs must
// be literal — hence one explicit entry per model rather than a loop over names.
//
// Every file referenced here is un-ignored in the root `.gitignore` and
// committed: the GitHub deploy builds from a bare checkout and cannot run the
// cook/extract tools (see `../../CLAUDE.md` "Assets").
// Loader and renderer for models cooked by tools/ufbx/cook.c (see
// unity-overview.md §6): a JSON manifest plus a flat f32 binary blob with
// non-indexed triangle-corner geometry in node-local space and animation
// clips as per-frame node TRS tracks.


// Minimal 4x4 matrix helpers (column-major, WebGL convention).
function $1669b27dd88d6d65$export$f0954fd7d5368655() {
    const m = new Float32Array(16);
    m[0] = m[5] = m[10] = m[15] = 1;
    return m;
}
function $1669b27dd88d6d65$export$541149539f3a4684(fovY, aspect, near, far) {
    const f = 1 / Math.tan(fovY / 2);
    const m = new Float32Array(16);
    m[0] = f / aspect;
    m[5] = f;
    m[10] = (far + near) / (near - far);
    m[11] = -1;
    m[14] = 2 * far * near / (near - far);
    return m;
}
function $1669b27dd88d6d65$export$2060d2db72cce88f(a, b) {
    const out = new Float32Array(16);
    for(let c = 0; c < 4; c++)for(let r = 0; r < 4; r++){
        let s = 0;
        for(let k = 0; k < 4; k++)s += a[k * 4 + r] * b[c * 4 + k];
        out[c * 4 + r] = s;
    }
    return out;
}
function $1669b27dd88d6d65$export$446bfe4478ff8d71(rad) {
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const m = $1669b27dd88d6d65$export$f0954fd7d5368655();
    m[5] = c;
    m[6] = s;
    m[9] = -s;
    m[10] = c;
    return m;
}
function $1669b27dd88d6d65$export$122a03c894ebb63(rad) {
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const m = $1669b27dd88d6d65$export$f0954fd7d5368655();
    m[0] = c;
    m[2] = -s;
    m[8] = s;
    m[10] = c;
    return m;
}
function $1669b27dd88d6d65$export$a984e488de492859(x, y, z) {
    const m = $1669b27dd88d6d65$export$f0954fd7d5368655();
    m[12] = x;
    m[13] = y;
    m[14] = z;
    return m;
}
function $1669b27dd88d6d65$export$89390739e80eec4a(s) {
    const m = $1669b27dd88d6d65$export$f0954fd7d5368655();
    m[0] = m[5] = m[10] = s;
    return m;
}
function $1669b27dd88d6d65$export$c97349202a66f91a(t, q, s) {
    const [x, y, z, w] = [
        q[0],
        q[1],
        q[2],
        q[3]
    ];
    const m = new Float32Array(16);
    m[0] = (1 - 2 * (y * y + z * z)) * s[0];
    m[1] = 2 * (x * y + z * w) * s[0];
    m[2] = 2 * (x * z - y * w) * s[0];
    m[4] = 2 * (x * y - z * w) * s[1];
    m[5] = (1 - 2 * (x * x + z * z)) * s[1];
    m[6] = 2 * (y * z + x * w) * s[1];
    m[8] = 2 * (x * z + y * w) * s[2];
    m[9] = 2 * (y * z - x * w) * s[2];
    m[10] = (1 - 2 * (x * x + y * y)) * s[2];
    m[12] = t[0];
    m[13] = t[1];
    m[14] = t[2];
    m[15] = 1;
    return m;
}


async function $0688557bb19ac86e$export$19de0130f5adf0ae(gl, url, into) {
    const blob = await (await fetch(url.href)).blob();
    const img = await createImageBitmap(blob, {
        imageOrientation: "flipY"
    });
    const tex = into ?? gl.createTexture();
    if (!tex) throw new Error("Failed to create texture");
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    img.close();
    return tex;
}
function $0688557bb19ac86e$var$partVao(gl, data, part) {
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("Failed to create VAO");
    gl.bindVertexArray(vao);
    const attribs = [
        // location, float offset, components
        [
            0,
            part.posOffset / 4,
            3
        ],
        [
            1,
            part.nrmOffset / 4,
            3
        ],
        [
            2,
            part.uvOffset / 4,
            2
        ]
    ];
    for (const [location, offset, size] of attribs){
        const buf = gl.createBuffer();
        if (!buf) throw new Error("Failed to create buffer");
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, data.subarray(offset, offset + part.vertexCount * size), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
    }
    gl.bindVertexArray(null);
    return vao;
}
function $0688557bb19ac86e$export$dbdceeb18407e856(json, data) {
    const worlds = new Array(json.nodes.length);
    for (const i of $0688557bb19ac86e$var$hierarchyOrder(json.nodes)){
        const node = json.nodes[i];
        const local = (0, $1669b27dd88d6d65$export$c97349202a66f91a)(node.t, node.q, node.s);
        worlds[i] = node.parent < 0 ? local : (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(worlds[node.parent], local);
    }
    const min = [
        Infinity,
        Infinity,
        Infinity
    ];
    const max = [
        -Infinity,
        -Infinity,
        -Infinity
    ];
    for (const part of json.parts){
        const m = worlds[part.node];
        const base = part.posOffset / 4;
        for(let v = 0; v < part.vertexCount; v++){
            const x = data[base + v * 3];
            const y = data[base + v * 3 + 1];
            const z = data[base + v * 3 + 2];
            const wx = m[0] * x + m[4] * y + m[8] * z + m[12];
            const wy = m[1] * x + m[5] * y + m[9] * z + m[13];
            const wz = m[2] * x + m[6] * y + m[10] * z + m[14];
            if (wx < min[0]) min[0] = wx;
            if (wy < min[1]) min[1] = wy;
            if (wz < min[2]) min[2] = wz;
            if (wx > max[0]) max[0] = wx;
            if (wy > max[1]) max[1] = wy;
            if (wz > max[2]) max[2] = wz;
        }
    }
    return {
        min: min,
        max: max
    };
}
// Traversal order with every parent before its children; node order in the
// cooked file follows the FBX file and does not guarantee this.
function $0688557bb19ac86e$var$hierarchyOrder(nodes) {
    const order = [];
    const placed = new Array(nodes.length).fill(false);
    while(order.length < nodes.length)for(let i = 0; i < nodes.length; i++){
        if (placed[i]) continue;
        const p = nodes[i].parent;
        if (p < 0 || placed[p]) {
            order.push(i);
            placed[i] = true;
        }
    }
    return order;
}
async function $0688557bb19ac86e$export$9170fee8584a3e27(gl, source) {
    const json = source.manifest;
    const bin = await fetch(source.bin.href).then((r)=>r.arrayBuffer());
    const data = new Float32Array(bin);
    const textures = new Map();
    for (const part of json.parts){
        if (!part.texture || textures.has(part.texture)) continue;
        const url = source.textures[part.texture];
        if (!url) {
            console.warn(`model ${json.name}: no URL provided for texture ${part.texture}`);
            continue;
        }
        textures.set(part.texture, await $0688557bb19ac86e$export$19de0130f5adf0ae(gl, url));
    }
    return {
        name: json.name,
        nodes: json.nodes,
        order: $0688557bb19ac86e$var$hierarchyOrder(json.nodes),
        parts: json.parts.map((p)=>({
                vao: $0688557bb19ac86e$var$partVao(gl, data, p),
                vertexCount: p.vertexCount,
                node: p.node,
                color: p.color?.slice(0, 3) ?? [
                    0.8,
                    0.8,
                    0.8
                ],
                texture: p.texture && textures.get(p.texture) || null
            })),
        anims: new Map(json.anims.map((a)=>[
                a.name,
                a
            ])),
        data: data,
        bounds: $0688557bb19ac86e$export$dbdceeb18407e856(json, data)
    };
}
const $0688557bb19ac86e$var$tmpT = new Float32Array(3);
const $0688557bb19ac86e$var$tmpQ = new Float32Array(4);
const $0688557bb19ac86e$var$tmpS = new Float32Array(3);
function $0688557bb19ac86e$export$8971a1d39538b7c1(model, clip, time, loop) {
    const anim = model.anims.get(clip);
    const worlds = new Array(model.nodes.length);
    const n = model.nodes.length;
    let f0 = 0;
    let f1 = 0;
    let alpha = 0;
    if (anim) {
        const wrapped = (time % anim.duration + anim.duration) % anim.duration;
        const clamped = time < 0 ? 0 : time > anim.duration ? anim.duration : time;
        const t = anim.duration > 0 ? loop ? wrapped : clamped : 0;
        const f = t * anim.fps;
        f0 = Math.min(Math.floor(f), anim.frames - 1);
        f1 = Math.min(f0 + 1, anim.frames - 1);
        alpha = f - f0;
    }
    for (const i of model.order){
        const node = model.nodes[i];
        let local;
        if (anim) {
            const a = anim.offset / 4 + (f0 * n + i) * 10;
            const b = anim.offset / 4 + (f1 * n + i) * 10;
            const d = model.data;
            for(let k = 0; k < 3; k++)$0688557bb19ac86e$var$tmpT[k] = d[a + k] + (d[b + k] - d[a + k]) * alpha;
            // nlerp, negating the second quat if the dot is negative (shortest arc)
            let dot = 0;
            for(let k = 0; k < 4; k++)dot += d[a + 3 + k] * d[b + 3 + k];
            const sign = dot < 0 ? -1 : 1;
            let len = 0;
            for(let k = 0; k < 4; k++){
                $0688557bb19ac86e$var$tmpQ[k] = d[a + 3 + k] + (sign * d[b + 3 + k] - d[a + 3 + k]) * alpha;
                len += $0688557bb19ac86e$var$tmpQ[k] * $0688557bb19ac86e$var$tmpQ[k];
            }
            len = Math.sqrt(len) || 1;
            for(let k = 0; k < 4; k++)$0688557bb19ac86e$var$tmpQ[k] /= len;
            for(let k = 0; k < 3; k++)$0688557bb19ac86e$var$tmpS[k] = d[a + 7 + k] + (d[b + 7 + k] - d[a + 7 + k]) * alpha;
            local = (0, $1669b27dd88d6d65$export$c97349202a66f91a)($0688557bb19ac86e$var$tmpT, $0688557bb19ac86e$var$tmpQ, $0688557bb19ac86e$var$tmpS);
        } else local = (0, $1669b27dd88d6d65$export$c97349202a66f91a)(node.t, node.q, node.s);
        worlds[i] = node.parent < 0 ? local : (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(worlds[node.parent], local);
    }
    return worlds;
}
class $0688557bb19ac86e$export$b51677472c947333 {
    program;
    uMvp;
    uModel;
    uColor;
    uUseTexture;
    uAlphaCutout;
    constructor(gl){
        this.program = (0, $1659abb3fbe87a4d$export$327d24a04cd0dc17)(gl, (0, $545dedaa33bafe10$export$74fce6f44befb8e8), (0, $545dedaa33bafe10$export$577de5190a76ff43));
        this.uMvp = gl.getUniformLocation(this.program, "uMvp");
        this.uModel = gl.getUniformLocation(this.program, "uModel");
        this.uColor = gl.getUniformLocation(this.program, "uColor");
        this.uUseTexture = gl.getUniformLocation(this.program, "uUseTexture");
        this.uAlphaCutout = gl.getUniformLocation(this.program, "uAlphaCutout");
        gl.useProgram(this.program);
        gl.uniform1i(gl.getUniformLocation(this.program, "uTexture"), 0);
        gl.useProgram(null);
    }
    // placement: model-to-world for the whole model (on top of node worlds).
    // alphaCutout: discard texels below 0.5 alpha — the menu text planes only.
    draw(gl, model, worlds, placement, viewProj, alphaCutout = false) {
        gl.useProgram(this.program);
        gl.disable(gl.CULL_FACE); // legacy meshes have mixed winding / thin parts
        gl.uniform1i(this.uAlphaCutout, alphaCutout ? 1 : 0);
        gl.activeTexture(gl.TEXTURE0);
        for (const part of model.parts){
            const world = (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(placement, worlds[part.node]);
            gl.uniformMatrix4fv(this.uModel, false, world);
            gl.uniformMatrix4fv(this.uMvp, false, (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(viewProj, world));
            gl.uniform3fv(this.uColor, part.color);
            gl.uniform1i(this.uUseTexture, part.texture ? 1 : 0);
            if (part.texture) gl.bindTexture(gl.TEXTURE_2D, part.texture);
            gl.bindVertexArray(part.vao);
            gl.drawArrays(gl.TRIANGLES, 0, part.vertexCount);
        }
        gl.bindVertexArray(null);
        gl.enable(gl.CULL_FACE);
    }
}



var $a568d37d83f9138e$exports = {};
$a568d37d83f9138e$exports = JSON.parse("{\"name\":\"0_bug_standard\",\"bin\":\"0_bug_standard.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_bug_standard\",\"parent\":1,\"t\":[0.3440388,3.010967e-7,0.00007874006],\"q\":[-0.5,0.5,0.5000001,0.4999999],\"s\":[1,1,1]},{\"name\":\"Capsule02\",\"parent\":10,\"t\":[2.932777,-2.716021,-0.7110212],\"q\":[0.5213338,-0.5213337,-0.4777147,0.4777143],\"s\":[1,0.6429867,0.9367889]},{\"name\":\"Box08\",\"parent\":10,\"t\":[2.661787,-7.385169,-7.371154],\"q\":[0.08682386,0.9924039,-0.02255768,0.08418576],\"s\":[-0.4770454,-0.4770453,-0.4770453]},{\"name\":\"Sphere03\",\"parent\":10,\"t\":[-18.94972,10.42121,3.271067],\"q\":[0.01266522,-0.257512,-0.002435292,0.966189],\"s\":[0.9000001,0.8999999,0.8999999]},{\"name\":\"Sphere04\",\"parent\":10,\"t\":[-18.94972,-10.05123,3.271067],\"q\":[0.01490458,-0.2575146,-0.002627967,0.9661559],\"s\":[0.9000001,0.8999999,0.8999999]},{\"name\":\"Box09\",\"parent\":10,\"t\":[13.97865,-7.385169,-6.847918],\"q\":[0.08682386,0.9924039,-0.02255768,0.08418576],\"s\":[-0.4770454,-0.4770453,-0.4770453]},{\"name\":\"Box10\",\"parent\":10,\"t\":[25.96662,-4.391894,-6.545536],\"q\":[0.08682386,0.9924039,-0.02255768,0.08418576],\"s\":[-0.4770454,-0.4770453,-0.4770453]},{\"name\":\"Box11\",\"parent\":10,\"t\":[2.661788,7.385168,-7.371153],\"q\":[0.08682386,0.9924039,-0.02255768,0.08418576],\"s\":[-0.4770454,0.4770453,-0.4770453]},{\"name\":\"Box12\",\"parent\":10,\"t\":[13.97865,7.385168,-6.847917],\"q\":[0.08682379,0.9924039,-0.02255767,0.08418576],\"s\":[-0.4770454,0.4770453,-0.4770453]},{\"name\":\"Box13\",\"parent\":10,\"t\":[25.96662,4.39189,-6.545535],\"q\":[0.08682386,0.9924039,-0.02255768,0.08418576],\"s\":[-0.4770454,0.4770453,-0.4770453]}],\"parts\":[{\"node\":11,\"vertexCount\":366,\"posOffset\":0,\"nrmOffset\":4392,\"uvOffset\":8784,\"material\":\"5_buggs_standard\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"5buggs_standard.png\"},{\"node\":12,\"vertexCount\":60,\"posOffset\":11712,\"nrmOffset\":12432,\"uvOffset\":13152,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]},{\"node\":13,\"vertexCount\":240,\"posOffset\":13632,\"nrmOffset\":16512,\"uvOffset\":19392,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye2.png\"},{\"node\":14,\"vertexCount\":240,\"posOffset\":21312,\"nrmOffset\":24192,\"uvOffset\":27072,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye2.png\"},{\"node\":15,\"vertexCount\":60,\"posOffset\":28992,\"nrmOffset\":29712,\"uvOffset\":30432,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]},{\"node\":16,\"vertexCount\":60,\"posOffset\":30912,\"nrmOffset\":31632,\"uvOffset\":32352,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]},{\"node\":17,\"vertexCount\":60,\"posOffset\":32832,\"nrmOffset\":33552,\"uvOffset\":34272,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]},{\"node\":18,\"vertexCount\":60,\"posOffset\":34752,\"nrmOffset\":35472,\"uvOffset\":36192,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]},{\"node\":19,\"vertexCount\":60,\"posOffset\":36672,\"nrmOffset\":37392,\"uvOffset\":38112,\"material\":\"0_standard_bug_leg\",\"color\":[0.1647,1,0,1]}],\"anims\":[{\"name\":\"die\",\"duration\":1.8,\"fps\":30,\"frames\":55,\"offset\":38592},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":82592},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":102592}]}");


var $8e56324373bfac34$exports = {};
$8e56324373bfac34$exports = JSON.parse("{\"name\":\"0_bug_lady\",\"bin\":\"0_bug_lady.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"lady_bug\",\"parent\":1,\"t\":[-0.3806809,-1.033464,0.2146149],\"q\":[0,0.04361936,0,0.9990482],\"s\":[1,1,1]},{\"name\":\"Sphere04\",\"parent\":10,\"t\":[-4.768372e-7,-4.872047,5.591335],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1.05,1.05,1.05]},{\"name\":\"Sphere03\",\"parent\":10,\"t\":[-0.03035851,-4.883663,-2.352694],\"q\":[0.002290459,0.7371924,0.6756752,0.002291153],\"s\":[0.95,0.95,0.95]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[3.346457,0.1493979,8.937792],\"q\":[0.0009659013,-0.5324403,-0.8128319,0.2362428],\"s\":[1.2,1.2,1.2]},{\"name\":\"Sphere05\",\"parent\":10,\"t\":[-3.346455,0.1493984,8.937793],\"q\":[0.2241438,-0.4829629,-0.8365163,0.1294094],\"s\":[1.2,1.2,1.2]}],\"parts\":[{\"node\":11,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"0_bug_lady_black\",\"color\":[0.1059,0.1059,0.1059,1]},{\"node\":12,\"vertexCount\":162,\"posOffset\":4608,\"nrmOffset\":6552,\"uvOffset\":8496,\"material\":\"09 - Default\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"lady_bug.png\"},{\"node\":13,\"vertexCount\":84,\"posOffset\":9792,\"nrmOffset\":10800,\"uvOffset\":11808,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":14,\"vertexCount\":84,\"posOffset\":12480,\"nrmOffset\":13488,\"uvOffset\":14496,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":15168},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":30168},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":45168},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":63168}]}");


var $e7d8415238c1bec3$exports = {};
$e7d8415238c1bec3$exports = JSON.parse("{\"name\":\"0_bug_fat\",\"bin\":\"0_bug_fat.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"fatbug\",\"parent\":1,\"t\":[0,0,0],\"q\":[8.429365e-8,0.7071069,-8.429367e-8,0.7071067],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[0,0,0],\"q\":[-0.7071068,-1.31665e-9,-7.652211e-11,0.7071068],\"s\":[1,1,1]},{\"name\":\"0_fatbug\",\"parent\":10,\"t\":[0.02672015,1.821036e-7,1.029121],\"q\":[-0.7071071,0,0,0.7071065],\"s\":[1.487772,1.487772,1.487772]},{\"name\":\"Sphere01\",\"parent\":12,\"t\":[-11.62208,9.913993,0.7926044],\"q\":[-0.02277037,-0.5390044,0.002011746,0.8419927],\"s\":[1,1,1]},{\"name\":\"Sphere05\",\"parent\":12,\"t\":[-18.17633,-0.000001907349,0.7926044],\"q\":[-0.006895107,-0.005206794,0.001544995,0.9999615],\"s\":[1,1,1]},{\"name\":\"Sphere06\",\"parent\":12,\"t\":[-11.62208,-9.913995,0.7926044],\"q\":[-0.02313803,-0.4949768,0.003566045,0.8685907],\"s\":[1,1,1]},{\"name\":\"Sphere03\",\"parent\":12,\"t\":[-1.017076,-6.556511e-7,-19.46528],\"q\":[0,0,0,1],\"s\":[0.8051328,0.6554288,1]}],\"parts\":[{\"node\":13,\"vertexCount\":162,\"posOffset\":0,\"nrmOffset\":1944,\"uvOffset\":3888,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":14,\"vertexCount\":162,\"posOffset\":5184,\"nrmOffset\":7128,\"uvOffset\":9072,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":15,\"vertexCount\":162,\"posOffset\":10368,\"nrmOffset\":12312,\"uvOffset\":14256,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":16,\"vertexCount\":528,\"posOffset\":15552,\"nrmOffset\":21888,\"uvOffset\":28224,\"material\":\"5_buggs_standard\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"5buggs_standard.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":1.8,\"fps\":30,\"frames\":55,\"offset\":32448},{\"name\":\"hit\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":69848},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":86848},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":103848}]}");


var $24b4e076c13a4198$exports = {};
$24b4e076c13a4198$exports = JSON.parse("{\"name\":\"0_bug_speed\",\"bin\":\"0_bug_speed.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_bug_speed\",\"parent\":1,\"t\":[-0.08009338,-2.975064e-8,-0.4732096],\"q\":[-0.5000001,0.4999999,0.5000003,0.4999997],\"s\":[1,1,1]},{\"name\":\"Box07\",\"parent\":10,\"t\":[0.4274216,-5.92783,-8.020692],\"q\":[0.2306482,0.9711545,-0.05009831,0.0339177],\"s\":[-0.4770453,-0.4770453,-0.4770453]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[0.4755256,-2.857167,-2.363497],\"q\":[0.5323723,0.511165,0.4881428,0.4658397],\"s\":[0.375528,0.3239765,0.6324911]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[-22.30642,-8.667757,4.333136],\"q\":[0.01212205,-0.1292019,-0.009213098,0.9915014],\"s\":[0.9,0.9,0.8999999]},{\"name\":\"Sphere02\",\"parent\":10,\"t\":[-22.30642,8.655077,4.333136],\"q\":[0.01482137,-0.521312,-0.003486015,0.8532303],\"s\":[0.9,0.9,0.8999999]},{\"name\":\"Box08\",\"parent\":10,\"t\":[9.221581,-6.661172,-8.020692],\"q\":[0.02369366,0.9978869,-0.04195165,0.04359254],\"s\":[-0.4770453,-0.4770453,-0.4770453]},{\"name\":\"Box09\",\"parent\":10,\"t\":[18.13042,-5.92783,-8.020692],\"q\":[0.2306482,0.9711545,-0.05009831,0.0339177],\"s\":[-0.4770453,-0.4770453,-0.4770453]},{\"name\":\"Box10\",\"parent\":10,\"t\":[26.39795,-3.952356,-8.020692],\"q\":[0.02369366,0.9978869,-0.04195165,0.04359254],\"s\":[-0.4770453,-0.4770453,-0.4770453]},{\"name\":\"Box11\",\"parent\":10,\"t\":[0.4016037,5.927831,-8.020692],\"q\":[0.0419516,-0.043592,0.02369367,0.997887],\"s\":[0.4770453,0.4770453,0.4770454]},{\"name\":\"Box12\",\"parent\":10,\"t\":[9.195763,6.661172,-8.020692],\"q\":[0.05009817,-0.03391718,0.2306482,0.9711546],\"s\":[0.4770453,0.4770452,0.4770453]},{\"name\":\"Box13\",\"parent\":10,\"t\":[18.10461,5.927831,-8.020692],\"q\":[0.0419516,-0.043592,0.02369367,0.997887],\"s\":[0.4770453,0.4770453,0.4770454]},{\"name\":\"Box14\",\"parent\":10,\"t\":[26.37213,3.952358,-8.020693],\"q\":[0.05009817,-0.03391718,0.2306482,0.9711546],\"s\":[0.4770453,0.4770452,0.4770453]}],\"parts\":[{\"node\":11,\"vertexCount\":60,\"posOffset\":0,\"nrmOffset\":720,\"uvOffset\":1440,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":12,\"vertexCount\":288,\"posOffset\":1920,\"nrmOffset\":5376,\"uvOffset\":8832,\"material\":\"5_buggs_standard\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"5buggs_standard.png\"},{\"node\":13,\"vertexCount\":162,\"posOffset\":11136,\"nrmOffset\":13080,\"uvOffset\":15024,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye3.png\"},{\"node\":14,\"vertexCount\":162,\"posOffset\":16320,\"nrmOffset\":18264,\"uvOffset\":20208,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye3.png\"},{\"node\":15,\"vertexCount\":60,\"posOffset\":21504,\"nrmOffset\":22224,\"uvOffset\":22944,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":16,\"vertexCount\":60,\"posOffset\":23424,\"nrmOffset\":24144,\"uvOffset\":24864,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":17,\"vertexCount\":60,\"posOffset\":25344,\"nrmOffset\":26064,\"uvOffset\":26784,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":18,\"vertexCount\":60,\"posOffset\":27264,\"nrmOffset\":27984,\"uvOffset\":28704,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":19,\"vertexCount\":60,\"posOffset\":29184,\"nrmOffset\":29904,\"uvOffset\":30624,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":20,\"vertexCount\":60,\"posOffset\":31104,\"nrmOffset\":31824,\"uvOffset\":32544,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]},{\"node\":21,\"vertexCount\":60,\"posOffset\":33024,\"nrmOffset\":33744,\"uvOffset\":34464,\"material\":\"0_bug_speed_legs\",\"color\":[0,0.4471,1,1]}],\"anims\":[{\"name\":\"die\",\"duration\":1.1333,\"fps\":30,\"frames\":35,\"offset\":34944},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":65744},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":87744}]}");


var $33568d9d768cf7b6$exports = {};
$33568d9d768cf7b6$exports = JSON.parse("{\"name\":\"0_bug_spy\",\"bin\":\"0_bug_spy.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Group01\",\"parent\":1,\"t\":[-0.3143129,6.698919e-8,-0.9039102],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[-0.006327069,-0.2793509,16.09873],\"q\":[-0.04345344,-1.294786e-10,0.003801678,0.9990482],\"s\":[1,0.8425151,1]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[0.000002723085,-2.162196,12.52128],\"q\":[-0.3420201,1.592607e-8,5.796615e-9,0.9396926],\"s\":[0.9999999,1,1]},{\"name\":\"Box02\",\"parent\":10,\"t\":[9.790967,-6.520226,-8.258936],\"q\":[0.1991375,0.6784864,0.678487,0.1991377],\"s\":[1.21285,1.21285,1.21285]},{\"name\":\"Box03\",\"parent\":10,\"t\":[-10.19432,-6.520224,4.051425],\"q\":[0.31393,-0.7027593,0.6335987,-0.07829494],\"s\":[-1.21285,-1.21285,-1.21285]},{\"name\":\"Box04\",\"parent\":10,\"t\":[-9.790964,-6.520224,-8.258936],\"q\":[0.044927,0.5694601,-0.7056774,0.4191851],\"s\":[-1.21285,-1.21285,-1.21285]},{\"name\":\"Box01\",\"parent\":10,\"t\":[9.164042,-6.520226,4.051425],\"q\":[-0.3139306,-0.702759,-0.6335988,-0.07829413],\"s\":[1.21285,1.21285,1.21285]}],\"parts\":[{\"node\":11,\"vertexCount\":318,\"posOffset\":0,\"nrmOffset\":3816,\"uvOffset\":7632,\"material\":\"5_buggs_standard\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"5buggs_standard.png\"},{\"node\":12,\"vertexCount\":264,\"posOffset\":10176,\"nrmOffset\":13344,\"uvOffset\":16512,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":13,\"vertexCount\":60,\"posOffset\":18624,\"nrmOffset\":19344,\"uvOffset\":20064,\"material\":\"0_bug_spy_legs\",\"color\":[0.5882,0.5882,0.5882,1]},{\"node\":14,\"vertexCount\":60,\"posOffset\":20544,\"nrmOffset\":21264,\"uvOffset\":21984,\"material\":\"0_bug_spy_legs\",\"color\":[0.5882,0.5882,0.5882,1]},{\"node\":15,\"vertexCount\":60,\"posOffset\":22464,\"nrmOffset\":23184,\"uvOffset\":23904,\"material\":\"0_bug_spy_legs\",\"color\":[0.5882,0.5882,0.5882,1]},{\"node\":16,\"vertexCount\":60,\"posOffset\":24384,\"nrmOffset\":25104,\"uvOffset\":25824,\"material\":\"0_bug_spy_legs\",\"color\":[0.5882,0.5882,0.5882,1]}],\"anims\":[{\"name\":\"die\",\"duration\":1.4667,\"fps\":30,\"frames\":45,\"offset\":26304},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":56904},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":73904}]}");


var $65d9431b8f5f097b$exports = {};
$65d9431b8f5f097b$exports = JSON.parse("{\"name\":\"0_bug_spike\",\"bin\":\"0_bug_spike.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_bug_spike\",\"parent\":1,\"t\":[-0.001491712,-9.889549e-8,-0.01661583],\"q\":[0,0.03489948,0,0.9993908],\"s\":[1,1,1]},{\"name\":\"Box05\",\"parent\":10,\"t\":[-8.149658,-29.68304,-16.23192],\"q\":[-0.07391262,0.7032335,0.7032329,0.07391258],\"s\":[1.298279,1.298279,0.9176234]},{\"name\":\"Box06\",\"parent\":10,\"t\":[-11.18884,-32.47656,-5.734888],\"q\":[-0.1045164,-0.6993402,-0.6993396,0.1045166],\"s\":[1.549496,1.549496,1.095184]},{\"name\":\"Box07\",\"parent\":10,\"t\":[-9.948074,-32.47656,7.347682],\"q\":[-0.04316768,0.7057882,0.7057876,0.04316761],\"s\":[1.549496,1.549496,1.095184]},{\"name\":\"Box08\",\"parent\":10,\"t\":[9.948067,-32.47656,7.347682],\"q\":[0.1045168,0.69934,-0.6993397,0.1045172],\"s\":[-1.549496,-1.549496,-1.095184]},{\"name\":\"Box09\",\"parent\":10,\"t\":[11.18883,-32.47656,-5.73489],\"q\":[0.04316731,-0.7057882,0.7057876,0.04316724],\"s\":[-1.549496,-1.549496,-1.095184]},{\"name\":\"Box10\",\"parent\":10,\"t\":[8.149643,-29.68304,-16.23192],\"q\":[0.0739126,0.7032333,-0.7032331,0.07391299],\"s\":[-1.298279,-1.298279,-0.9176234]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[-0.9055176,2.285643,-0.3541727],\"q\":[0,0,-0.9914448,0.1305265],\"s\":[1.549496,1.549496,2.139585]},{\"name\":\"Sphere04\",\"parent\":10,\"t\":[-8.433434,6.941904,18.28517],\"q\":[-0.8373194,-0.1617289,-0.3904486,0.3468283],\"s\":[1.084647,1.084647,1.084647]},{\"name\":\"Sphere03\",\"parent\":10,\"t\":[6.622391,6.941912,18.28517],\"q\":[-0.8373194,-0.1617289,-0.3904486,0.3468283],\"s\":[1.084647,1.084647,1.084647]}],\"parts\":[{\"node\":11,\"vertexCount\":60,\"posOffset\":0,\"nrmOffset\":720,\"uvOffset\":1440,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":12,\"vertexCount\":60,\"posOffset\":1920,\"nrmOffset\":2640,\"uvOffset\":3360,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":13,\"vertexCount\":60,\"posOffset\":3840,\"nrmOffset\":4560,\"uvOffset\":5280,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":14,\"vertexCount\":60,\"posOffset\":5760,\"nrmOffset\":6480,\"uvOffset\":7200,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":15,\"vertexCount\":60,\"posOffset\":7680,\"nrmOffset\":8400,\"uvOffset\":9120,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":16,\"vertexCount\":60,\"posOffset\":9600,\"nrmOffset\":10320,\"uvOffset\":11040,\"material\":\"0_bug_spike_leg\",\"color\":[0,0.02353,0.3608,1]},{\"node\":17,\"vertexCount\":366,\"posOffset\":11520,\"nrmOffset\":15912,\"uvOffset\":20304,\"material\":\"0_bug_spike_body\",\"color\":[0,0,1,1],\"texture\":\"0_bug_spike.png\"},{\"node\":18,\"vertexCount\":162,\"posOffset\":23232,\"nrmOffset\":25176,\"uvOffset\":27120,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"},{\"node\":19,\"vertexCount\":162,\"posOffset\":28416,\"nrmOffset\":30360,\"uvOffset\":32304,\"material\":\"06 - Default\",\"color\":[0.9882,1,0,1],\"texture\":\"eye.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":1.4667,\"fps\":30,\"frames\":45,\"offset\":33600},{\"name\":\"infect\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":69600},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":89600}]}");


var $ef8b92ffec715ff7$exports = {};
$ef8b92ffec715ff7$exports = JSON.parse("{\"name\":\"0_bug_black\",\"bin\":\"0_bug_black.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Group02\",\"parent\":1,\"t\":[0.4289732,-0.750989,1.297371],\"q\":[0,-0.04361938,0,0.9990482],\"s\":[1,1,1]},{\"name\":\"Sphere05\",\"parent\":10,\"t\":[1.957724,-3.02049,-6.184899],\"q\":[0.8191521,0,0,0.5735763],\"s\":[1,1,0.8]},{\"name\":\"Group01\",\"parent\":10,\"t\":[9.536743e-7,0.5805302,12.53668],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[0.000009469688,-7.474706,-19.62669],\"q\":[0.7660445,0,0,0.6427875],\"s\":[1,0.9999999,0.8]},{\"name\":\"Sphere03\",\"parent\":12,\"t\":[14.40478,3.565547,4.23921],\"q\":[-2.874379e-7,-0.9848077,-0.1736484,7.605903e-7],\"s\":[1,1,1]},{\"name\":\"Sphere07\",\"parent\":12,\"t\":[-14.40478,3.56555,4.239206],\"q\":[-6.43852e-8,0.1736484,-0.9848077,7.999206e-7],\"s\":[-1,-1,-1]},{\"name\":\"Sphere06\",\"parent\":12,\"t\":[0.0000038445,-0.4514146,0],\"q\":[0.7071069,0,0,0.7071067],\"s\":[1,1,0.8]}],\"parts\":[{\"node\":11,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"14 - Default\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"0_bug_black_skull.png\"},{\"node\":13,\"vertexCount\":144,\"posOffset\":4608,\"nrmOffset\":6336,\"uvOffset\":8064,\"material\":\"14 - Default\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"0_bug_black_skull.png\"},{\"node\":14,\"vertexCount\":240,\"posOffset\":9216,\"nrmOffset\":12096,\"uvOffset\":14976,\"material\":\"13 - Default\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"eye_black2.png\"},{\"node\":15,\"vertexCount\":240,\"posOffset\":16896,\"nrmOffset\":19776,\"uvOffset\":22656,\"material\":\"13 - Default\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"eye_black2.png\"},{\"node\":16,\"vertexCount\":240,\"posOffset\":24576,\"nrmOffset\":27456,\"uvOffset\":30336,\"material\":\"14 - Default\",\"color\":[0.5882,0.5882,0.5882,1],\"texture\":\"0_bug_black_skull.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.63333,\"fps\":30,\"frames\":20,\"offset\":32256},{\"name\":\"hit\",\"duration\":1.1667,\"fps\":30,\"frames\":36,\"offset\":45856},{\"name\":\"infect\",\"duration\":0.63333,\"fps\":30,\"frames\":20,\"offset\":70336},{\"name\":\"pick\",\"duration\":1.1667,\"fps\":30,\"frames\":36,\"offset\":83936},{\"name\":\"run\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":108416}]}");


var $e2362be944cc2183$exports = {};
$e2362be944cc2183$exports = JSON.parse("{\"name\":\"0_apple\",\"bin\":\"0_apple.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_apple\",\"parent\":1,\"t\":[0,0,0],\"q\":[-0.4985105,0.4791606,0.5227385,0.4986351],\"s\":[1,1,1]},{\"name\":\"Cylinder01\",\"parent\":10,\"t\":[-0.00001113974,-0.8512,22.36108],\"q\":[0,0,0.9981831,0.06025345],\"s\":[1,1,1]},{\"name\":\"Line01\",\"parent\":10,\"t\":[-0.6052399,0.5107474,25.2757],\"q\":[0.1463962,-0.2501106,-0.1220784,0.949268],\"s\":[0.8392338,1.368858,1.000146]},{\"name\":\"Line02\",\"parent\":10,\"t\":[0.000001907349,14.98451,-11.01712],\"q\":[-0.5000005,0.4999999,0.5000003,-0.4999993],\"s\":[1,1,1]}],\"parts\":[{\"node\":11,\"vertexCount\":78,\"posOffset\":0,\"nrmOffset\":936,\"uvOffset\":1872,\"material\":\"0_apple_stil\",\"color\":[0.2824,0.1451,0,1],\"texture\":\"apfel_stil.png\"},{\"node\":12,\"vertexCount\":72,\"posOffset\":2496,\"nrmOffset\":3360,\"uvOffset\":4224,\"material\":\"0_Apple_leav\",\"color\":[0.06667,0.2902,0,1],\"texture\":\"apfelblatt.png\"},{\"node\":13,\"vertexCount\":1044,\"posOffset\":4800,\"nrmOffset\":17328,\"uvOffset\":29856,\"material\":\"0_apple\",\"color\":[1,0,0,1],\"texture\":\"apple.png\"}],\"anims\":[{\"name\":\"create\",\"duration\":0.43333,\"fps\":30,\"frames\":14,\"offset\":38208},{\"name\":\"grow\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":46048},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":52768},{\"name\":\"pick\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":59488}]}");


var $80b6c3149485d86e$exports = {};
$80b6c3149485d86e$exports = JSON.parse("{\"name\":\"0_plant_mushroom1\",\"bin\":\"0_plant_mushroom1.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_mushroom1\",\"parent\":1,\"t\":[-1.259826,-33.13779,0],\"q\":[-0.7069844,-0.002802851,-0.002568292,0.707219],\"s\":[1.2,1.2,1.2]},{\"name\":\"\",\"parent\":10,\"t\":[1.05895,0.01716461,27.61448],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[1.05895,0.01716418,40.61717],\"q\":[0.04357595,-0.04324689,-0.001917195,0.9981118],\"s\":[1,1,0.6729034]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[1.05895,0.0171627,-0.4347935],\"q\":[0,0,0,1],\"s\":[1,1,1]}],\"parts\":[{\"node\":12,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"0_plant_mushroom1\",\"color\":[1,0,0,1],\"texture\":\"0_plant_mushroom1a.png\"},{\"node\":13,\"vertexCount\":75,\"posOffset\":4608,\"nrmOffset\":5508,\"uvOffset\":6408,\"material\":\"0_plant_mushroom1_stiel\",\"color\":[1,1,1,1]}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":7008},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":21008},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":77008},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":83728}]}");


var $42bd533a60751363$exports = {};
$42bd533a60751363$exports = JSON.parse("{\"name\":\"0_plant_mushroom2\",\"bin\":\"0_plant_mushroom2.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_mushroom2\",\"parent\":1,\"t\":[-3.280251,-45.91954,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[3.280251,45.91954,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"0_plant_mushroom2_ncl1_1\",\"parent\":10,\"t\":[-11.61874,35.96012,-9.097999],\"q\":[-0.5591829,-0.4327505,-0.4324232,0.5595102],\"s\":[1.2,1.2,1.2]},{\"name\":\"0_plant_mushroom2_ncl1_2\",\"parent\":10,\"t\":[17.17793,23.09114,7.82859],\"q\":[-0.4691434,-0.4410398,-0.6671679,0.3745335],\"s\":[0.8087615,0.8087616,0.8087615]},{\"name\":\"Sphere01\",\"parent\":12,\"t\":[2.811843,0.02060431,10.71838],\"q\":[0.07738366,0.01558756,0.003587751,0.9968731],\"s\":[1,1,0.6729034]},{\"name\":\"Capsule01\",\"parent\":12,\"t\":[2.270493,-0.02292289,-28.05001],\"q\":[0,0,0,1],\"s\":[0.7884593,0.8732671,0.8732671]},{\"name\":\"Sphere02\",\"parent\":13,\"t\":[2.811843,0.02060431,10.71838],\"q\":[0.07738366,0.01558756,0.003587751,0.9968731],\"s\":[1,1,0.6729034]},{\"name\":\"Capsule02\",\"parent\":13,\"t\":[2.270493,-0.02292289,-28.05001],\"q\":[0,0,0,1],\"s\":[0.7884593,0.8732671,0.8732671]}],\"parts\":[{\"node\":14,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"0_plant_mushroom2\",\"color\":[1,0,0,1],\"texture\":\"0_plant_mushroom2a.png\"},{\"node\":15,\"vertexCount\":75,\"posOffset\":4608,\"nrmOffset\":5508,\"uvOffset\":6408,\"material\":\"0_plant_mushroom2_stiel\",\"color\":[1,1,1,1]},{\"node\":16,\"vertexCount\":144,\"posOffset\":7008,\"nrmOffset\":8736,\"uvOffset\":10464,\"material\":\"0_plant_mushroom2\",\"color\":[1,0,0,1],\"texture\":\"0_plant_mushroom2a.png\"},{\"node\":17,\"vertexCount\":75,\"posOffset\":11616,\"nrmOffset\":12516,\"uvOffset\":13416,\"material\":\"0_plant_mushroom2_stiel\",\"color\":[1,1,1,1]}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":14016},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":32016},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":104016},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":112656}]}");


var $96729eb1934e73c7$exports = {};
$96729eb1934e73c7$exports = JSON.parse("{\"name\":\"0_plant_mushroom3\",\"bin\":\"0_plant_mushroom3.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_mushroom3\",\"parent\":1,\"t\":[3.831234,-24.6362,0],\"q\":[0,-0.7435651,0,0.6686635],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[0.4052629,24.6362,3.809738],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"0_plant_mushroom3\",\"parent\":10,\"t\":[-14.49372,14.67678,-5.288261],\"q\":[-0.5591829,-0.4327505,-0.4324232,0.5595102],\"s\":[1.2,1.2,1.2]},{\"name\":\"Sphere01\",\"parent\":12,\"t\":[10.13268,9.798158,8.031847],\"q\":[0.07738367,0.01558748,0.003587589,0.9968731],\"s\":[1.32895,1.32895,0.8942552]}],\"parts\":[{\"node\":13,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"0_plant_mushroom3\",\"color\":[1,0,0,1],\"texture\":\"0_plant_mushroom3b.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":4608},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":18608},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":74608},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":81328}]}");


var $d9a91875b7da6727$exports = {};
$d9a91875b7da6727$exports = JSON.parse("{\"name\":\"0_plant_mushroom4\",\"bin\":\"0_plant_mushroom4.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Group01\",\"parent\":1,\"t\":[0.09531403,-44.79058,-8.621846e-7],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[-10.17584,44.79058,0],\"q\":[0,0,-0.1736482,0.9848078],\"s\":[1,1,1]},{\"name\":\"0_mushroom4\",\"parent\":10,\"t\":[-0.09532166,70.30412,0],\"q\":[6.324836e-8,0.7071067,0.7071069,-1.250656e-7],\"s\":[0.9999999,1,0.9999999]}],\"parts\":[{\"node\":12,\"vertexCount\":324,\"posOffset\":0,\"nrmOffset\":3888,\"uvOffset\":7776,\"material\":\"0_plant_mushroom3\",\"color\":[1,0,0,1],\"texture\":\"0_plant_mushroom_4.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":10368},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":23368},{\"name\":\"idle\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":75368},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":88368}]}");


var $388ae6fd6bf36503$exports = {};
$388ae6fd6bf36503$exports = JSON.parse("{\"name\":\"0_plant_mushroom5\",\"bin\":\"0_plant_mushroom5.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_mushroom5\",\"parent\":1,\"t\":[-2.355854,-25.79626,0],\"q\":[-0.7069844,-0.002802851,-0.002568292,0.707219],\"s\":[1.2,1.2,1.2]},{\"name\":\"\",\"parent\":10,\"t\":[1.883873,0.01960288,16.02468],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Sphere01\",\"parent\":10,\"t\":[1.883873,0.01960246,29.02737],\"q\":[0.04357595,-0.04324689,-0.001917195,0.9981118],\"s\":[1,1,0.6729034]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[1.883873,0.01960097,-12.02459],\"q\":[0,0,0,1],\"s\":[1,1,1]}],\"parts\":[{\"node\":12,\"vertexCount\":162,\"posOffset\":0,\"nrmOffset\":1944,\"uvOffset\":3888,\"material\":\"0_plant_mushroom5\",\"color\":[0.8863,0.8863,0.8863,1],\"texture\":\"0_plant_mushroom5.png\"},{\"node\":13,\"vertexCount\":75,\"posOffset\":5184,\"nrmOffset\":6084,\"uvOffset\":6984,\"material\":\"0_plant_mushroom1_stiel\",\"color\":[1,1,1,1]}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":7584},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":21584},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":77584},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":84304}]}");


var $577edafed3e0d4f5$exports = {};
$577edafed3e0d4f5$exports = JSON.parse("{\"name\":\"0_plant_flower_1\",\"bin\":\"0_plant_flower_1.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_flower1\",\"parent\":1,\"t\":[0,-3.937008,0],\"q\":[-0.00001174855,-0.4055141,0.006183887,0.9140679],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[0.4511537,6.930761,-0.4463414],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[0.4511508,-0.581195,-0.44634],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]},{\"name\":\"Capsule02\",\"parent\":10,\"t\":[0.4511508,-0.581195,-0.44634],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]}],\"parts\":[{\"node\":12,\"vertexCount\":132,\"posOffset\":0,\"nrmOffset\":1584,\"uvOffset\":3168,\"material\":\"0_plant_flower1\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_1.png\"},{\"node\":13,\"vertexCount\":132,\"posOffset\":4224,\"nrmOffset\":5808,\"uvOffset\":7392,\"material\":\"0_plant_flower1\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_1.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":8448},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":22448},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":78448},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":85168}]}");


var $a2be4449b82a6e2c$exports = {};
$a2be4449b82a6e2c$exports = JSON.parse("{\"name\":\"0_plant_flower_2\",\"bin\":\"0_plant_flower_2.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_flower_2\",\"parent\":1,\"t\":[0.01877722,-4.073975,9.60872e-16],\"q\":[-0.00001174855,-0.4055141,0.006183887,0.9140679],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[-0.1335136,7.735673,-0.2174869],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[-0.1335155,0.2237181,-0.217485],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]},{\"name\":\"Capsule02\",\"parent\":10,\"t\":[-0.1335155,0.2237181,-0.217485],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]}],\"parts\":[{\"node\":12,\"vertexCount\":468,\"posOffset\":0,\"nrmOffset\":5616,\"uvOffset\":11232,\"material\":\"0_plant_flower2\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_2.png\"},{\"node\":13,\"vertexCount\":468,\"posOffset\":14976,\"nrmOffset\":20592,\"uvOffset\":26208,\"material\":\"0_plant_flower2\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_2.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":29952},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":43952},{\"name\":\"idle\",\"duration\":0.36667,\"fps\":30,\"frames\":12,\"offset\":99952},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":106672}]}");


var $864caba18c181cb0$exports = {};
$864caba18c181cb0$exports = JSON.parse("{\"name\":\"0_plant_flower_3\",\"bin\":\"0_plant_flower_3.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_flower_3\",\"parent\":1,\"t\":[-0.7091904,-2.84309,0],\"q\":[-0.03588668,-0.806555,0.02816552,0.5893962],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[0.4029665,6.769385,-0.6969318],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Capsule02\",\"parent\":10,\"t\":[0.4029636,-0.7425699,-0.6969318],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[0.4029636,-0.7425699,-0.6969318],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]}],\"parts\":[{\"node\":12,\"vertexCount\":120,\"posOffset\":0,\"nrmOffset\":1440,\"uvOffset\":2880,\"material\":\"0_plant_flower3\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_3.png\"},{\"node\":13,\"vertexCount\":120,\"posOffset\":3840,\"nrmOffset\":5280,\"uvOffset\":6720,\"material\":\"0_plant_flower3\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_3.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":7680},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":21680},{\"name\":\"idle\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":77680},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":91680}]}");


var $37869967ac32afe1$exports = {};
$37869967ac32afe1$exports = JSON.parse("{\"name\":\"0_plant_flower_4\",\"bin\":\"0_plant_flower_4.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_flower4\",\"parent\":1,\"t\":[0.9701741,-1.62216,0],\"q\":[0.03831506,-0.9929294,0.04560658,0.1026804],\"s\":[0.8,0.8,0.8]},{\"name\":\"\",\"parent\":10,\"t\":[1.263775,6.168199,-0.2544198],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Capsule02\",\"parent\":10,\"t\":[1.263773,-1.343757,-0.2544184],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]},{\"name\":\"Capsule01\",\"parent\":10,\"t\":[1.263773,-1.343757,-0.2544184],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]}],\"parts\":[{\"node\":12,\"vertexCount\":78,\"posOffset\":0,\"nrmOffset\":936,\"uvOffset\":1872,\"material\":\"0_plant_flower4\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_4.png\"},{\"node\":13,\"vertexCount\":78,\"posOffset\":2496,\"nrmOffset\":3432,\"uvOffset\":4368,\"material\":\"0_plant_flower4\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_4.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":4992},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":18992},{\"name\":\"idle\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":74992},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":88992}]}");


var $16fb392ad5a0ba23$exports = {};
$16fb392ad5a0ba23$exports = JSON.parse("{\"name\":\"0_plant_flower_5\",\"bin\":\"0_plant_flower_5.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"0_plant_flower_5\",\"parent\":1,\"t\":[-1.164417,-3.211856,0.229342],\"q\":[0,0,-7.450583e-9,1],\"s\":[1,1,1]},{\"name\":\"\",\"parent\":10,\"t\":[2.129035,4.750031,0],\"q\":[0,0,-0.06975647,0.9975641],\"s\":[1,1,1]},{\"name\":\"0_plant_flower01\",\"parent\":10,\"t\":[1.414772,6.598842,-0.229342],\"q\":[-0.005167508,-0.6931489,-0.018862,0.720529],\"s\":[1,1,1]},{\"name\":\"0_plant_flower5\",\"parent\":10,\"t\":[1.414772,6.598842,-0.229342],\"q\":[-0.005167508,-0.6931489,-0.018862,0.720529],\"s\":[1,1,1]},{\"name\":\"Capsule02\",\"parent\":12,\"t\":[-0.000002861023,-7.511955,0.000001430511],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]},{\"name\":\"Capsule01\",\"parent\":13,\"t\":[-0.000002861023,-7.511955,0.000001430511],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[1,1,0.6658086]}],\"parts\":[{\"node\":14,\"vertexCount\":144,\"posOffset\":0,\"nrmOffset\":1728,\"uvOffset\":3456,\"material\":\"0_plant_flower1\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_5.png\"},{\"node\":15,\"vertexCount\":144,\"posOffset\":4608,\"nrmOffset\":6336,\"uvOffset\":8064,\"material\":\"0_plant_flower1\",\"color\":[0.2157,0.6118,0,1],\"texture\":\"0_plant_flower_5.png\"}],\"anims\":[{\"name\":\"die\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":9216},{\"name\":\"grow\",\"duration\":3.3,\"fps\":30,\"frames\":100,\"offset\":25216},{\"name\":\"idle\",\"duration\":0.8,\"fps\":30,\"frames\":25,\"offset\":89216},{\"name\":\"pick\",\"duration\":0.96667,\"fps\":30,\"frames\":30,\"offset\":105216}]}");


var $ed181d07ea3090da$exports = {};
$ed181d07ea3090da$exports = JSON.parse("{\"name\":\"2_menu_single_bugg\",\"bin\":\"2_menu_single_bugg.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"2_menu_single_bugg.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $6c9c018d54329624$exports = {};
$6c9c018d54329624$exports = JSON.parse("{\"name\":\"21_menu_howto\",\"bin\":\"21_menu_howto.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"21_menu_howto.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $6265e6f037b21aee$exports = {};
$6265e6f037b21aee$exports = JSON.parse("{\"name\":\"2_menu_options\",\"bin\":\"2_menu_options.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"2_menu_options.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $10f195310ee455ee$exports = {};
$10f195310ee455ee$exports = JSON.parse("{\"name\":\"2_menu_highscore\",\"bin\":\"2_menu_highscore.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"2_menu_highscore.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $ba7197e0d3b83ee9$exports = {};
$ba7197e0d3b83ee9$exports = JSON.parse("{\"name\":\"22_menu_easy\",\"bin\":\"22_menu_easy.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"22_menu_normal.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $963403e0cda629c4$exports = {};
$963403e0cda629c4$exports = JSON.parse("{\"name\":\"22_menu_normal\",\"bin\":\"22_menu_normal.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"22_menu_normal.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $57cfc67211c8c339$exports = {};
$57cfc67211c8c339$exports = JSON.parse("{\"name\":\"22_menu_hard\",\"bin\":\"22_menu_hard.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"22_menu_hard.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $eea6e6b0d73dd639$exports = {};
$eea6e6b0d73dd639$exports = JSON.parse("{\"name\":\"2_menu_back\",\"bin\":\"2_menu_back.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"menu_stuff\",\"parent\":1,\"t\":[0,0,0],\"q\":[-5.338506e-8,-0.7071067,-0.7071069,-5.338508e-8],\"s\":[7,7,7]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"menu\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"2_0_menu_back.png\"}],\"anims\":[{\"name\":\"move\",\"duration\":0.46667,\"fps\":30,\"frames\":15,\"offset\":192}]}");


var $d7de55da2964a484$exports = {};
$d7de55da2964a484$exports = JSON.parse("{\"name\":\"0_background_title\",\"bin\":\"0_background_title.bin\",\"nodes\":[{\"name\":\"\",\"parent\":-1,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[1,1,1]},{\"name\":\"Fbx_Root\",\"parent\":0,\"t\":[0,0,0],\"q\":[-0.7071068,0,0,0.7071068],\"s\":[0.0254,0.0254,0.0254]},{\"name\":\"Producer Perspective\",\"parent\":0,\"t\":[0,0.713,2.875],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Top\",\"parent\":0,\"t\":[0,40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Bottom\",\"parent\":0,\"t\":[0,-40,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Front\",\"parent\":0,\"t\":[0,0,40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Back\",\"parent\":0,\"t\":[0,0,-40],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Right\",\"parent\":0,\"t\":[40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Producer Left\",\"parent\":0,\"t\":[-40,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Camera Switcher\",\"parent\":0,\"t\":[0,0,0],\"q\":[0,0,0,1],\"s\":[0.01,0.01,0.01]},{\"name\":\"Plane01\",\"parent\":1,\"t\":[0,0.000001652084,0],\"q\":[3.850602e-7,-0.7071065,-0.7071071,-3.681955e-7],\"s\":[1,1,1]}],\"parts\":[{\"node\":10,\"vertexCount\":6,\"posOffset\":0,\"nrmOffset\":72,\"uvOffset\":144,\"material\":\"Material #0\",\"color\":[0.588,0.588,0.588,1],\"texture\":\"title13a.png\"}],\"anims\":[]}");


const $25b4b9b6aebf4516$export$c551a9539b16d259 = [
    "menuStart",
    "menuHowto",
    "menuOptions",
    "menuHighscore",
    "menuEasy",
    "menuNormal",
    "menuHard",
    "menuBack",
    "menuTitle"
];
const $25b4b9b6aebf4516$export$13c47d1bd7c9578f = (0, $1669b27dd88d6d65$export$446bfe4478ff8d71)(Math.PI / 2);
var $9482e0a2d9014916$exports = {};
$9482e0a2d9014916$exports = import.meta.resolve("5m8P0");


var $a4a698e1ddb05665$exports = {};
$a4a698e1ddb05665$exports = import.meta.resolve("2YFln");


var $29b63bfbe48c4c4b$exports = {};
$29b63bfbe48c4c4b$exports = import.meta.resolve("6STf6");


var $7b03d912e23dfbe9$exports = {};
$7b03d912e23dfbe9$exports = import.meta.resolve("iCyMs");


var $afc9beba328cfc68$exports = {};
$afc9beba328cfc68$exports = import.meta.resolve("8Hy3H");


var $4311b67fbfbb0629$exports = {};
$4311b67fbfbb0629$exports = import.meta.resolve("lPOlU");


var $5f629803ab9967cd$exports = {};
$5f629803ab9967cd$exports = import.meta.resolve("80vTX");


var $e4029a5816c95df5$exports = {};
$e4029a5816c95df5$exports = import.meta.resolve("cS7Pf");


var $0de065c5524c33b9$exports = {};
$0de065c5524c33b9$exports = import.meta.resolve("cLvsb");


var $f4f0ff59f4417b08$exports = {};
$f4f0ff59f4417b08$exports = import.meta.resolve("jIkBe");


var $32b2b7ca0c24f00d$exports = {};
$32b2b7ca0c24f00d$exports = import.meta.resolve("9RNJM");


var $c542007509c6419d$exports = {};
$c542007509c6419d$exports = import.meta.resolve("8AE7S");


var $9f80d2900e0527ed$exports = {};
$9f80d2900e0527ed$exports = import.meta.resolve("1qlDd");


var $9fbacd2e6223e739$exports = {};
$9fbacd2e6223e739$exports = import.meta.resolve("8z8XS");


var $96f238776f8a6fed$exports = {};
$96f238776f8a6fed$exports = import.meta.resolve("3hxcO");


var $067e8715a052e9a8$exports = {};
$067e8715a052e9a8$exports = import.meta.resolve("k352b");


var $8b679265a1b7f1a7$exports = {};
$8b679265a1b7f1a7$exports = import.meta.resolve("e9mqM");


var $b60e4ccc068affc6$exports = {};
$b60e4ccc068affc6$exports = import.meta.resolve("9618K");


var $92af29f29a960389$exports = {};
$92af29f29a960389$exports = import.meta.resolve("sZNeS");


var $47c1e7eeaff8cee8$exports = {};
$47c1e7eeaff8cee8$exports = import.meta.resolve("dPqYW");


var $298ef755a467b1da$exports = {};
$298ef755a467b1da$exports = import.meta.resolve("bjmR2");


var $5af28522ea1a6faa$exports = {};
$5af28522ea1a6faa$exports = import.meta.resolve("hO4cq");


var $9c38d70b0b527d70$exports = {};
$9c38d70b0b527d70$exports = import.meta.resolve("6MLfJ");


var $a4d44369a4c725f4$exports = {};
$a4d44369a4c725f4$exports = import.meta.resolve("92Gdq");


var $f12d48e116d2f9f6$exports = {};
$f12d48e116d2f9f6$exports = import.meta.resolve("bdpB3");


var $b6b99e15b3686b0a$exports = {};
$b6b99e15b3686b0a$exports = import.meta.resolve("gUCxl");


var $30a5083a31f16c75$exports = {};
$30a5083a31f16c75$exports = import.meta.resolve("dBISO");


var $84563099084b13e9$exports = {};
$84563099084b13e9$exports = import.meta.resolve("klHaG");


var $fb7c60713683c6c5$exports = {};
$fb7c60713683c6c5$exports = import.meta.resolve("aMIse");


var $485dce2dca072aa7$exports = {};
$485dce2dca072aa7$exports = import.meta.resolve("kf5Le");


// One literal `new URL` per texture: Parcel rewrites these at build time and a
// template-literal path silently leaves a `file:///…` URL that the browser
// refuses to fetch. Shared textures are named once here and referenced by
// several entries below.
//
// TODO(texture-cache): `5buggs_standard.png` and `eye.png` are each uploaded
// once per model that names them (4x and 5x respectively) — `loadCookedModel`
// only dedupes within one model. They are small and the roster loads once at
// startup, so a shared GL-texture cache is not worth building yet.
const $25b4b9b6aebf4516$var$TEXTURES = {
    "0_bug_black_skull.png": new URL($9482e0a2d9014916$exports),
    "0_bug_spike.png": new URL($a4a698e1ddb05665$exports),
    "5buggs_standard.png": new URL($29b63bfbe48c4c4b$exports),
    "apfel_stil.png": new URL($7b03d912e23dfbe9$exports),
    "apfelblatt.png": new URL($afc9beba328cfc68$exports),
    "apple.png": new URL($4311b67fbfbb0629$exports),
    "eye.png": new URL($5f629803ab9967cd$exports),
    "eye2.png": new URL($e4029a5816c95df5$exports),
    "eye3.png": new URL($0de065c5524c33b9$exports),
    "eye_black2.png": new URL($f4f0ff59f4417b08$exports),
    "lady_bug.png": new URL($32b2b7ca0c24f00d$exports),
    // The plants name exactly one texture each; mushroom3's cooked manifest
    // references only its "b" PSD, so `0_plant_mushroom3.png` stays uncommitted.
    "0_plant_mushroom1a.png": new URL($c542007509c6419d$exports),
    "0_plant_mushroom2a.png": new URL($9f80d2900e0527ed$exports),
    "0_plant_mushroom3b.png": new URL($9fbacd2e6223e739$exports),
    "0_plant_mushroom_4.png": new URL($96f238776f8a6fed$exports),
    "0_plant_mushroom5.png": new URL($067e8715a052e9a8$exports),
    "0_plant_flower_1.png": new URL($8b679265a1b7f1a7$exports),
    "0_plant_flower_2.png": new URL($b60e4ccc068affc6$exports),
    "0_plant_flower_3.png": new URL($92af29f29a960389$exports),
    "0_plant_flower_4.png": new URL($47c1e7eeaff8cee8$exports),
    "0_plant_flower_5.png": new URL($298ef755a467b1da$exports),
    // Menu text planes (plan 17). The shipped difficulty *labels* are a rename
    // the FBX file names never caught up with: `22_menu_easy`'s own texture
    // reads NORMAL, `22_menu_normal`'s reads BUGGY, `22_menu_hard`'s BUGG HARD
    // (the highscore tables `_normal`/`_buggy`/`_bugg_hard` confirm the naming).
    // Both of the first two FBXs reference the texture *name*
    // "22_menu_normal.png"; the extractor disambiguated the two same-named
    // source PSDs, so the two entries below map that one name to different
    // files per model.
    "2_menu_single_bugg.png": new URL($5af28522ea1a6faa$exports),
    "21_menu_howto.png": new URL($9c38d70b0b527d70$exports),
    "2_menu_options.png": new URL($a4d44369a4c725f4$exports),
    "2_menu_highscore.png": new URL($f12d48e116d2f9f6$exports),
    "22_menu_normal.png": new URL($b6b99e15b3686b0a$exports),
    "22_menu_normal_afdecb.png": new URL($30a5083a31f16c75$exports),
    "22_menu_hard.png": new URL($84563099084b13e9$exports),
    "2_0_menu_back.png": new URL($fb7c60713683c6c5$exports),
    "title13a.png": new URL($485dce2dca072aa7$exports)
};
var $f4b963a99a146316$exports = {};
$f4b963a99a146316$exports = import.meta.resolve("7Hruj");


var $dfb4d90dd8e0b26f$exports = {};
$dfb4d90dd8e0b26f$exports = import.meta.resolve("i6nFS");


var $df7fc5b29fba7509$exports = {};
$df7fc5b29fba7509$exports = import.meta.resolve("lH8PY");


var $8b4f6f2bd028c2f5$exports = {};
$8b4f6f2bd028c2f5$exports = import.meta.resolve("1UTbM");


var $51cbd4ca634eae4a$exports = {};
$51cbd4ca634eae4a$exports = import.meta.resolve("lwC4E");


var $255159cebe1c036b$exports = {};
$255159cebe1c036b$exports = import.meta.resolve("jsHPd");


var $72ed7f42d2a148de$exports = {};
$72ed7f42d2a148de$exports = import.meta.resolve("fYuAc");


var $d394a4f0586436fa$exports = {};
$d394a4f0586436fa$exports = import.meta.resolve("4r3gu");


var $b3ebc5639668b38d$exports = {};
$b3ebc5639668b38d$exports = import.meta.resolve("1otVY");


var $63808b0cfaeb6f82$exports = {};
$63808b0cfaeb6f82$exports = import.meta.resolve("jeXZ1");


var $c22eaf0ccb73c0b1$exports = {};
$c22eaf0ccb73c0b1$exports = import.meta.resolve("9QREj");


var $9f3c5bc8e82fcea8$exports = {};
$9f3c5bc8e82fcea8$exports = import.meta.resolve("jWg4l");


var $acc9ab319e7fc80c$exports = {};
$acc9ab319e7fc80c$exports = import.meta.resolve("aUV0L");


var $e3072fc025c8bf69$exports = {};
$e3072fc025c8bf69$exports = import.meta.resolve("hZsql");


var $bbcc778830d5896b$exports = {};
$bbcc778830d5896b$exports = import.meta.resolve("beRpH");


var $1e063043856cf3c6$exports = {};
$1e063043856cf3c6$exports = import.meta.resolve("62UWT");


var $1525bb004500ca89$exports = {};
$1525bb004500ca89$exports = import.meta.resolve("g6pIm");


var $804ccc3f582c0e23$exports = {};
$804ccc3f582c0e23$exports = import.meta.resolve("gG0PV");


var $ad7ad78e407a7b8a$exports = {};
$ad7ad78e407a7b8a$exports = import.meta.resolve("1k1gQ");


var $83178001711d8c27$exports = {};
$83178001711d8c27$exports = import.meta.resolve("ezOkc");


var $b8a4738df4aff805$exports = {};
$b8a4738df4aff805$exports = import.meta.resolve("6hV0y");


var $b7f700de97e4071e$exports = {};
$b7f700de97e4071e$exports = import.meta.resolve("hebuh");


var $c450cece4f5f1092$exports = {};
$c450cece4f5f1092$exports = import.meta.resolve("4wdZx");


var $7bc085488bb93aab$exports = {};
$7bc085488bb93aab$exports = import.meta.resolve("gXQXS");


var $9d502dfd53c8d2f7$exports = {};
$9d502dfd53c8d2f7$exports = import.meta.resolve("ahM1Y");


var $90c10dbcbcf4d6a2$exports = {};
$90c10dbcbcf4d6a2$exports = import.meta.resolve("4SFG5");


var $cf6f1a086a389ac1$exports = {};
$cf6f1a086a389ac1$exports = import.meta.resolve("77PCd");


const $25b4b9b6aebf4516$export$e2023bf903dbea56 = {
    standard: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($a568d37d83f9138e$exports))),
            bin: new URL($f4b963a99a146316$exports),
            textures: {
                "5buggs_standard.png": $25b4b9b6aebf4516$var$TEXTURES["5buggs_standard.png"],
                "eye2.png": $25b4b9b6aebf4516$var$TEXTURES["eye2.png"]
            }
        },
        fix: null,
        lift: 0.378
    },
    lady: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($8e56324373bfac34$exports))),
            bin: new URL($dfb4d90dd8e0b26f$exports),
            textures: {
                "lady_bug.png": $25b4b9b6aebf4516$var$TEXTURES["lady_bug.png"],
                "eye.png": $25b4b9b6aebf4516$var$TEXTURES["eye.png"]
            }
        },
        fix: null,
        lift: 0.202
    },
    fat: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($e7d8415238c1bec3$exports))),
            bin: new URL($df7fc5b29fba7509$exports),
            textures: {
                "5buggs_standard.png": $25b4b9b6aebf4516$var$TEXTURES["5buggs_standard.png"],
                "eye.png": $25b4b9b6aebf4516$var$TEXTURES["eye.png"]
            }
        },
        fix: null,
        lift: 0.529
    },
    speed: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($24b4e076c13a4198$exports))),
            bin: new URL($8b4f6f2bd028c2f5$exports),
            textures: {
                "5buggs_standard.png": $25b4b9b6aebf4516$var$TEXTURES["5buggs_standard.png"],
                "eye3.png": $25b4b9b6aebf4516$var$TEXTURES["eye3.png"]
            }
        },
        fix: null,
        lift: 0.334
    },
    spy: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($33568d9d768cf7b6$exports))),
            bin: new URL($51cbd4ca634eae4a$exports),
            textures: {
                "5buggs_standard.png": $25b4b9b6aebf4516$var$TEXTURES["5buggs_standard.png"],
                "eye.png": $25b4b9b6aebf4516$var$TEXTURES["eye.png"]
            }
        },
        fix: null,
        lift: 0.544
    },
    spike: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($65d9431b8f5f097b$exports))),
            bin: new URL($255159cebe1c036b$exports),
            textures: {
                "0_bug_spike.png": $25b4b9b6aebf4516$var$TEXTURES["0_bug_spike.png"],
                "eye.png": $25b4b9b6aebf4516$var$TEXTURES["eye.png"]
            }
        },
        fix: null,
        lift: 0.497
    },
    black: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($ef8b92ffec715ff7$exports))),
            bin: new URL($72ed7f42d2a148de$exports),
            textures: {
                "0_bug_black_skull.png": $25b4b9b6aebf4516$var$TEXTURES["0_bug_black_skull.png"],
                "eye_black2.png": $25b4b9b6aebf4516$var$TEXTURES["eye_black2.png"]
            }
        },
        fix: null,
        lift: 0.445
    },
    apple: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($e2362be944cc2183$exports))),
            bin: new URL($d394a4f0586436fa$exports),
            textures: {
                "apple.png": $25b4b9b6aebf4516$var$TEXTURES["apple.png"],
                "apfel_stil.png": $25b4b9b6aebf4516$var$TEXTURES["apfel_stil.png"],
                "apfelblatt.png": $25b4b9b6aebf4516$var$TEXTURES["apfelblatt.png"]
            }
        },
        fix: null,
        lift: 1.124
    },
    // The ten power-up plants (plan 16 §1). Lifts are the rest-pose +z extent
    // like every other entry; the flowers are nearly flat (lift 0.06–0.11), so
    // they hug the surface where the mushrooms stand tall on their stalks.
    mushroom1: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($80b6c3149485d86e$exports))),
            bin: new URL($b3ebc5639668b38d$exports),
            textures: {
                "0_plant_mushroom1a.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_mushroom1a.png"]
            }
        },
        fix: null,
        lift: 0.855
    },
    mushroom2: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($42bd533a60751363$exports))),
            bin: new URL($63808b0cfaeb6f82$exports),
            textures: {
                "0_plant_mushroom2a.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_mushroom2a.png"]
            }
        },
        fix: null,
        lift: 1.125
    },
    mushroom3: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($96729eb1934e73c7$exports))),
            bin: new URL($c22eaf0ccb73c0b1$exports),
            textures: {
                "0_plant_mushroom3b.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_mushroom3b.png"]
            }
        },
        fix: null,
        lift: 0.61
    },
    mushroom4: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($d9a91875b7da6727$exports))),
            bin: new URL($9f3c5bc8e82fcea8$exports),
            textures: {
                "0_plant_mushroom_4.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_mushroom_4.png"]
            }
        },
        fix: null,
        lift: 1.138
    },
    mushroom5: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($388ae6fd6bf36503$exports))),
            bin: new URL($acc9ab319e7fc80c$exports),
            textures: {
                "0_plant_mushroom5.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_mushroom5.png"]
            }
        },
        fix: null,
        lift: 0.655
    },
    flower1: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($577edafed3e0d4f5$exports))),
            bin: new URL($e3072fc025c8bf69$exports),
            textures: {
                "0_plant_flower_1.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_flower_1.png"]
            }
        },
        fix: null,
        lift: 0.108
    },
    flower2: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($a2be4449b82a6e2c$exports))),
            bin: new URL($bbcc778830d5896b$exports),
            textures: {
                "0_plant_flower_2.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_flower_2.png"]
            }
        },
        fix: null,
        lift: 0.098
    },
    flower3: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($864caba18c181cb0$exports))),
            bin: new URL($1e063043856cf3c6$exports),
            textures: {
                "0_plant_flower_3.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_flower_3.png"]
            }
        },
        fix: null,
        lift: 0.077
    },
    flower4: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($37869967ac32afe1$exports))),
            bin: new URL($1525bb004500ca89$exports),
            textures: {
                "0_plant_flower_4.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_flower_4.png"]
            }
        },
        fix: null,
        lift: 0.062
    },
    flower5: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($16fb392ad5a0ba23$exports))),
            bin: new URL($804ccc3f582c0e23$exports),
            textures: {
                "0_plant_flower_5.png": $25b4b9b6aebf4516$var$TEXTURES["0_plant_flower_5.png"]
            }
        },
        fix: null,
        lift: 0.086
    },
    // The menu planes (plan 17). `fix`/`lift` are inert — placement is the menu
    // page transform, not `placeOnSurface` (see `MenuModelName`).
    menuStart: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($ed181d07ea3090da$exports))),
            bin: new URL($ad7ad78e407a7b8a$exports),
            textures: {
                "2_menu_single_bugg.png": $25b4b9b6aebf4516$var$TEXTURES["2_menu_single_bugg.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuHowto: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($6c9c018d54329624$exports))),
            bin: new URL($83178001711d8c27$exports),
            textures: {
                "21_menu_howto.png": $25b4b9b6aebf4516$var$TEXTURES["21_menu_howto.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuOptions: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($6265e6f037b21aee$exports))),
            bin: new URL($b8a4738df4aff805$exports),
            textures: {
                "2_menu_options.png": $25b4b9b6aebf4516$var$TEXTURES["2_menu_options.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuHighscore: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($10f195310ee455ee$exports))),
            bin: new URL($b7f700de97e4071e$exports),
            textures: {
                "2_menu_highscore.png": $25b4b9b6aebf4516$var$TEXTURES["2_menu_highscore.png"]
            }
        },
        fix: null,
        lift: 0
    },
    // easy/normal: both manifests name "22_menu_normal.png"; the files differ
    // (label rename — see the TEXTURES comment). easy shows NORMAL, normal BUGGY.
    menuEasy: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($ba7197e0d3b83ee9$exports))),
            bin: new URL($c450cece4f5f1092$exports),
            textures: {
                "22_menu_normal.png": $25b4b9b6aebf4516$var$TEXTURES["22_menu_normal.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuNormal: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($963403e0cda629c4$exports))),
            bin: new URL($7bc085488bb93aab$exports),
            textures: {
                "22_menu_normal.png": $25b4b9b6aebf4516$var$TEXTURES["22_menu_normal_afdecb.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuHard: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($57cfc67211c8c339$exports))),
            bin: new URL($9d502dfd53c8d2f7$exports),
            textures: {
                "22_menu_hard.png": $25b4b9b6aebf4516$var$TEXTURES["22_menu_hard.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuBack: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($eea6e6b0d73dd639$exports))),
            bin: new URL($90c10dbcbcf4d6a2$exports),
            textures: {
                "2_0_menu_back.png": $25b4b9b6aebf4516$var$TEXTURES["2_0_menu_back.png"]
            }
        },
        fix: null,
        lift: 0
    },
    menuTitle: {
        source: {
            manifest: (0, (/*@__PURE__*/$parcel$interopDefault($d7de55da2964a484$exports))),
            bin: new URL($cf6f1a086a389ac1$exports),
            textures: {
                "title13a.png": $25b4b9b6aebf4516$var$TEXTURES["title13a.png"]
            }
        },
        fix: null,
        lift: 0
    }
};
const $25b4b9b6aebf4516$export$2b6c32962192c4a4 = Object.keys($25b4b9b6aebf4516$export$e2023bf903dbea56);
async function $25b4b9b6aebf4516$export$1702f9080daf04fe(gl) {
    const loaded = await Promise.all($25b4b9b6aebf4516$export$2b6c32962192c4a4.map(async (name)=>[
            name,
            await (0, $0688557bb19ac86e$export$9170fee8584a3e27)(gl, $25b4b9b6aebf4516$export$e2023bf903dbea56[name].source)
        ]));
    return Object.fromEntries(loaded);
}


// Draws every bug the simulation owns. Reads sim state, never mutates it
// (00-architecture.md §1); the placement math is the surface frame from
// `game/surface.ts` with the cooked model's axis fix applied on top.




const $1886a5c91af384ee$export$6ce129e933bde03f = 0.4 / 3;
/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */ function $1886a5c91af384ee$var$duration(model, clip) {
    return model.anims.get(clip)?.duration ?? 0;
}
function $1886a5c91af384ee$var$appearance(bug, model) {
    const state = bug.state;
    switch(state.kind){
        case "spawning":
            // Grow in from nothing, legs still, so the arrival reads as an arrival.
            return {
                clip: "run",
                time: 0,
                loop: true,
                grow: state.progress
            };
        case "running":
            return {
                clip: "run",
                time: bug.lifeTime,
                loop: true,
                grow: 1
            };
        case "infecting":
            // One bite, played once and held: the sim's BITE_SECONDS is the cooked
            // clip's own 0.8 s, but stretching it here keeps the black bug's shorter
            // clip in step too, exactly as `die` does below.
            return {
                clip: "infect",
                time: state.progress * $1886a5c91af384ee$var$duration(model, "infect"),
                loop: false,
                grow: 1
            };
        case "dying":
            // Plays once and holds the last frame until the sim removes the bug. The
            // clip is stretched over the sim's DIE_SECONDS: the cooked durations run
            // 0.63 s (black) to 1.8 s (standard, fat), and the sim owns the timing.
            return {
                clip: "die",
                time: state.progress * $1886a5c91af384ee$var$duration(model, "die"),
                loop: false,
                grow: 1
            };
        case "takenByPlayer":
            {
                // Only the lady and black bugs can be picked up; the rest fall back to
                // `die`, which every bug has (plan 08 drives this state).
                const clip = model.anims.has("pick") ? "pick" : "die";
                return {
                    clip: clip,
                    time: state.progress * $1886a5c91af384ee$var$duration(model, clip),
                    loop: false,
                    grow: 1
                };
            }
        default:
            {
                const unreachable = state;
                throw new Error(`unhandled bug state ${JSON.stringify(unreachable)}`);
            }
    }
}
function $1886a5c91af384ee$export$ee41df9543d2bc3f(frame, name, grow) {
    const entry = (0, $25b4b9b6aebf4516$export$e2023bf903dbea56)[name];
    const { pos: pos, normal: up, forward: forward } = frame;
    const right = [
        up[1] * forward[2] - up[2] * forward[1],
        up[2] * forward[0] - up[0] * forward[2],
        up[0] * forward[1] - up[1] * forward[0]
    ];
    // `forward` is finite-differenced off the rounded surface, so it is only
    // near-tangent; rebuilding it from right x up makes the basis exactly
    // orthonormal without changing the visible heading.
    const fwd = [
        right[1] * up[2] - right[2] * up[1],
        right[2] * up[0] - right[0] * up[2],
        right[0] * up[1] - right[1] * up[0]
    ];
    const m = new Float32Array(16);
    m[0] = right[0];
    m[1] = right[1];
    m[2] = right[2];
    m[4] = up[0];
    m[5] = up[1];
    m[6] = up[2];
    m[8] = fwd[0];
    m[9] = fwd[1];
    m[10] = fwd[2];
    const lift = entry.lift * $1886a5c91af384ee$export$6ce129e933bde03f * grow;
    m[12] = pos[0] + up[0] * lift;
    m[13] = pos[1] + up[1] * lift;
    m[14] = pos[2] + up[2] * lift;
    m[15] = 1;
    return (0, $1669b27dd88d6d65$export$2060d2db72cce88f)((0, $1669b27dd88d6d65$export$2060d2db72cce88f)(m, entry.fix ?? (0, $25b4b9b6aebf4516$export$13c47d1bd7c9578f)), (0, $1669b27dd88d6d65$export$89390739e80eec4a)($1886a5c91af384ee$export$6ce129e933bde03f * grow));
}
class $1886a5c91af384ee$export$af96eb7f175bb266 {
    models;
    constructor(models){
        this.models = models;
    }
    draw(gl, renderer, bugs, cubeModel, viewProj) {
        for (const bug of bugs.values()){
            const model = this.models[bug.kindDef];
            const look = $1886a5c91af384ee$var$appearance(bug, model);
            if (look.grow <= 0) continue; // a zero-scale model is invisible anyway
            const worlds = (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(model, look.clip, look.time, look.loop);
            renderer.draw(gl, model, worlds, (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, $1886a5c91af384ee$export$ee41df9543d2bc3f((0, $267a74da153328b6$export$93c280f99766aeb9)(bug), bug.kindDef, look.grow)), viewProj);
        }
    }
}


const $e9eec882a992d2bf$export$423a03b28a2355d3 = 0.12;
// Apply the transpose of the model's rotation part; for a pure rotation
// matrix that is its inverse, taking the ray from world into object space.
function $e9eec882a992d2bf$var$rotateByTranspose(m, v) {
    return [
        m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
        m[4] * v[0] + m[5] * v[1] + m[6] * v[2],
        m[8] * v[0] + m[9] * v[1] + m[10] * v[2]
    ];
}
function $e9eec882a992d2bf$export$6e549f13d73aa867(model, canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const ndcX = (clientX - rect.left) / rect.width * 2 - 1;
    const ndcY = 1 - (clientY - rect.top) / rect.height * 2;
    const cam = (0, $238457a12dbae9c5$export$8b7b81ac271094c6)(rect.width / rect.height);
    const f = 1 / Math.tan(cam.fovY / 2);
    // View is a pure translation along z, so the world-space ray is simple.
    const origin = $e9eec882a992d2bf$var$rotateByTranspose(model, [
        0,
        0,
        cam.dist
    ]);
    const raw = $e9eec882a992d2bf$var$rotateByTranspose(model, [
        ndcX * cam.aspect / f,
        ndcY / f,
        -1
    ]);
    const len = Math.hypot(raw[0], raw[1], raw[2]);
    return {
        origin: origin,
        dir: [
            raw[0] / len,
            raw[1] / len,
            raw[2] / len
        ]
    };
}
/**
 * Distance along the ray to a sphere of `BUG_PICK_RADIUS` around a lifted
 * surface frame — the frame's position raised along its normal by the same
 * amount the renderer lifts the model, so the sphere sits on the body rather
 * than under its feet. Null on a miss (or with the camera inside/past it).
 */ function $e9eec882a992d2bf$var$sphereHit(frame, lift, ray) {
    const cx = frame.pos[0] + frame.normal[0] * lift - ray.origin[0];
    const cy = frame.pos[1] + frame.normal[1] * lift - ray.origin[1];
    const cz = frame.pos[2] + frame.normal[2] * lift - ray.origin[2];
    // `dir` is unit length, so the quadratic reduces to this projection form.
    const along = cx * ray.dir[0] + cy * ray.dir[1] + cz * ray.dir[2];
    const gap2 = cx * cx + cy * cy + cz * cz - along * along;
    const r2 = $e9eec882a992d2bf$export$423a03b28a2355d3 * $e9eec882a992d2bf$export$423a03b28a2355d3;
    if (gap2 > r2) return null;
    const t = along - Math.sqrt(r2 - gap2);
    return t < 0 ? null : t;
}
/**
 * Nearest living bug or collectable power-up the ray passes through — one
 * distance sort over both, as the original's raycast resolves everything by
 * distance and breaks on the first hit. Bugs that are already dying or being
 * carried off are skipped, as are taken/killed power-ups: a tap on one must
 * fall through to the block behind it rather than be swallowed.
 */ function $e9eec882a992d2bf$var$nearestTarget(world, ray) {
    let best = null;
    for (const bug of world.bugs.values()){
        if (!(0, $267a74da153328b6$export$3ab83a34b537c934)(bug.state)) continue;
        const t = $e9eec882a992d2bf$var$sphereHit((0, $267a74da153328b6$export$93c280f99766aeb9)(bug), (0, $25b4b9b6aebf4516$export$e2023bf903dbea56)[bug.kindDef].lift * (0, $1886a5c91af384ee$export$6ce129e933bde03f), ray);
        if (t !== null && (best === null || t < best.t)) best = {
            target: {
                kind: "bug",
                id: bug.id
            },
            t: t
        };
    }
    for (const powerUp of world.powerups.values()){
        if (!(0, $08dfdfd795a13c32$export$ceb0abbf2403c9de)(powerUp.state)) continue;
        const name = (0, $0f82c75150dd11fd$export$8c930e56a7009e81)[powerUp.slot].model;
        const t = $e9eec882a992d2bf$var$sphereHit((0, $08dfdfd795a13c32$export$94b2b7bc7de2d125)(powerUp), (0, $25b4b9b6aebf4516$export$e2023bf903dbea56)[name].lift * (0, $1886a5c91af384ee$export$6ce129e933bde03f), ray);
        if (t !== null && (best === null || t < best.t)) best = {
            target: {
                kind: "powerup",
                id: powerUp.id
            },
            t: t
        };
    }
    return best;
}
/** Where the ray enters the cube's bounding box, or null if it misses. */ function $e9eec882a992d2bf$var$enterCube(ray) {
    let tMin = -Infinity;
    let tMax = Infinity;
    for(let a = 0; a < 3; a++){
        if (Math.abs(ray.dir[a]) < 1e-9) {
            if (ray.origin[a] < -0.5 || ray.origin[a] > 0.5) return null;
            continue;
        }
        const t1 = (-0.5 - ray.origin[a]) / ray.dir[a];
        const t2 = (0.5 - ray.origin[a]) / ray.dir[a];
        tMin = Math.max(tMin, Math.min(t1, t2));
        tMax = Math.min(tMax, Math.max(t1, t2));
    }
    if (tMax < Math.max(tMin, 0)) return null;
    return Math.max(tMin, 0);
}
/**
 * The nearest damaged cell along the ray — anything that is not `healthy`.
 *
 * Ports `Cube.MouseInteractionWithObjects`, which walks its distance-sorted
 * raycast hits collecting non-healthy blocks and **breaks on the first Healthy
 * one**: you cannot repair through intact grass, but you can tap the visible
 * back wall of a hole and repair the hole itself. Returning the damaged cell
 * directly replaces the `prev` bookkeeping an earlier version needed, because a
 * ghost is now a cell in its own right rather than an absence.
 *
 * Not ported: the original collects *every* damaged cell along the ray and
 * tries `RepairBlock` on each until one succeeds, so a nearest ghost whose
 * repair is refused (a walker on it) falls through to the next one behind it.
 * Returning only the nearest keeps picking a pure view-side query; the fallback
 * matters solely when a walker is standing on the target.
 */ function $e9eec882a992d2bf$var$marchBlocks(grid, ray) {
    const entry = $e9eec882a992d2bf$var$enterCube(ray);
    if (entry === null) return null;
    const n = grid.size;
    const cellSize = 1 / n;
    const tEnter = entry + 1e-6;
    const cell = [
        0,
        0,
        0
    ];
    const step = [
        0,
        0,
        0
    ];
    const tNext = [
        Infinity,
        Infinity,
        Infinity
    ];
    const tDelta = [
        Infinity,
        Infinity,
        Infinity
    ];
    for(let a = 0; a < 3; a++){
        const p = ray.origin[a] + ray.dir[a] * tEnter;
        cell[a] = Math.min(n - 1, Math.max(0, Math.floor((p + 0.5) * n)));
        if (ray.dir[a] > 0) step[a] = 1;
        else if (ray.dir[a] < 0) step[a] = -1;
        if (step[a] !== 0) {
            const boundary = (cell[a] + (step[a] > 0 ? 1 : 0)) * cellSize - 0.5;
            tNext[a] = (boundary - ray.origin[a]) / ray.dir[a];
            tDelta[a] = cellSize / Math.abs(ray.dir[a]);
        }
    }
    // In practice this decides on the very first cell the ray enters — every cell
    // is either healthy (stop) or damaged (target). The walk is kept because the
    // entry point is nudged by an epsilon and may land a hair outside the grid.
    let tCell = tEnter;
    for(let i = 0; i < 3 * n + 3; i++){
        if (cell.every((c)=>c >= 0 && c < n)) {
            if (grid.state(cell[0], cell[1], cell[2]) === "healthy") return null;
            return {
                cell: [
                    cell[0],
                    cell[1],
                    cell[2]
                ],
                t: tCell
            };
        }
        const a = tNext[0] < tNext[1] ? tNext[0] < tNext[2] ? 0 : 2 : tNext[1] < tNext[2] ? 1 : 2;
        tCell = tNext[a];
        cell[a] += step[a];
        tNext[a] += tDelta[a];
    }
    return null;
}
function $e9eec882a992d2bf$export$357523c63a2253b9(clientX, clientY, canvas, cubeModel, world) {
    const ray = $e9eec882a992d2bf$export$6e549f13d73aa867(cubeModel, canvas, clientX, clientY);
    const hit = $e9eec882a992d2bf$var$nearestTarget(world, ray);
    const block = $e9eec882a992d2bf$var$marchBlocks(world.blocks, ray);
    if (hit !== null && (block === null || hit.t <= block.t)) return hit.target;
    if (block === null) return null;
    return {
        kind: "block",
        cell: block.cell
    };
}



// Discrete rotation steps from cursor keys and swipes.
// Each key press or swipe queues one quarter-turn around the screen X or Y axis.
const $8efa37a468313d04$var$SWIPE_THRESHOLD = 30; // CSS px of travel before a drag counts as a swipe
const $8efa37a468313d04$var$MAX_QUEUE = 2; // buffer a little input during animation, but don't spiral
const $8efa37a468313d04$var$KEY_STEPS = {
    ArrowLeft: {
        axis: "y",
        dir: -1
    },
    ArrowRight: {
        axis: "y",
        dir: 1
    },
    ArrowUp: {
        axis: "x",
        dir: -1
    },
    ArrowDown: {
        axis: "x",
        dir: 1
    }
};
class $8efa37a468313d04$export$19de138317f754cf {
    onTap = null;
    queue = [];
    pointerActive = false;
    startX = 0;
    startY = 0;
    constructor(target){
        window.addEventListener("keydown", (e)=>{
            const step = $8efa37a468313d04$var$KEY_STEPS[e.key];
            if (!step) return;
            e.preventDefault();
            this.push(step);
        });
        target.addEventListener("pointerdown", (e)=>{
            this.pointerActive = true;
            this.startX = e.clientX;
            this.startY = e.clientY;
            target.setPointerCapture(e.pointerId);
        });
        target.addEventListener("pointerup", (e)=>{
            if (!this.pointerActive) return;
            this.pointerActive = false;
            const dx = e.clientX - this.startX;
            const dy = e.clientY - this.startY;
            if (Math.max(Math.abs(dx), Math.abs(dy)) < $8efa37a468313d04$var$SWIPE_THRESHOLD) {
                this.onTap?.({
                    x: e.clientX,
                    y: e.clientY
                });
                return;
            }
            if (Math.abs(dx) >= Math.abs(dy)) this.push({
                axis: "y",
                dir: dx > 0 ? 1 : -1
            });
            else this.push({
                axis: "x",
                dir: dy > 0 ? 1 : -1
            });
        });
        target.addEventListener("pointercancel", ()=>{
            this.pointerActive = false;
        });
        // The game owns every button on the canvas; no browser menu on it.
        target.addEventListener("contextmenu", (e)=>e.preventDefault());
    }
    next() {
        return this.queue.shift() ?? null;
    }
    push(step) {
        if (this.queue.length < $8efa37a468313d04$var$MAX_QUEUE) this.queue.push(step);
    }
}




// Draws the apples the simulation owns. Reads sim state, never mutates it
// (00-architecture.md §1); placement is the shared `placeOnSurface` the bug
// renderer uses, fed the apple's own frame (which interpolates its pivot).


// The apples: spawn points that roam the surface and periodically stop to
// produce a bug. Ports `SpawnPoint.cs` plus the `MoveStopSpawnTurn` half of the
// spawn-point state machine in `BugsModel.cs` (~950–1210) — walk a leg, stop,
// spawn a weighted bug kind, pivot 90 degrees, repeat.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).





/** Slack for landmark distances along a lane, in cells. */ const $fa1e7313cc9cf93a$var$EPS = 1e-9;
function $fa1e7313cc9cf93a$export$60150edaea0df6d6(rng, mean) {
    const raw = -mean * Math.log(1 - rng());
    return Math.min(3 * mean, Math.max(0.5 * mean, raw));
}
function $fa1e7313cc9cf93a$export$954069c662db8484(rng, weights) {
    const index = (0, $8c55fc03ff988801$export$653e707a21062548)(rng, weights);
    return index === null ? null : (0, $0f82c75150dd11fd$export$89a9536059633afb)[index];
}
/**
 * Distance along the heading to the next lane centre. Apples stop only on
 * centres: that is where the original parks them (`CubeWalkerPrepareForStopIn
 * Center`) and it is what puts a freshly spawned bug on a lane centre.
 */ function $fa1e7313cc9cf93a$var$distanceToNextCentre(p) {
    const along = p.dir === 0 || p.dir === 2 ? p.u : p.v;
    const frac = along - Math.floor(along);
    const forward = p.dir === 0 || p.dir === 1;
    const ahead = forward ? 0.5 - frac : frac - 0.5;
    return ahead > $fa1e7313cc9cf93a$var$EPS ? ahead : ahead + 1;
}
/**
 * Snap both coordinates onto the tile centre the walker has just reached. Only
 * called on arrival at a centre, so the fractional parts are already 0.5 up to
 * accumulated float error — this stops that error from compounding over a lap.
 */ function $fa1e7313cc9cf93a$var$snapToCentre(p) {
    return {
        ...p,
        u: Math.floor(p.u) + 0.5,
        v: Math.floor(p.v) + 0.5
    };
}
function $fa1e7313cc9cf93a$export$ef469229ac854fd0(s) {
    if (s.state.kind !== "turning") return (0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)(s.pose);
    const from = (0, $733d112f9a19cd2b$export$1c1343d814ee6703)[s.pose.dir];
    const to = (0, $733d112f9a19cd2b$export$1c1343d814ee6703)[(0, $733d112f9a19cd2b$export$af85df91db0258da)(s.pose).dir];
    const a = s.state.progress * Math.PI / 2;
    const c = Math.cos(a);
    const t = Math.sin(a);
    return (0, $733d112f9a19cd2b$export$17fffb2ca18f2877)(s.pose.face, s.pose.u, s.pose.v, from[0] * c + to[0] * t, from[1] * c + to[1] * t);
}
class $fa1e7313cc9cf93a$export$41011d84e918e916 {
    events;
    rng;
    bugs;
    occupancy;
    levels;
    modifiers;
    map;
    nextId;
    constructor(events, rng, bugs, occupancy, // Plan 06 injected a single `LevelDef` here rather than hard-wiring LEVEL_1
    // precisely so plan 15 could swap it for the clock: the row is now read per
    // use, so a level crossing takes effect without anything being rebuilt.
    levels, // The double-spawn power-up (plan 16); defaults to 1x for the suites that
    // are not about power-ups.
    modifiers = (0, $aae4ef88cc18edf2$export$1a5e2253a67e66c4)()){
        this.events = events;
        this.rng = rng;
        this.bugs = bugs;
        this.occupancy = occupancy;
        this.levels = levels;
        this.modifiers = modifiers;
        this.map = new Map();
        this.nextId = -1;
    }
    /**
   * A freshly drawn spawn delay, divided by the double-spawn effect's rate —
   * the original's `TimeUntilNextSpawn /= _bugSpawnRate` (`BugsModel.cs:1053`)
   * applied where the delay is computed, so a rate change mid-countdown does
   * not retroactively bend a delay already drawn, as it does not there either.
   */ drawSpawnDelay() {
        return $fa1e7313cc9cf93a$export$60150edaea0df6d6(this.rng, this.level.meanTimeBetweenBugSpawn) / this.modifiers.spawnRate;
    }
    /** The row governing spawning right now. */ get level() {
        return this.levels.current;
    }
    get spawners() {
        return this.map;
    }
    /** Place an apple at `pose`, walking, with its first spawn already scheduled. */ add(pose) {
        const id = this.nextId--;
        const spawner = {
            id: id,
            pose: pose,
            state: {
                kind: "moving",
                remaining: (0, $0f82c75150dd11fd$export$ecc24cbb1055bc3e)
            },
            nextSpawnIn: this.drawSpawnDelay()
        };
        this.map.set(id, spawner);
        this.occupancy.reclaim(id, (0, $45485603a00f3ba6$export$a15fb836434290c4)(pose));
        this.events.emit("spawnerAdded", {
            id: id
        });
        return spawner;
    }
    /**
   * Retire an apple — a level whose `AmountOfSpawnPoints` dropped
   * (`RemoveSpawnPoint`, `BugsModel.cs:44-49`). Its tile claims go with it, so
   * a walker braking behind it is released the same step.
   */ remove(id) {
        if (!this.map.delete(id)) return;
        this.occupancy.releaseAll(id);
        this.events.emit("spawnerRemoved", {
            id: id
        });
    }
    /** Advance every apple by `dt` seconds. */ update(dt) {
        for (const s of this.map.values()){
            const before = s.state.kind;
            this.step(s, dt);
            if (s.state.kind !== before) this.events.emit("spawnerStateChanged", {
                id: s.id,
                state: s.state.kind
            });
        }
    }
    /**
   * The one place spawner transitions live (00-architecture.md §5): an
   * exhaustive switch with a `never` check.
   */ step(s, dt) {
        // The countdown runs in every state, not just while walking: the original
        // credits the apple's stop against it explicitly (`TimeUntilNextSpawn -= 2f`
        // after spawning, `BugsModel.cs` ~1154). Ticking it throughout is the same
        // thing without hard-coding the stop's length.
        s.nextSpawnIn -= dt;
        const state = s.state;
        switch(state.kind){
            case "moving":
                state.remaining -= dt;
                this.walk(s, state, dt);
                return;
            case "spawning":
                state.progress += dt / (0, $0f82c75150dd11fd$export$976c8f9b7547c13c);
                if (state.progress < 1) return;
                s.state = {
                    kind: "turning",
                    progress: 0
                };
                return;
            case "turning":
                state.progress += dt / (0, $0f82c75150dd11fd$export$943f87761ed966d);
                if (state.progress < 1) return;
                s.pose = (0, $733d112f9a19cd2b$export$af85df91db0258da)(s.pose);
                this.startLeg(s);
                return;
            default:
                {
                    const unreachable = state;
                    throw new Error(`unhandled spawner state ${JSON.stringify(unreachable)}`);
                }
        }
    }
    /** Resume walking, taking the tiles the moving body needs if they are free. */ startLeg(s) {
        s.state = {
            kind: "moving",
            remaining: (0, $0f82c75150dd11fd$export$ecc24cbb1055bc3e)
        };
        this.occupancy.reclaim(s.id, (0, $45485603a00f3ba6$export$3fe8347445b8cd97)(s.pose));
    }
    /**
   * One step of a leg: walk at a constant speed, stopping at every lane centre
   * to see whether the leg is over. A tile it may not enter turns it right, the
   * same rule bugs follow — the original re-plans at block boundaries too.
   */ walk(s, state, dt) {
        // The level's `SpeedFactor` dilates apples too — it is applied to the
        // apple's leg budget in the original (`BugsModel.cs:1054`, `:1065`).
        let budget = (0, $0f82c75150dd11fd$export$a432f4c95d0c5c38) * this.levels.speedFactor * dt;
        let pose = s.pose;
        while(budget > $fa1e7313cc9cf93a$var$EPS){
            const toCentre = $fa1e7313cc9cf93a$var$distanceToNextCentre(pose);
            const step = Math.min(budget, toCentre, (0, $733d112f9a19cd2b$export$50bbbfe3556a9553)(pose));
            const next = (0, $733d112f9a19cd2b$export$35d233f3b2dbf62a)(pose, step);
            if (!this.occupancy.reclaim(s.id, (0, $45485603a00f3ba6$export$3fe8347445b8cd97)(next))) {
                s.pose = pose;
                s.state = {
                    kind: "turning",
                    progress: 0
                };
                return;
            }
            pose = next;
            budget -= step;
            if (step < toCentre - $fa1e7313cc9cf93a$var$EPS) continue;
            pose = $fa1e7313cc9cf93a$var$snapToCentre(pose);
            if (state.remaining <= 0) {
                s.pose = pose;
                this.stop(s);
                return;
            }
        }
        s.pose = pose;
    }
    /**
   * End of a leg on a lane centre: produce a bug if one is due and the
   * population allows (`SetSpawningIfAmountOfBugsPermits`), otherwise just
   * pivot and walk on.
   */ stop(s) {
        if (s.nextSpawnIn > 0 || this.bugs.bugs.size >= this.level.maxBugs) {
            s.state = {
                kind: "turning",
                progress: 0
            };
            this.occupancy.reclaim(s.id, (0, $45485603a00f3ba6$export$a15fb836434290c4)(s.pose));
            return;
        }
        s.nextSpawnIn = this.drawSpawnDelay();
        s.state = {
            kind: "spawning",
            progress: 0
        };
        // The apple yields its tile to the bug growing out of it — they genuinely
        // stand on the same spot in the original, and the bug needs the claim to be
        // able to walk away. The apple takes a tile again when it moves off.
        this.occupancy.releaseAll(s.id);
        const kind = $fa1e7313cc9cf93a$export$954069c662db8484(this.rng, this.level.occurrenceWeights);
        // A roll into the gap above the weights spawns nothing; the apple still
        // stops and turns, exactly as the original does with a null bug.
        if (kind !== null) this.bugs.spawn(kind, s.pose);
    }
}



/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */ function $6427347c70638553$var$duration(model, clip) {
    return model.anims.get(clip)?.duration ?? 0;
}
/**
 * `time` is the wall clock, which only the looping bob needs — the one-shot
 * `create` clip is stretched over the sim's `SPAWN_ANIM_SECONDS` via the
 * state's own progress, so the sim keeps owning the timing.
 */ function $6427347c70638553$var$appearance(s, model, time) {
    const state = s.state;
    switch(state.kind){
        case "moving":
        case "turning":
            // The apple bobs while it walks and while it pivots — it is never still.
            return {
                clip: "idle",
                time: time,
                loop: true
            };
        case "spawning":
            return {
                clip: "create",
                time: state.progress * $6427347c70638553$var$duration(model, "create"),
                loop: false
            };
        default:
            {
                const unreachable = state;
                throw new Error(`unhandled spawner state ${JSON.stringify(unreachable)}`);
            }
    }
}
class $6427347c70638553$export$874673e2a3f118d0 {
    model;
    constructor(model){
        this.model = model;
    }
    draw(gl, renderer, spawners, cubeModel, viewProj, time) {
        for (const s of spawners.values()){
            const look = $6427347c70638553$var$appearance(s, this.model, time);
            renderer.draw(gl, this.model, (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(this.model, look.clip, look.time, look.loop), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, (0, $1886a5c91af384ee$export$ee41df9543d2bc3f)((0, $fa1e7313cc9cf93a$export$ef469229ac854fd0)(s), "apple", 1)), viewProj);
        }
    }
}


// Draws the power-up plants the simulation owns. Reads sim state, never
// mutates it (00-architecture.md §1); placement is the shared `placeOnSurface`,
// fed `powerUpFrame` (which handles a plant recessed into a crater), and the
// model comes from the slot's `POWERUP_DEFS` row.





/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */ function $9a84109e593b2195$var$duration(model, clip) {
    return model.anims.get(clip)?.duration ?? 0;
}
/**
 * `time` is the wall clock, which only the looping idle needs — the one-shot
 * clips are stretched over the sim state's own progress, so the sim keeps
 * owning the timing (`grow` clamped while growing, `pick`/`die` clamped on the
 * way out, exactly the spawner renderer's shape).
 */ function $9a84109e593b2195$var$appearance(p, model, time) {
    const state = p.state;
    switch(state.kind){
        case "growing":
            return {
                clip: "grow",
                time: state.progress * $9a84109e593b2195$var$duration(model, "grow"),
                loop: false
            };
        case "idle":
            return {
                clip: "idle",
                time: time,
                loop: true
            };
        case "taken":
            return {
                clip: "pick",
                time: state.progress * $9a84109e593b2195$var$duration(model, "pick"),
                loop: false
            };
        case "killed":
            return {
                clip: "die",
                time: state.progress * $9a84109e593b2195$var$duration(model, "die"),
                loop: false
            };
        default:
            {
                const unreachable = state;
                throw new Error(`unhandled power-up state ${JSON.stringify(unreachable)}`);
            }
    }
}
class $9a84109e593b2195$export$ba3b4e3a34e58a53 {
    models;
    constructor(models){
        this.models = models;
    }
    draw(gl, renderer, powerups, cubeModel, viewProj, time) {
        for (const p of powerups.values()){
            const name = (0, $0f82c75150dd11fd$export$8c930e56a7009e81)[p.slot].model;
            const model = this.models[name];
            const look = $9a84109e593b2195$var$appearance(p, model, time);
            renderer.draw(gl, model, (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(model, look.clip, look.time, look.loop), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, (0, $1886a5c91af384ee$export$ee41df9543d2bc3f)((0, $08dfdfd795a13c32$export$94b2b7bc7de2d125)(p), name, 1)), viewProj);
        }
    }
}



// The menu scene (plan 17): pages of yellow bubble-text planes anchored to
// cube faces, the BUGGS! logo above, drawn through the existing
// `ModelRenderer` with the alpha cutout. A page carries a yaw and each item a
// local transform; item world = cubeModel x rotationY(pageYaw) x itemLocal,
// the same `model` matrix everything else uses, so the pages ride every
// quarter-turn for free — exactly like the original's child transforms.
//
// Picking is shell-side screen space, not a sim ray: the active page projects
// each item's rest-pose AABB corners through the frame's own matrices, takes
// the 2D bounding rect, pads it, and a tap hit-tests rects in stack order.


// Screen-space projection shared by the `?debug=interaction` hooks and menu
// picking (plan 17 — the third consumer earned the extraction). Pure math
// over the same camera `frameCube` gives the renderer, so screen positions
// always agree with what the last frame drew.

function $3cc3165f22f27637$export$b230084cdcd5952a(point, model, rect) {
    const cam = (0, $238457a12dbae9c5$export$8b7b81ac271094c6)(rect.width / rect.height);
    const m = model;
    const x = m[0] * point[0] + m[4] * point[1] + m[8] * point[2] + m[12];
    const y = m[1] * point[0] + m[5] * point[1] + m[9] * point[2] + m[13];
    const z = m[2] * point[0] + m[6] * point[1] + m[10] * point[2] + m[14] - cam.dist;
    if (z >= -0.000001) return null;
    const f = 1 / Math.tan(cam.fovY / 2);
    return {
        x: rect.left + (f / cam.aspect * x / -z + 1) / 2 * rect.width,
        y: rect.top + (1 - f * y / -z) / 2 * rect.height
    };
}


// The game-start lifecycle (plan 17): boot -> menu <-> difficulty ->
// starting (fade) -> running. A plain state machine over injected callbacks —
// no DOM, no WebGL imports — so its transitions are unit-testable; the
// composition root (`main.ts`) supplies the real session builders, the cube
// rotation and the fade overlay. Ports `Cube.HandleUserInputInMenu`
// (`Cube.cs:2648`): tap plays the item's `move` clip, and the action fires
// only when the clip finishes.

const $04f247d236263f2f$export$7606087abd4824a1 = {
    main: [
        "start",
        "howto",
        "options",
        "highscore"
    ],
    difficulty: [
        "easy",
        "normal",
        "hard",
        "back"
    ]
};
const $04f247d236263f2f$export$33999363f60ed28 = 14 / 30;
function $04f247d236263f2f$export$5c6da6306ff122b7(item) {
    switch(item){
        case "easy":
            return (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[0];
        case "normal":
            return (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[1];
        case "hard":
            return (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[2];
        default:
            return null;
    }
}
const $04f247d236263f2f$export$b035402f400c69ee = -1;
const $04f247d236263f2f$export$5ff25486e8aea2b0 = 1;
class $04f247d236263f2f$export$794614fa392eb573 {
    deps;
    current;
    session;
    constructor(deps){
        this.deps = deps;
        this.current = {
            kind: "menu",
            page: "main",
            pressed: null
        };
        this.session = deps.buildMenuSession();
    }
    get state() {
        return this.current;
    }
    /** Whether swipes may start a cube turn. In menu states they are dropped. */ get swipesToSim() {
        return this.current.kind === "running";
    }
    /** Whether taps go to sim picking rather than menu items. */ get tapsToSim() {
        return this.current.kind === "running";
    }
    /**
   * The page whose items are tappable right now, or null while rotating,
   * pressed, starting or running — "nothing is tappable while rotating or
   * pressed", and only the active page ever hit-tests.
   */ get activePage() {
        if (this.current.kind !== "menu" || this.current.pressed !== null) return null;
        return this.current.page;
    }
    /** A tap that hit `item` on screen. Ignored unless it is active-page live. */ tapItem(item) {
        if (this.current.kind !== "menu" || this.current.pressed !== null) return;
        if (!$04f247d236263f2f$export$7606087abd4824a1[this.current.page].includes(item)) return;
        this.current = {
            ...this.current,
            pressed: {
                item: item,
                elapsed: 0
            }
        };
    }
    /** Advance the press clock; fires the pressed item's action on clip end. */ update(dt) {
        if (this.current.kind !== "menu" || this.current.pressed === null) return;
        this.current.pressed.elapsed += dt;
        if (this.current.pressed.elapsed < $04f247d236263f2f$export$33999363f60ed28) return;
        this.act(this.current.pressed.item);
    }
    act(item) {
        switch(item){
            case "start":
                this.current = {
                    kind: "rotating",
                    toPage: "difficulty"
                };
                this.deps.rotate($04f247d236263f2f$export$b035402f400c69ee, ()=>{
                    this.current = {
                        kind: "menu",
                        page: "difficulty",
                        pressed: null
                    };
                });
                return;
            case "back":
                this.current = {
                    kind: "rotating",
                    toPage: "main"
                };
                this.deps.rotate($04f247d236263f2f$export$5ff25486e8aea2b0, ()=>{
                    this.current = {
                        kind: "menu",
                        page: "main",
                        pressed: null
                    };
                });
                return;
            case "easy":
            case "normal":
            case "hard":
                {
                    const difficulty = $04f247d236263f2f$export$5c6da6306ff122b7(item);
                    if (difficulty === null) return; // unreachable; narrows the type
                    this.current = {
                        kind: "starting",
                        difficulty: difficulty
                    };
                    this.deps.fadeOut(()=>{
                        // The menu session (and everything it decayed into) dies here; the
                        // run is a disposable session object, never module state. Its
                        // `gameLost` will genuinely have fired on a long-idled menu — the
                        // shell subscribes to nothing, so it cannot leak into the lifecycle.
                        this.session.dispose();
                        this.deps.resetOrientation();
                        this.session = this.deps.buildGameSession(difficulty);
                        this.deps.fadeIn();
                        this.current = {
                            kind: "running"
                        };
                    });
                    return;
                }
            case "howto":
            case "options":
            case "highscore":
                // Placeholders: press animation only, back to the page (plan 17 §7).
                this.current = {
                    kind: "menu",
                    page: "main",
                    pressed: null
                };
                return;
            default:
                {
                    const unreachable = item;
                    throw new Error(`unhandled menu item ${JSON.stringify(unreachable)}`);
                }
        }
    }
}


const $5f961402d6b14b96$var$ITEM_MODEL = {
    start: "menuStart",
    howto: "menuHowto",
    options: "menuOptions",
    highscore: "menuHighscore",
    easy: "menuEasy",
    normal: "menuNormal",
    hard: "menuHard",
    back: "menuBack"
};
// Layout: the original scene's proportions, converted at (our half-cube
// 0.5) / (its 7.5). Items sit at y 2.5 / 0 / -2.5 / -5 there — stacked low,
// leaving the face's TOP for the logo. The cooked item quads are 13.65 x
// 1.96 cooked units in the model x-y plane; the cube face is 1.0 world
// unit, so ITEM_SCALE puts an item at ~0.88 of a face width. Everything
// hovers just in front of the face plane (0.53 vs its 7.55/7.5) so bugs
// walk behind the text.
const $5f961402d6b14b96$var$ITEM_SCALE = 0.065;
const $5f961402d6b14b96$var$ITEM_HOVER_Z = 0.505;
const $5f961402d6b14b96$var$ITEM_YS = [
    0.167,
    0,
    -0.167,
    -0.333
];
// The title copies live on the same hover plane as their page's items, at
// y = 6 there — OVERLAPPING the face's upper rows (drawn over the grass,
// being in front), fully inside the face outline. What keeps the four
// copies out of each other's way is geometry, and it constrains all three
// numbers here:
//   1. adjacent pages' planes are perpendicular, so quads intersect if a
//      half-width exceeds the hover distance (an earlier title z of 0.28 <
//      0.43 half-width cut a sliver through the visible title);
//   2. a neighbouring copy is hidden behind the cube's own side face only
//      while hover/face <= (camDist - halfWidth)/(camDist - face) — our
//      camera sits proportionally close (2.2 vs their 25.7/7.5), so the
//      old 0.53 hover (6% of the face, vs the original's 0.7%) let the
//      neighbour's near edge slide out past the right silhouette;
//   3. nothing may rise past the top edge (y + halfHeight <= 0.5), or the
//      neighbours' top strips peek over the cube (the y 0.62 attempt) —
//      which also bounds the original's own logo to halfHeight <= ~1.5,
//      smaller than the marketing artwork suggests.
const $5f961402d6b14b96$var$TITLE_SCALE = 0.7;
const $5f961402d6b14b96$var$TITLE_Y = 0.4;
const $5f961402d6b14b96$var$TITLE_Z = $5f961402d6b14b96$var$ITEM_HOVER_Z;
/** Fraction added to each side of an item's screen rect before hit-testing. */ const $5f961402d6b14b96$var$RECT_PAD = 0.1;
/**
 * The page yaws. `main` faces the resting camera (+Z); `difficulty` sits one
 * clockwise turn away on +X — `rotationY(pi/2)` maps +Z to +X, and the
 * shell's CLOCKWISE turn (`rotationY(-pi/2)`) cancels it exactly when the
 * face arrives, so the page reads upright with zero special cases.
 */ const $5f961402d6b14b96$var$PAGE_YAW = {
    main: 0,
    difficulty: Math.PI / 2
};
function $5f961402d6b14b96$export$3611764542a4f708(page) {
    return (0, $1669b27dd88d6d65$export$122a03c894ebb63)($5f961402d6b14b96$var$PAGE_YAW[page]);
}
/**
 * An item's face-local transform. The cooked quads read correctly from their
 * -z side (audited in the §1 pass), so a half-turn about Y faces the text at
 * the camera the page fronts.
 */ function $5f961402d6b14b96$var$itemLocal(index) {
    return (0, $1669b27dd88d6d65$export$2060d2db72cce88f)((0, $1669b27dd88d6d65$export$a984e488de492859)(0, $5f961402d6b14b96$var$ITEM_YS[index], $5f961402d6b14b96$var$ITEM_HOVER_Z), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)((0, $1669b27dd88d6d65$export$122a03c894ebb63)(Math.PI), (0, $1669b27dd88d6d65$export$89390739e80eec4a)($5f961402d6b14b96$var$ITEM_SCALE)));
}
const $5f961402d6b14b96$var$TITLE_LOCAL = (0, $1669b27dd88d6d65$export$2060d2db72cce88f)((0, $1669b27dd88d6d65$export$a984e488de492859)(0, $5f961402d6b14b96$var$TITLE_Y, $5f961402d6b14b96$var$TITLE_Z), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)((0, $1669b27dd88d6d65$export$122a03c894ebb63)(Math.PI), (0, $1669b27dd88d6d65$export$89390739e80eec4a)($5f961402d6b14b96$var$TITLE_SCALE)));
function $5f961402d6b14b96$export$fc84728af9a1eb89(page) {
    const pageM = $5f961402d6b14b96$export$3611764542a4f708(page);
    return (0, $04f247d236263f2f$export$7606087abd4824a1)[page].map((item, i)=>({
            item: item,
            world: (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(pageM, $5f961402d6b14b96$var$itemLocal(i))
        }));
}
function $5f961402d6b14b96$export$5c93a5fb57f91a5e(bounds, world, rect) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for(let corner = 0; corner < 8; corner++){
        const p = [
            (corner & 1) === 0 ? bounds.min[0] : bounds.max[0],
            (corner & 2) === 0 ? bounds.min[1] : bounds.max[1],
            (corner & 4) === 0 ? bounds.min[2] : bounds.max[2]
        ];
        const at = (0, $3cc3165f22f27637$export$b230084cdcd5952a)(p, world, rect);
        if (at === null) return null;
        if (at.x < minX) minX = at.x;
        if (at.y < minY) minY = at.y;
        if (at.x > maxX) maxX = at.x;
        if (at.y > maxY) maxY = at.y;
    }
    const padX = (maxX - minX) * $5f961402d6b14b96$var$RECT_PAD;
    const padY = (maxY - minY) * $5f961402d6b14b96$var$RECT_PAD;
    return {
        left: minX - padX,
        top: minY - padY,
        width: maxX - minX + 2 * padX,
        height: maxY - minY + 2 * padY
    };
}
function $5f961402d6b14b96$export$32174ac2df02c060(rects, x, y) {
    for (const r of rects){
        if (x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height) return r.item;
    }
    return null;
}
class $5f961402d6b14b96$export$98f0ff58f9536fe8 {
    models;
    constructor(models){
        this.models = models;
    }
    /**
   * The active page's tappable rects — the same rects `buggsDebug.menu()`
   * reports, so shot tools tap exactly what picking sees.
   */ itemRects(page, cubeModel, rect) {
        const out = [];
        for (const { item: item, world: world } of $5f961402d6b14b96$export$fc84728af9a1eb89(page)){
            const model = this.models[$5f961402d6b14b96$var$ITEM_MODEL[item]];
            const r = $5f961402d6b14b96$export$5c93a5fb57f91a5e(model.bounds, (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, world), rect);
            if (r !== null) out.push({
                item: item,
                ...r
            });
        }
        return out;
    }
    /**
   * Draw both pages, each with its own title copy. The inactive page rides
   * its own face — edge-on at rest (invisible), swinging through view during
   * a turn, exactly as the original's child transforms behave. The original
   * mounts an identical BUGGS! logo above *every* menu-bearing face of
   * `CubeGameObject` (`MainMenu.unity`: four `0_background_title` instances,
   * one per page), so a turn swings one logo out as the next page's swings
   * in and every page keeps a logo overhead. The pressed item plays its
   * `move` clip; everything else holds the rest pose.
   */ draw(gl, renderer, state, cubeModel, viewProj) {
        const pressed = state.kind === "menu" ? state.pressed : null;
        const title = this.models.menuTitle;
        for (const page of [
            "main",
            "difficulty"
        ]){
            for (const { item: item, world: world } of $5f961402d6b14b96$export$fc84728af9a1eb89(page)){
                const model = this.models[$5f961402d6b14b96$var$ITEM_MODEL[item]];
                const isPressed = pressed !== null && pressed.item === item;
                // A missing clip name rests the pose; "move" plays once while pressed.
                const worlds = isPressed ? (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(model, "move", pressed.elapsed, false) : (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(model, "", 0, false);
                renderer.draw(gl, model, worlds, (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, world), viewProj, true);
            }
            renderer.draw(gl, title, (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(title, "", 0, false), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(cubeModel, (0, $1669b27dd88d6d65$export$2060d2db72cce88f)($5f961402d6b14b96$export$3611764542a4f708(page), $5f961402d6b14b96$var$TITLE_LOCAL)), viewProj, true);
        }
    }
}



// Typed observer bus (00-architecture.md §4): the sim emits, the view
// subscribes. Emission is synchronous; game systems never subscribe.
// Type-only import: erased at build time, so the blocks <-> events cycle
// never exists at runtime.
class $1fa0c77519c053c5$export$5087227eb54526 {
    // The one place a loose type is acceptable: listeners of different events
    // have incompatible signatures, so the map is untyped inside and the
    // public API re-establishes the typing. Nothing loose leaks out.
    listeners = new Map();
    /** Subscribe to an event; returns an unsubscribe function. */ on(key, fn) {
        let set = this.listeners.get(key);
        if (!set) {
            set = new Set();
            this.listeners.set(key, set);
        }
        const entry = fn;
        set.add(entry);
        return ()=>{
            set.delete(entry);
        };
    }
    /** Notify every current listener, in subscription order. */ emit(key, e) {
        const set = this.listeners.get(key);
        if (!set) return;
        // Copy first so a listener unsubscribing during dispatch is safe.
        for (const fn of [
            ...set
        ])fn(e);
    }
}




// Block state model (Unity `BlocksModel.cs` / `Block.cs`, see
// `unity-overview.md` §3 "Blocks, infection, repair"). Pure logic: no WebGL,
// no DOM. The view subscribes to `blockChanged` to rebuild its mesh.

class $6c49afd34e8a4458$var$Blocks {
    events;
    size;
    states;
    /** Seconds left before an infected block turns to ghost; 0 when not infected. */ countdown;
    /** Which blocks are currently infected, so `update` costs those, not 125. */ infected;
    healthy;
    constructor(size, events){
        this.events = events;
        this.infected = new Set();
        this.size = size;
        const count = size * size * size;
        this.states = new Array(count).fill("healthy");
        this.countdown = new Float64Array(count);
        this.healthy = count;
    }
    state(x, y, z) {
        const i = this.index(x, y, z);
        return i < 0 ? "empty" : this.states[i];
    }
    decay(x, y, z) {
        const i = this.index(x, y, z);
        if (i < 0 || this.states[i] !== "infected") return 0;
        return 1 - this.countdown[i] / (0, $0f82c75150dd11fd$export$fb189d3247a5c5ae);
    }
    infect(x, y, z) {
        const i = this.index(x, y, z);
        // The original's guard: only a healthy block can be infected, so a second
        // bite on the same block does nothing at all (`BlocksModel.cs:494`).
        if (i < 0 || this.states[i] !== "healthy") return;
        this.countdown[i] = (0, $0f82c75150dd11fd$export$fb189d3247a5c5ae);
        this.infected.add(i);
        this.setState(i, x, y, z, "infected");
    }
    repair(x, y, z) {
        const i = this.index(x, y, z);
        if (i < 0) return false;
        this.countdown[i] = 0;
        this.infected.delete(i);
        if (this.states[i] === "healthy") return false;
        this.setState(i, x, y, z, "healthy");
        return true;
    }
    isSolid(x, y, z) {
        const s = this.state(x, y, z);
        return s === "healthy" || s === "infected";
    }
    isWalkable(x, y, z) {
        return this.state(x, y, z) !== "empty";
    }
    healthyFraction() {
        return this.healthy / this.states.length;
    }
    update(dt) {
        if (this.infected.size === 0) return;
        // Collect first: `setState` mutates `this.infected`, and deleting from a
        // Set while iterating it is legal but reads as a trap waiting to happen.
        const due = [];
        for (const i of this.infected){
            this.countdown[i] -= dt;
            if (this.countdown[i] <= 0) due.push(i);
        }
        for (const i of due){
            const n = this.size;
            const x = i % n;
            const y = Math.floor(i / n) % n;
            const z = Math.floor(i / (n * n));
            this.countdown[i] = 0;
            this.infected.delete(i);
            this.setState(i, x, y, z, "ghost");
        }
    }
    setState(i, x, y, z, state) {
        if (this.states[i] === state) return;
        // Mirrors `ChangeAmountOfHealthyBlocks`: the count drops the moment a block
        // stops being healthy, so infected->ghost does not decrement a second time.
        if (this.states[i] === "healthy") this.healthy--;
        else if (state === "healthy") this.healthy++;
        this.states[i] = state;
        this.events.emit("blockChanged", {
            x: x,
            y: y,
            z: z,
            state: state
        });
    }
    /** Linear index, or -1 when out of bounds. */ index(x, y, z) {
        const n = this.size;
        if (x < 0 || x >= n || y < 0 || y >= n || z < 0 || z >= n) return -1;
        return (z * n + y) * n + x;
    }
}
function $6c49afd34e8a4458$export$cf9c20dac39e2618(size, events) {
    return new $6c49afd34e8a4458$var$Blocks(size, events);
}





// Repair combos: a run of neighbouring blocks repaired in quick succession
// scores as one (Unity `BlockCombos.cs`, `unity-overview.md` §3 "Blocks,
// infection, repair" and §5 "Score multipliers"). Pure logic: no WebGL, no DOM
// (00-architecture.md §1).

/** True when the two cells share a face — one of the 6 axis directions. */ function $cbd9d8389cc96286$var$isNeighbour(a, b) {
    return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) === 1;
}
class $cbd9d8389cc96286$export$4b5867d53d99d7a3 {
    events;
    chain;
    lastTime;
    constructor(events){
        this.events = events;
        this.chain = [];
        this.lastTime = 0;
    }
    /** Record a repair that actually changed a block, at simulation time `simTime`. */ addRepair(cell, simTime) {
        const last = this.chain.length > 0 ? this.chain[this.chain.length - 1] : null;
        const extended = last !== null && $cbd9d8389cc96286$var$isNeighbour(last, cell) && simTime - this.lastTime <= (0, $0f82c75150dd11fd$export$fc3750dd29efecc0);
        if (extended) this.chain.push(cell);
        else {
            this.endChain();
            this.chain = [
                cell
            ];
        }
        this.lastTime = simTime;
    }
    /** End the chain, scoring it if it got long enough. */ endChain() {
        const cells = this.chain;
        this.chain = [];
        if (cells.length < 2) return;
        this.events.emit("combo", {
            cells: cells,
            length: cells.length
        });
        // Clamped rather than dropped: the table stops at a chain of 7, and a
        // longer one is worth at least as much as the longest listed.
        const index = Math.min(cells.length - 2, (0, $0f82c75150dd11fd$export$86e9e857dfb55419).length - 1);
        this.events.emit("scored", {
            points: (0, $0f82c75150dd11fd$export$86e9e857dfb55419)[index],
            multiplier: 1
        });
    }
}


// Player commands: what the view asks the simulation to do. Queued as the tap
// lands and drained at the top of the next fixed step (00-architecture.md §2,
// slot 1), so input never mutates sim state at an arbitrary point in the frame.
//
// Plain data: the picking that produces these lives in `render/picking.ts`,
// because only the view knows about cameras and canvases.
// Type-only imports: erased at build time, so no cycle exists at runtime.
class $30eaf70597607b9e$export$bc4d090da4c38c92 {
    queue = [];
    push(command) {
        this.queue.push(command);
    }
    /** Take everything queued so far, leaving the queue empty. */ drain() {
        const taken = this.queue;
        this.queue = [];
        return taken;
    }
}


// The difficulty ramp: a clock that walks the 30-row level table, and the two
// conditions that end a run. Ports `Cube.UpdateCurrentLevel` (`Cube.cs:3215`)
// and the level half of `BugsModel._cubeView_LevelChanged` (`:40-66`).
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

function $415de7aa0860edf8$export$9f51ceb98f8b3c65(def) {
    return {
        current: def,
        speedFactor: $415de7aa0860edf8$var$clampSpeedFactor(def.speedFactor)
    };
}
/** `BugsModel.cs:57`: a degenerate factor reads as 1 rather than freezing the game. */ function $415de7aa0860edf8$var$clampSpeedFactor(factor) {
    return factor <= (0, $0f82c75150dd11fd$export$d3f85a7f6ac0f49e) ? 1 : factor;
}
class $415de7aa0860edf8$export$e4643951de17b2d2 {
    events;
    levels;
    difficulty;
    currentLevel;
    wonAt;
    constructor(events, levels, // The difficulty crosses the menu -> game boundary as config (plan 17,
    // the original's static `GameConfiguration.DifficultyLevel`); it carries
    // the win level and a global speed multiplier. Defaulted to easy so every
    // pre-17 caller and test builds unchanged.
    difficulty = (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[0]){
        this.events = events;
        this.levels = levels;
        this.difficulty = difficulty;
        this.currentLevel = 0;
        this.wonAt = null;
    }
    /** 0 before the first level begins; 1..levels.length after. */ get level() {
        return this.currentLevel;
    }
    /** The row governing spawning right now — `levels[max(0, level - 1)]`. */ get current() {
        return this.levels[Math.max(0, this.currentLevel - 1)];
    }
    /**
   * The current row's `SpeedFactor` (with the original's degenerate guard)
   * times the difficulty's `GlobalSpeedMultiplier`. Composed here — the one
   * place every reader (`updateBug`'s `maxSpeed`, the spawners) already fans
   * out from — so `fixedLevel()` test fixtures stay difficulty-blind.
   */ get speedFactor() {
        return $415de7aa0860edf8$var$clampSpeedFactor(this.current.speedFactor) * this.difficulty.speedMultiplier;
    }
    /** Whether the player may act — the original's `_currentLevel < 1` gate. */ get acceptsInput() {
        return this.currentLevel >= 1;
    }
    /**
   * Advance to `time` seconds of elapsed simulation, emitting one
   * `levelChanged` per level crossed. It loops rather than stepping once: a
   * suspended tab that releases a large accumulator must not swallow levels.
   *
   * The comparison reads the row the clock is *waiting for* (`levels[level]`,
   * not `[level - 1]`), which is what makes level 0 work without a special
   * case. The last level never rolls over — the original gives it a synthetic
   * `+30` window and then wins (`Cube.cs:3222`); here winning is this method's
   * job and the guard is simply `level < levels.length`.
   */ update(time) {
        const whole = Math.floor(time);
        while(this.currentLevel < this.levels.length){
            if (whole <= this.levels[this.currentLevel].activeAfterSeconds) return;
            this.currentLevel++;
            // The win fires as the *winning* level's window expires — the original
            // checks `_currentLevel == gameWonAfterLevel` before incrementing
            // (`Cube.cs:3253`), so at difficulty 0 that is the crossing out of level
            // 10 at t = 301 s. Which level wins is the difficulty's `winAfterLevel`
            // (10/15/20 — plan 17). DIVERGE: the original `return`s into its win
            // sequence and stops advancing; this port's shell ignores the event in
            // `running` for now, so the clock keeps walking the table and the
            // remaining rows stay reachable.
            const winAfter = this.difficulty.winAfterLevel;
            if (this.currentLevel - 1 === winAfter && this.wonAt === null) {
                this.wonAt = winAfter;
                this.events.emit("gameWon", {
                    level: winAfter
                });
            }
            this.events.emit("levelChanged", {
                level: this.currentLevel,
                def: this.current
            });
        }
    }
}





const $1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9 = 1 / 60;
function $1dc1f21cb4eacbbb$export$bd9127779b59056d(seed, opts = {}) {
    const events = new (0, $1fa0c77519c053c5$export$5087227eb54526)();
    const rng = (0, $8c55fc03ff988801$export$d22a027dada4117a)(seed);
    const blocks = (0, $6c49afd34e8a4458$export$cf9c20dac39e2618)((0, $733d112f9a19cd2b$export$a07804094c3ecf2d), events);
    const levels = new (0, $415de7aa0860edf8$export$e4643951de17b2d2)(events, opts.levels ?? (0, $0f82c75150dd11fd$export$834c2ad6e999b78f), opts.difficulty ?? (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[0]);
    const modifiers = (0, $aae4ef88cc18edf2$export$1a5e2253a67e66c4)();
    const bugs = new (0, $267a74da153328b6$export$6535a4bc347cd13e)(events, blocks, rng, levels, modifiers);
    const powerups = new (0, $08dfdfd795a13c32$export$8ce0593c091cd29c)(events, rng, blocks, bugs, levels, modifiers);
    // Late-bound: the kill-half effect needs `bugs`, so the constructor order
    // cannot satisfy both directions (see `BugManager.powerups`).
    bugs.powerups = powerups;
    return {
        events: events,
        rng: rng,
        blocks: blocks,
        bugs: bugs,
        spawners: new (0, $fa1e7313cc9cf93a$export$41011d84e918e916)(events, rng, bugs, bugs.occupancy, levels, modifiers),
        powerups: powerups,
        modifiers: modifiers,
        combos: new (0, $cbd9d8389cc96286$export$4b5867d53d99d7a3)(events),
        commands: new (0, $30eaf70597607b9e$export$bc4d090da4c38c92)(),
        levels: levels,
        time: 0,
        lostEmitted: false
    };
}
/** How many random draws a relocation gets before it gives up for this step. */ const $1dc1f21cb4eacbbb$var$RELOCATE_ATTEMPTS = 32;
/**
 * A free lane centre for a newly added apple.
 *
 * APPROX: the original adds a spawn point by *relocating* one
 * (`RelocateSpawnPoint(null)`), which walks its own placement machinery; a
 * rejection-sampled tile centre drawn from the sim's seeded RNG is this port's
 * version. `null` when every draw landed on a claimed or non-walkable tile,
 * in which case the reconciliation simply retries at the next level.
 */ function $1dc1f21cb4eacbbb$var$freeLaneCentre(world) {
    for(let i = 0; i < $1dc1f21cb4eacbbb$var$RELOCATE_ATTEMPTS; i++){
        const pose = {
            face: (0, $733d112f9a19cd2b$export$6349a8ed6fe8449e)[Math.floor(world.rng() * (0, $733d112f9a19cd2b$export$6349a8ed6fe8449e).length)],
            u: Math.floor(world.rng() * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d)) + 0.5,
            v: Math.floor(world.rng() * (0, $733d112f9a19cd2b$export$a07804094c3ecf2d)) + 0.5,
            dir: Math.floor(world.rng() * 4)
        };
        const tiles = (0, $45485603a00f3ba6$export$a15fb836434290c4)(pose);
        if (tiles.some((key)=>world.bugs.occupancy.holder(key) !== null)) continue;
        return pose;
    }
    return null;
}
/**
 * Match the apple population to the new level's `AmountOfSpawnPoints` — the
 * spawn-point half of `BugsModel._cubeView_LevelChanged` (`:42-54`). Removal
 * takes the **oldest** (`RemoveSpawnPoint(_spawnPoints[0])`); the map preserves
 * insertion order, so its first key is that apple.
 *
 * The column is 2 for all 30 shipped levels, so this loop never runs in the
 * shipped game. It is ported anyway: a dead branch that only wakes when someone
 * edits the table should already work when they do.
 */ function $1dc1f21cb4eacbbb$var$reconcileSpawnPoints(world, def) {
    const spawners = world.spawners;
    while(spawners.spawners.size > def.spawnPoints){
        const oldest = spawners.spawners.keys().next();
        if (oldest.done === true) return;
        spawners.remove(oldest.value);
    }
    while(spawners.spawners.size < def.spawnPoints){
        const pose = $1dc1f21cb4eacbbb$var$freeLaneCentre(world);
        if (pose === null) return;
        spawners.add(pose);
    }
}
/**
 * One queued player command (slot 1 of the step order). A tap on a bug hurts
 * it; a repair is refused while any walker stands on the target block — the
 * original's `GetCubeWalkerBlockingPlaneRepair` gate in `BlocksModel.RepairBlock`,
 * which iterates power-ups too (`BugsModel.cs:301-313`) — and only a repair
 * that actually changed something feeds the combo chain, matching
 * `RepairBlock`'s "returns true if it was not already healthy".
 */ function $1dc1f21cb4eacbbb$var$applyCommand(world, command) {
    switch(command.kind){
        case "tapBug":
            world.bugs.hurt(command.id);
            return;
        case "repairBlock":
            {
                const cell = command.cell;
                const blockers = world.bugs.occupancy.holdersByCell(cell);
                const powerUp = world.powerups.at(cell);
                if (powerUp !== null) blockers.push(powerUp.id);
                if (blockers.length > 0) {
                    world.events.emit("repairBlocked", {
                        cell: cell,
                        blockers: blockers
                    });
                    return;
                }
                if (world.blocks.repair(cell[0], cell[1], cell[2])) world.combos.addRepair(cell, world.time);
                return;
            }
        case "tapPowerUp":
            world.powerups.take(command.id);
            return;
        default:
            {
                const unreachable = command;
                throw new Error(`unhandled command ${JSON.stringify(unreachable)}`);
            }
    }
}
function $1dc1f21cb4eacbbb$export$a0e9724da95b17d(world) {
    // Fixed system update order (00-architecture.md §2). Systems land here as
    // later plans implement them; the order is binding:
    //   0. the level clock
    //   1. queued player commands
    //   2. spawners
    //   3. power-ups
    //   4. bug movement
    //   5. collision
    //   6. infection
    //   7. block model reactions (and the lose check)
    //   8. event flush
    //
    // Slot 0 is first so a level change is in force for the whole step that
    // notices it — the weights a spawner rolls and the speed a walker carries
    // must not be half a step out of date. Slot 3 (plan 16) sits before the
    // walkers so a power-up is placed before they move: a bug stepping onto the
    // cell in the same step tramples it through the tile-entry hook rather than
    // ending up standing on one it never entered.
    const levelBefore = world.levels.level;
    world.levels.update(world.time);
    if (world.levels.level !== levelBefore) $1dc1f21cb4eacbbb$var$reconcileSpawnPoints(world, world.levels.current);
    // Commands queued during level 0 are **dropped**, not deferred — the original
    // returns early out of `HandleInput` (`Cube.cs:3190`) rather than buffering.
    // It is a one-second window; the point is that the rule lives in the sim.
    const queued = world.commands.drain();
    if (world.levels.acceptsInput) for (const command of queued)$1dc1f21cb4eacbbb$var$applyCommand(world, command); // slot 1
    world.spawners.update($1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9); // slot 2: spawners
    world.powerups.update($1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9); // slot 3: power-ups — placed before walkers move
    // Slots 4–6 are one pass: a walker's step, the tiles it may enter and the
    // bite it takes are the same state machine (`updateBug`), and splitting them
    // would mean walking the bug map three times to no benefit. Slot 7 needs no
    // pass of its own — `BlocksModel` reacts inside `infect()` and emits there.
    world.bugs.update($1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9); // slots 4-6: bug movement, collision, infection
    // Slot 7: the block model's own reaction — infected blocks decaying to ghost
    // on the `INFECTED_SECONDS` countdown (`BlocksModel.Update`). It runs after
    // the bugs so a block bitten this step gets its full stage.
    world.blocks.update($1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9);
    // Still slot 7: the lose check reads the block model after it has settled for
    // the step (`Cube.cs:2345`). It fires once and stops nothing — see `gameLost`.
    if (!world.lostEmitted) {
        const healthy = world.blocks.healthyFraction();
        if (healthy <= (0, $0f82c75150dd11fd$export$db95b8c10274b52f)) {
            world.lostEmitted = true;
            world.events.emit("gameLost", {
                healthyFraction: healthy
            });
        }
    }
    world.time += $1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9;
}
function $1dc1f21cb4eacbbb$export$bb93bf71f466a028(acc, step) {
    if (acc < step) return {
        steps: 0,
        rest: acc
    };
    const steps = Math.floor(acc / step);
    return {
        steps: steps,
        rest: acc - steps * step
    };
}


// The Web Audio wrapper (plan 18). Three layers so the parts that carry
// logic run in vitest without a browser:
//
//   AudioEngine        — mute flags + the two-channel jukebox command model,
//                        over an injected `AudioBackend` (pure bookkeeping)
//   createWebAudioBackend — the only Web Audio code: lazy AudioContext,
//                        autoplay unlock, fetch/decode cache, voices
//
// The command model is the original `JukeBox`'s: a play command on a channel
// replaces what that channel plays; channel 0 is the music, channel 1 the
// level-up stinger overlay. Two deliberate simplifications of its 0.1 s
// poll loop (DIVERGENCES.md): looping uses native `loop = true` instead of
// re-triggering on the next poll (the original's ~0.1 s gap per lap is an
// artefact), and `fadeOut` is a 0.7 s linear gain ramp instead of the
// −0.1-per-0.1 s staircase (0.7 s is what the staircase takes from the
// standard 0.7 music volume).
const $5c740b034d9a72cc$export$18b401d935b69ad8 = 0.7;
class $5c740b034d9a72cc$export$2e9600652eafa17 {
    backend;
    soundOn;
    musicOn;
    channels;
    constructor(backend){
        this.backend = backend;
        this.soundOn = true;
        this.musicOn = true;
        this.channels = new Map();
    }
    /** Forward a user gesture to the backend; starts any pending music. */ unlock() {
        this.backend.unlock(()=>this.startPendingChannels());
    }
    get locked() {
        return this.backend.locked;
    }
    /** One-shot SFX; skipped (not queued) while muted or locked. */ playSfx(url, delaySeconds = 0) {
        if (!this.soundOn || this.backend.locked) return;
        this.backend.playOnce(url, delaySeconds);
    }
    /** Issue a jukebox play command: replaces what the track's channel plays. */ playMusic(track) {
        const previous = this.channels.get(track.channel);
        previous?.handle?.stop();
        const channel = {
            track: track,
            handle: null
        };
        this.channels.set(track.channel, channel);
        this.startChannel(channel);
    }
    /** Fade the channel out and free it (`JukeBox.FadeOutJukeBox`). */ fadeOutMusic(channelNumber) {
        const channel = this.channels.get(channelNumber);
        if (!channel) return;
        channel.handle?.fadeOut($5c740b034d9a72cc$export$18b401d935b69ad8);
        this.channels.delete(channelNumber);
    }
    /** The command a channel currently holds — for tests and debugging. */ currentTrack(channelNumber) {
        return this.channels.get(channelNumber)?.track ?? null;
    }
    setSoundOn(on) {
        this.soundOn = on;
    }
    setMusicOn(on) {
        this.musicOn = on;
        if (on) {
            this.startPendingChannels();
            return;
        }
        for (const channel of this.channels.values()){
            channel.handle?.stop();
            channel.handle = null;
        }
    }
    startChannel(channel) {
        if (!this.musicOn || this.backend.locked) return;
        const { track: track } = channel;
        channel.handle = this.backend.startVoice(track.url, track.volume, track.loop, ()=>{
            // A one-shot that played out frees its channel, so a later unlock or
            // music-on toggle does not restart a finished track from the top.
            if (this.channels.get(track.channel) === channel) this.channels.delete(track.channel);
        });
    }
    startPendingChannels() {
        for (const channel of this.channels.values())if (channel.handle === null) this.startChannel(channel);
    }
}
function $5c740b034d9a72cc$export$240c5961a6b362f4() {
    let ctx = null;
    let sfxGain = null;
    let musicGain = null;
    let locked = true;
    const unlockedCallbacks = [];
    // url -> raw bytes promise (pre-context), then decoded buffer (or null =
    // failed, logged once).
    const bytes = new Map();
    const buffers = new Map();
    const fetchBytes = (url)=>{
        let pending = bytes.get(url);
        if (!pending) {
            pending = fetch(url).then((res)=>{
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.arrayBuffer();
            }).catch((err)=>{
                console.warn(`audio: failed to fetch ${url}:`, err);
                return null;
            });
            bytes.set(url, pending);
        }
        return pending;
    };
    const decode = (url)=>{
        let pending = buffers.get(url);
        if (!pending) {
            const context = ctx;
            if (!context) return Promise.resolve(null);
            pending = fetchBytes(url).then((raw)=>{
                if (raw === null) return null;
                // decodeAudioData detaches its input; copy so a retry stays possible.
                return context.decodeAudioData(raw.slice(0)).catch((err)=>{
                    console.warn(`audio: failed to decode ${url}:`, err);
                    return null;
                });
            });
            buffers.set(url, pending);
        }
        return pending;
    };
    return {
        get locked () {
            return locked;
        },
        warm (urls) {
            for (const url of urls)fetchBytes(url);
        },
        unlock (onUnlocked) {
            if (!locked) return;
            unlockedCallbacks.push(onUnlocked);
            if (!ctx) {
                ctx = new AudioContext();
                const master = ctx.createGain();
                master.connect(ctx.destination);
                sfxGain = ctx.createGain();
                sfxGain.connect(master);
                musicGain = ctx.createGain();
                musicGain.connect(master);
            }
            ctx.resume().then(()=>{
                if (!locked) return;
                locked = false;
                for (const cb of unlockedCallbacks.splice(0))cb();
            });
        },
        playOnce (url, delaySeconds) {
            const context = ctx;
            const out = sfxGain;
            if (!context || !out) return;
            const at = context.currentTime + delaySeconds;
            decode(url).then((buffer)=>{
                if (!buffer) return;
                const source = context.createBufferSource();
                source.buffer = buffer;
                source.connect(out);
                // A delay that decode time already ate plays immediately (start
                // clamps past times to "now"), which is the right degradation.
                source.start(at);
            });
        },
        startVoice (url, volume, loop, onEnded) {
            const context = ctx;
            const out = musicGain;
            if (!context || !out) return null;
            const gain = context.createGain();
            gain.gain.value = volume;
            gain.connect(out);
            let source = null;
            let cancelled = false;
            let fading = false;
            decode(url).then((buffer)=>{
                if (!buffer || cancelled) return;
                source = context.createBufferSource();
                source.buffer = buffer;
                source.loop = loop;
                source.connect(gain);
                source.onended = ()=>{
                    gain.disconnect();
                    if (!cancelled && !fading) onEnded();
                };
                source.start();
            });
            return {
                stop () {
                    cancelled = true;
                    source?.stop();
                    gain.disconnect();
                },
                fadeOut (seconds) {
                    if (cancelled || fading) return;
                    fading = true;
                    const now = context.currentTime;
                    gain.gain.setValueAtTime(gain.gain.value, now);
                    gain.gain.linearRampToValueAtTime(0, now + seconds);
                    if (source) source.stop(now + seconds);
                    else // Still decoding: nothing audible yet, just never start it.
                    cancelled = true;
                }
            };
        }
    };
}


// Maps game events to sounds (plan 18): subscribes to the `EventBus` and
// drives the engine — the audio half of what `Cube.cs` does view-side. The
// sim never changes: the director only reads (00-architecture.md §4), through
// the same narrow views the renderers use (it needs `BugKind` by id for the
// death sounds).
// The sound-effect banks and jukebox tracks, transcribed from the Unity
// scenes (plan 18). Pure data — no Web Audio, no DOM; the director maps
// events to banks and the engine turns URLs into sound.
//
// Sources of truth (GUIDs resolved via the .meta files, re-verified for this
// transcription rather than copied from the plan):
//   - `MainGame.unity:3413-3458` — the `SoundEffects` bank bindings
//   - `MainGame.unity:2334-2364` — the `JukeBox` clip/volume tables
//   - `MainGame.unity:3933-3960` — the seven `JukeBoxPlayCommand`s
//   - `MainMenu.unity:8552-8555` — the menu scene's boot-music command
//     (clip 5 = Summer.mp3, played once)
// The menu scene also binds a *smaller* SoundEffects copy (`MainMenu.unity:
// 8363-8400`): block sounds nulled, lady/fat death banks empty, and a
// FatBugHit whose GUID (7046c8b9...) resolves to no asset in the project — a
// dangling reference, so the menu's fat-bug-hit is silent there. The port
// uses the main game's bindings everywhere (one game, one binding); the
// menu-vs-game block-sound difference is reproduced by the director's
// in-game gate instead, mirroring the original's `gameStates == Running`
// guard around the block one-shots (`Cube.cs:1943`, `:1958`).
//
// Parcel resolves `new URL(<literal>, import.meta.url)` statically, so every
// URL is written out longhand — no helper, no loop (same constraint as
// `model-catalog.ts`). Every file referenced here is produced by
// `tools/extract_audio.py`, un-ignored in the root `.gitignore` and
// committed for CI (`tests/audio-catalog.test.ts` checks).
var $f0b9c7ba70f22794$exports = {};
$f0b9c7ba70f22794$exports = import.meta.resolve("8x0TG");


const $767c47259de57029$var$bugSpawn1 = new URL($f0b9c7ba70f22794$exports).href;
var $4007b95826ce0cd5$exports = {};
$4007b95826ce0cd5$exports = import.meta.resolve("lk3sK");


const $767c47259de57029$var$bugSpawn2 = new URL($4007b95826ce0cd5$exports).href;
var $42e078f5beb36374$exports = {};
$42e078f5beb36374$exports = import.meta.resolve("8WKor");


const $767c47259de57029$var$bugSpawn3 = new URL($42e078f5beb36374$exports).href;
var $d21e30df093727fd$exports = {};
$d21e30df093727fd$exports = import.meta.resolve("5DtEW");


const $767c47259de57029$var$bugDie1 = new URL($d21e30df093727fd$exports).href;
var $1dd5180a2447b76f$exports = {};
$1dd5180a2447b76f$exports = import.meta.resolve("2C1hR");


const $767c47259de57029$var$bugDie2 = new URL($1dd5180a2447b76f$exports).href;
var $91e9ef13009b7251$exports = {};
$91e9ef13009b7251$exports = import.meta.resolve("4dJ5q");


const $767c47259de57029$var$bugDie3 = new URL($91e9ef13009b7251$exports).href;
var $e02c8dad6df1f172$exports = {};
$e02c8dad6df1f172$exports = import.meta.resolve("eUFMW");


const $767c47259de57029$var$bugDie4 = new URL($e02c8dad6df1f172$exports).href;
var $f94afe8e9472ae09$exports = {};
$f94afe8e9472ae09$exports = import.meta.resolve("5rikN");


const $767c47259de57029$var$bugDie5 = new URL($f94afe8e9472ae09$exports).href;
var $9cbed38857bbe9a8$exports = {};
$9cbed38857bbe9a8$exports = import.meta.resolve("1A2CR");


const $767c47259de57029$var$bugDie6 = new URL($9cbed38857bbe9a8$exports).href;
var $4f43b7550383417d$exports = {};
$4f43b7550383417d$exports = import.meta.resolve("cLef3");


const $767c47259de57029$var$bugInfect6 = new URL($4f43b7550383417d$exports).href;
var $7b84a989f43ea105$exports = {};
$7b84a989f43ea105$exports = import.meta.resolve("gQD1q");


const $767c47259de57029$var$bugInfect7 = new URL($7b84a989f43ea105$exports).href;
var $e3a8a51b7e6cd03f$exports = {};
$e3a8a51b7e6cd03f$exports = import.meta.resolve("cBsWq");


const $767c47259de57029$var$blockDown = new URL($e3a8a51b7e6cd03f$exports).href;
var $b75aced19df8958b$exports = {};
$b75aced19df8958b$exports = import.meta.resolve("fQHFN");


const $767c47259de57029$var$blockUp = new URL($b75aced19df8958b$exports).href;
var $1db6bdef74f35211$exports = {};
$1db6bdef74f35211$exports = import.meta.resolve("l6p93");


const $767c47259de57029$var$cubeRotates1 = new URL($1db6bdef74f35211$exports).href;
var $ca2cd2e0504bdd51$exports = {};
$ca2cd2e0504bdd51$exports = import.meta.resolve("5TLIo");


const $767c47259de57029$var$cubeRotates2 = new URL($ca2cd2e0504bdd51$exports).href;
var $421e58d65f1418cf$exports = {};
$421e58d65f1418cf$exports = import.meta.resolve("pijZb");


const $767c47259de57029$var$cubeRotates3 = new URL($421e58d65f1418cf$exports).href;
var $29f2fc941372cdb0$exports = {};
$29f2fc941372cdb0$exports = import.meta.resolve("kGfv6");


const $767c47259de57029$var$powerUpSpawn = new URL($29f2fc941372cdb0$exports).href;
var $911477918c957fc5$exports = {};
$911477918c957fc5$exports = import.meta.resolve("ee2kK");


const $767c47259de57029$var$powerUpBugKill = new URL($911477918c957fc5$exports).href;
var $a1a9df6a89e3b59b$exports = {};
$a1a9df6a89e3b59b$exports = import.meta.resolve("4pAo4");


const $767c47259de57029$var$powerUpBugKill2 = new URL($a1a9df6a89e3b59b$exports).href;
var $b525da72738e3149$exports = {};
$b525da72738e3149$exports = import.meta.resolve("hydlk");


const $767c47259de57029$var$powerUpPickPlayer = new URL($b525da72738e3149$exports).href;
var $112e21514395c996$exports = {};
$112e21514395c996$exports = import.meta.resolve("4lDrl");


const $767c47259de57029$var$ladyBugPick = new URL($112e21514395c996$exports).href;
var $764a175419b1feb6$exports = {};
$764a175419b1feb6$exports = import.meta.resolve("jE4F2");


const $767c47259de57029$var$blackDespawn = new URL($764a175419b1feb6$exports).href;
var $ab2689c6b8851bd9$exports = {};
$ab2689c6b8851bd9$exports = import.meta.resolve("43eUu");


const $767c47259de57029$var$levelUp2 = new URL($ab2689c6b8851bd9$exports).href;
var $27ef0420ca8c3520$exports = {};
$27ef0420ca8c3520$exports = import.meta.resolve("ie7IP");


const $767c47259de57029$var$petolectroMenu = new URL($27ef0420ca8c3520$exports).href;
var $fcac7e509a21d3ce$exports = {};
$fcac7e509a21d3ce$exports = import.meta.resolve("kZ7pW");


const $767c47259de57029$var$summer = new URL($fcac7e509a21d3ce$exports).href;
var $7e624b279c722577$exports = {};
$7e624b279c722577$exports = import.meta.resolve("1D2Xa");


const $767c47259de57029$var$summerstreaming = new URL($7e624b279c722577$exports).href;
var $1904c5c28b7e112d$exports = {};
$1904c5c28b7e112d$exports = import.meta.resolve("cQ0sc");


const $767c47259de57029$var$gameOverShort = new URL($1904c5c28b7e112d$exports).href;
var $7e30174d8daa3650$exports = {};
$7e30174d8daa3650$exports = import.meta.resolve("51PL0");


const $767c47259de57029$var$balloon = new URL($7e30174d8daa3650$exports).href;
var $4a4496d5b9fb1b95$exports = {};
$4a4496d5b9fb1b95$exports = import.meta.resolve("kMQ4l");


const $767c47259de57029$var$success = new URL($4a4496d5b9fb1b95$exports).href;
const $767c47259de57029$export$f0ff92d80602608 = {
    BugSpawn: [
        $767c47259de57029$var$bugSpawn1,
        $767c47259de57029$var$bugSpawn2,
        $767c47259de57029$var$bugSpawn3
    ],
    BugDies: [
        $767c47259de57029$var$bugDie2,
        $767c47259de57029$var$bugDie3,
        $767c47259de57029$var$bugDie4,
        $767c47259de57029$var$bugDie5
    ],
    LadyBugTakenByPlayer: [
        $767c47259de57029$var$powerUpPickPlayer
    ],
    FatBugDies: [
        $767c47259de57029$var$bugDie1
    ],
    BugInfects: [
        $767c47259de57029$var$bugInfect7
    ],
    BlockBuildsDown: [
        $767c47259de57029$var$blockDown
    ],
    BlockBuildsUp: [
        $767c47259de57029$var$blockUp
    ],
    SubBlockBuildsDown: [],
    PowerUpSpawns: [
        $767c47259de57029$var$powerUpSpawn
    ],
    PowerUpKilledByBug: [
        $767c47259de57029$var$powerUpBugKill2,
        $767c47259de57029$var$powerUpBugKill
    ],
    PowerUpTakenByPlayer: [
        $767c47259de57029$var$powerUpPickPlayer
    ],
    CubeRotates: [
        $767c47259de57029$var$cubeRotates1,
        $767c47259de57029$var$cubeRotates2,
        $767c47259de57029$var$cubeRotates3
    ],
    FatBugHit: [
        $767c47259de57029$var$bugDie6
    ],
    SpawnPointDies: [],
    DiamondsPick: [
        $767c47259de57029$var$ladyBugPick
    ],
    BlackBuggSpawn: [
        $767c47259de57029$var$bugSpawn3
    ],
    BlackBuggPickedByPlayer: [
        $767c47259de57029$var$bugInfect6
    ],
    BlackBuggDisappearsByItself: [
        $767c47259de57029$var$blackDespawn
    ]
};
const $767c47259de57029$export$cb2ba035ac68bae7 = {
    menu: {
        name: "menu",
        url: $767c47259de57029$var$summer,
        volume: 0.7,
        loop: false,
        channel: 0
    },
    inGame: {
        name: "inGame",
        url: $767c47259de57029$var$balloon,
        volume: 0.7,
        loop: true,
        channel: 0
    },
    inGameSecondHalf: {
        name: "inGameSecondHalf",
        url: $767c47259de57029$var$summerstreaming,
        volume: 0.7,
        loop: true,
        channel: 0
    },
    levelUp: {
        name: "levelUp",
        url: $767c47259de57029$var$levelUp2,
        volume: 1,
        loop: false,
        channel: 1
    },
    gameOver: {
        name: "gameOver",
        url: $767c47259de57029$var$gameOverShort,
        volume: 1,
        loop: false,
        channel: 0
    },
    highScore: {
        name: "highScore",
        url: $767c47259de57029$var$petolectroMenu,
        volume: 0.7,
        loop: true,
        channel: 0
    },
    goldTrophy: {
        name: "goldTrophy",
        url: $767c47259de57029$var$success,
        volume: 1,
        loop: false,
        channel: 0
    },
    silverTrophy: {
        name: "silverTrophy",
        url: $767c47259de57029$var$gameOverShort,
        volume: 1,
        loop: false,
        channel: 0
    }
};
const $767c47259de57029$export$a4ec390ab1134e30 = [
    ...new Set([
        ...Object.values($767c47259de57029$export$f0ff92d80602608).flat(),
        ...Object.values($767c47259de57029$export$cb2ba035ac68bae7).map((t)=>t.url)
    ])
];


const $d0f636a8c8508f12$export$2380bba2c045831e = 0.25;
const $d0f636a8c8508f12$var$LOG_CAPACITY = 256;
function $d0f636a8c8508f12$var$clipName(url) {
    const last = url.split("/").pop() ?? url;
    return last.split("?")[0];
}
class $d0f636a8c8508f12$export$d19a239ee329dfc {
    sink;
    rng;
    now;
    log;
    unsubscribes;
    world;
    inGame;
    /** Crossing into this level switches to the second-half music. */ halfLevel;
    /** Set once `gameWon`/`gameLost` fired: music stops reacting (the original
   * freezes the whole game there; the port's sim keeps running until shell
   * part 2, so only the music side goes quiet — SFX keep narrating). */ ended;
    constructor(sink, // Same seeded-RNG family as the sim but a separate stream: sound choice
    // must not perturb gameplay draws (`main.ts` salts the seed).
    rng, now){
        this.sink = sink;
        this.rng = rng;
        this.now = now;
        this.log = [];
        this.unsubscribes = [];
        this.world = null;
        this.inGame = false;
        this.halfLevel = 0;
        this.ended = false;
    }
    /** The menu scene: Summer once, demo-game SFX, silent blocks. */ attachMenu(world) {
        this.attach(world, false);
        this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).menu);
    }
    /** A run at `difficulty`: Balloon_6 looped, everything audible. */ attachGame(world, difficulty) {
        this.attach(world, true);
        // C# integer division (`gameWonAfterLevel / 2`, `Cube.cs:3241`).
        this.halfLevel = Math.floor(difficulty.winAfterLevel / 2);
        this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).inGame);
    }
    /** Every quarter-turn start — game swipe or menu page turn (`Cube.cs:3387`,
   * `:3478-3536`; the menu binds the same bank). Called by `main.ts`'s one
   * turn-starting helper, which both paths feed. */ cubeRotateStarted() {
        this.playBank("CubeRotates");
    }
    audioLog() {
        return this.log;
    }
    attach(world, inGame) {
        for (const unsubscribe of this.unsubscribes.splice(0))unsubscribe();
        this.world = world;
        this.inGame = inGame;
        this.ended = false;
        const events = world.events;
        this.unsubscribes = [
            events.on("bugSpawned", ({ kind: kind })=>{
                this.playBank(kind === "black" ? "BlackBuggSpawn" : "BugSpawn", $d0f636a8c8508f12$export$2380bba2c045831e);
            }),
            events.on("bugStateChanged", ({ id: id, state: state })=>this.bugStateSound(id, state)),
            events.on("bugHit", ()=>this.playBank("FatBugHit")),
            events.on("blockChanged", ({ state: state })=>{
                // Only while a real run is on — the original wraps exactly these two
                // one-shots in `gameStates == Running` (`Cube.cs:1943`, `:1958`),
                // which is what keeps the menu's decaying demo cube quiet (its scene
                // nulls the block banks too). `ghost`/`empty` play nothing: the
                // eat-through bank (`SubBlockBuildsDown`) is empty in the scene.
                if (!this.inGame) return;
                if (state === "infected") this.playBank("BlockBuildsDown");
                else if (state === "healthy") this.playBank("BlockBuildsUp");
            }),
            events.on("combo", ({ length: length })=>{
                // Lengths 2-5, as `Cube.cs:2135`; every reachable index of the
                // original's `DiamondsPick` list is the same clip (see banks.ts).
                if (length >= 2 && length <= 5) this.playBank("DiamondsPick");
            }),
            events.on("powerUpSpawned", ()=>this.playBank("PowerUpSpawns")),
            events.on("powerUpStateChanged", ({ state: state })=>{
                if (state === "taken") this.playBank("PowerUpTakenByPlayer");
                else if (state === "killed") this.playBank("PowerUpKilledByBug");
            }),
            // `spawnerRemoved` and the apple cull play `SpawnPointDies` in the
            // original — an empty bank, silent by design; `repairBlocked` never
            // reaches a sound at all. Neither is subscribed.
            events.on("levelChanged", ({ level: level })=>{
                if (!this.inGame || this.ended) return;
                // The stinger overlays channel 1 at every crossing, including the
                // level-1 `LetsGo` and the final-level billboard (`Cube.cs:3275-3295`).
                this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).levelUp);
                if (level === this.halfLevel) this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).inGameSecondHalf);
            }),
            events.on("gameWon", ()=>{
                if (!this.inGame || this.ended) return;
                this.ended = true;
                // APPROX: the original picks gold/silver trophy music by comparing the
                // score to the online highscore (`Cube.cs:2311`); no highscore service
                // exists here, so a win always takes gold (SUCCESS.mp3).
                this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).goldTrophy);
            }),
            events.on("gameLost", ()=>{
                if (!this.inGame || this.ended) return;
                this.ended = true;
                // `ConductBugsGameOver` one-shots BlockBuildsDown on top of the
                // game-over music (`Cube.cs:2277`).
                this.playMusic((0, $767c47259de57029$export$cb2ba035ac68bae7).gameOver);
                this.playBank("BlockBuildsDown");
            })
        ];
    }
    /** The death/pickup switch of `_bugsModel_BugStateChanged` (`Cube.cs:1506`). */ bugStateSound(id, state) {
        if (state === "infecting") {
            this.playBank("BugInfects");
            return;
        }
        const kind = this.world?.bugs.bugs.get(id)?.kindDef ?? null;
        if (state === "dying") {
            // The black branch covers both its 20 s despawn and (for now) the
            // player tap — the tap-kill distinction arrives with the `TODO(black)`
            // instant game over, whose port-side state is still `dying`.
            if (kind === "fat") this.playBank("FatBugDies");
            else if (kind === "black") this.playBank("BlackBuggDisappearsByItself");
            else this.playBank("BugDies");
            return;
        }
        if (state === "takenByPlayer") {
            // Only the lady reaches this state today; the full original switch is
            // ported so the mapping stays right if that changes (`Cube.cs:1561-1577`).
            if (kind === "fat") this.playBank("FatBugDies");
            else if (kind === "black") this.playBank("BlackBuggPickedByPlayer");
            else if (kind === "lady") this.playBank("LadyBugTakenByPlayer");
            else this.playBank("BugDies");
        }
    }
    /** Uniform pick from a bank (`Cube.GetAnyOf`); empty banks are silent. */ playBank(bank, delaySeconds = 0) {
        const clips = (0, $767c47259de57029$export$f0ff92d80602608)[bank];
        if (clips.length === 0) return;
        const url = clips[Math.floor(this.rng() * clips.length)];
        this.record(bank, $d0f636a8c8508f12$var$clipName(url));
        this.sink.playSfx(url, delaySeconds);
    }
    playMusic(track) {
        this.record(`music:${track.name}`, $d0f636a8c8508f12$var$clipName(track.url));
        this.sink.playMusic(track);
    }
    /** Ring buffer of decisions — written even while the engine is locked. */ record(kind, clip) {
        this.log.push({
            t: this.now(),
            kind: kind,
            clip: clip
        });
        if (this.log.length > $d0f636a8c8508f12$var$LOG_CAPACITY) this.log.splice(0, this.log.length - $d0f636a8c8508f12$var$LOG_CAPACITY);
    }
}




// One run of the game as a disposable object (plan 17): the World, the cube
// meshes built from its blocks, and the dirty-subscription that rebuilds
// them. `main.ts`'s frame loop asks the *current* session for its world and
// meshes instead of closing over module globals — which is what lets the
// shell throw a run away (menu -> game now; game -> menu in the next shell
// plan) without a page reload.
//
// Layering note: `shell.ts` (the state machine) owns no GPU resources; this
// module is the one shell file that does, because a run's meshes die with the
// run — `dispose()` unsubscribes and frees them.

// Cube geometry, rebuilt from the block model: only faces exposed to air are
// emitted. Which atlas tile a face gets follows the cell's *onion depth* and
// the face's orientation within its shell (`render/onion.ts`) — grass on the
// crust, magma underneath — exactly as the original's per-zone block meshes
// bake it. Infection is a tint on top of that tile, not a tile of its own.
//
// Two meshes come out of here: the opaque cube, and the translucent ghost
// shells destroyed blocks leave behind. Both go through `emitFace`, so both sit
// on the same rounded box — a ghost built from raw axis-aligned quads pokes out
// past the cube's silhouette at every rounded edge, which is exactly what it
// looked like before this was shared.
// Onion zones: how deep a cell sits inside the cube, which way each of its
// faces points relative to the shell it belongs to, and therefore which atlas
// tile that face samples. Ports `CubeOnion.cs` + the prefab selection in
// `Cube.AddSubCube` (`Cube.cs:3689-3717`).
//
// Pure math: no GL, no DOM — `grid.ts` consumes it while building the mesh.
//
// **`PartType` (Corner/Edge/Middle) is deliberately not ported.** In the
// original it exists only to pick among three pre-baked meshes whose UVs wrap
// grass around one, two or three outward faces. This port builds geometry per
// exposed *face*, so the same result falls out of the per-face rule below — a
// corner cell simply has three outward faces and gets three grass tiles. That
// is why this module is a handful of lines rather than a port of
// `CubeOnion.Init`'s four shell loops and its 26-entry rotation table.
/** Which way an exposed face points relative to its cell's shell. */ const $0e557cd41ed89955$export$235cb65c20ad2b7 = {
    GrassTop: 0,
    GrassSide: 1,
    MagmaTop: 2,
    MagmaSide: 3,
    Stone: 4
};
/** Distance from either end of one axis, in cells. */ function $0e557cd41ed89955$var$axisDepth(c, n) {
    return Math.min(c, n - 1 - c);
}
/**
 * Which way is "out" along one axis: -1 for a cell in the low half, +1 for the
 * high half, 0 for a cell sitting exactly on the middle of an odd-sized cube —
 * that axis then has no outward direction at all.
 */ function $0e557cd41ed89955$var$outwardSign(c, n) {
    const high = n - 1 - c;
    if (c < high) return -1;
    if (c > high) return 1;
    return 0;
}
function $0e557cd41ed89955$export$d106d802c349fa0e(x, y, z, n) {
    return Math.min($0e557cd41ed89955$var$axisDepth(x, n), $0e557cd41ed89955$var$axisDepth(y, n), $0e557cd41ed89955$var$axisDepth(z, n));
}
function $0e557cd41ed89955$export$2fed5d47bfeb6d49(x, y, z, n, normal) {
    const axis = normal[0] !== 0 ? 0 : normal[1] !== 0 ? 1 : 2;
    const c = axis === 0 ? x : axis === 1 ? y : z;
    // An axis the cell is not at the bound of cannot carry the shell's outward
    // face: the block's "top" points along the shallowest axis, not this one.
    if ($0e557cd41ed89955$var$axisDepth(c, n) !== $0e557cd41ed89955$export$d106d802c349fa0e(x, y, z, n)) return "side";
    const out = $0e557cd41ed89955$var$outwardSign(c, n);
    if (out === 0) return "side";
    return normal[axis] === out ? "outward" : "inward";
}
function $0e557cd41ed89955$export$955f9e56ed2fff6b(depth, role) {
    if (depth >= 2) return $0e557cd41ed89955$export$235cb65c20ad2b7.MagmaTop;
    if (role === "inward") return $0e557cd41ed89955$export$235cb65c20ad2b7.Stone;
    if (depth === 0) return role === "outward" ? $0e557cd41ed89955$export$235cb65c20ad2b7.GrassTop : $0e557cd41ed89955$export$235cb65c20ad2b7.GrassSide;
    return role === "outward" ? $0e557cd41ed89955$export$235cb65c20ad2b7.MagmaTop : $0e557cd41ed89955$export$235cb65c20ad2b7.MagmaSide;
}


const $1b9c19121388f259$export$bc90872e265d166e = 5;
// Per-direction face template: outward normal + the 4 cell-corner offsets
// (counter-clockwise seen from outside), matching UV order (0,0)(1,0)(1,1)(0,1).
const $1b9c19121388f259$var$FACES = [
    {
        n: [
            0,
            0,
            1
        ],
        corners: [
            [
                0,
                0,
                1
            ],
            [
                1,
                0,
                1
            ],
            [
                1,
                1,
                1
            ],
            [
                0,
                1,
                1
            ]
        ]
    },
    {
        n: [
            0,
            0,
            -1
        ],
        corners: [
            [
                1,
                0,
                0
            ],
            [
                0,
                0,
                0
            ],
            [
                0,
                1,
                0
            ],
            [
                1,
                1,
                0
            ]
        ]
    },
    {
        n: [
            1,
            0,
            0
        ],
        corners: [
            [
                1,
                0,
                1
            ],
            [
                1,
                0,
                0
            ],
            [
                1,
                1,
                0
            ],
            [
                1,
                1,
                1
            ]
        ]
    },
    {
        n: [
            -1,
            0,
            0
        ],
        corners: [
            [
                0,
                0,
                0
            ],
            [
                0,
                0,
                1
            ],
            [
                0,
                1,
                1
            ],
            [
                0,
                1,
                0
            ]
        ]
    },
    {
        n: [
            0,
            1,
            0
        ],
        corners: [
            [
                0,
                1,
                1
            ],
            [
                1,
                1,
                1
            ],
            [
                1,
                1,
                0
            ],
            [
                0,
                1,
                0
            ]
        ]
    },
    {
        n: [
            0,
            -1,
            0
        ],
        corners: [
            [
                0,
                0,
                0
            ],
            [
                1,
                0,
                0
            ],
            [
                1,
                0,
                1
            ],
            [
                0,
                0,
                1
            ]
        ]
    }
];
// Faces are subdivided so vertices near the cube's outer edges can bend
// around the rounding; SUBDIV sub-quads per edge keeps the curve smooth.
const $1b9c19121388f259$var$SUBDIV = 4;
// Rounding radius of the outer edges, as a fraction of one cell.
// Measured off a production screenshot: corner arc ~56px at ~112px per block.
const $1b9c19121388f259$var$EDGE_RADIUS_CELLS = 0.5;
function $1b9c19121388f259$var$clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
function $1b9c19121388f259$var$builder() {
    return {
        positions: [],
        normals: [],
        uvs: [],
        materials: [],
        tints: [],
        indices: []
    };
}
function $1b9c19121388f259$var$finish(b) {
    return {
        positions: new Float32Array(b.positions),
        normals: new Float32Array(b.normals),
        uvs: new Float32Array(b.uvs),
        materials: new Float32Array(b.materials),
        tints: new Float32Array(b.tints),
        indices: new Uint16Array(b.indices)
    };
}
function $1b9c19121388f259$var$frameFor(size, n) {
    const cell = size / n;
    const radius = cell * $1b9c19121388f259$var$EDGE_RADIUS_CELLS;
    return {
        cell: cell,
        origin: -size / 2,
        radius: radius,
        inner: size / 2 - radius
    };
}
/**
 * Emit one subdivided, rounded face of the cell at `(x, y, z)`.
 *
 * Vertices are clamped onto a rounded box: anything further than `radius` from
 * the inner box is pulled back onto its surface. `onHull` says whether the face
 * lies on the cube's outer skin — only those get the radial normal that shades
 * the rounding smoothly; interior walls that merely graze an edge keep their
 * flat normal.
 */ function $1b9c19121388f259$var$emitFace(b, f, face, x, y, z, onHull, material, tint) {
    const [nx, ny, nz] = face.n;
    const c = face.corners.map(([cx, cy, cz])=>[
            f.origin + (x + cx) * f.cell,
            f.origin + (y + cy) * f.cell,
            f.origin + (z + cz) * f.cell
        ]);
    const base = b.positions.length / 3;
    for(let j = 0; j <= $1b9c19121388f259$var$SUBDIV; j++){
        const v = j / $1b9c19121388f259$var$SUBDIV;
        for(let i = 0; i <= $1b9c19121388f259$var$SUBDIV; i++){
            const u = i / $1b9c19121388f259$var$SUBDIV;
            const w0 = (1 - u) * (1 - v);
            const w1 = u * (1 - v);
            const w2 = u * v;
            const w3 = (1 - u) * v;
            let px = w0 * c[0][0] + w1 * c[1][0] + w2 * c[2][0] + w3 * c[3][0];
            let py = w0 * c[0][1] + w1 * c[1][1] + w2 * c[2][1] + w3 * c[3][1];
            let pz = w0 * c[0][2] + w1 * c[1][2] + w2 * c[2][2] + w3 * c[3][2];
            const dx = px - $1b9c19121388f259$var$clamp(px, -f.inner, f.inner);
            const dy = py - $1b9c19121388f259$var$clamp(py, -f.inner, f.inner);
            const dz = pz - $1b9c19121388f259$var$clamp(pz, -f.inner, f.inner);
            const len = Math.hypot(dx, dy, dz);
            let vnx = nx;
            let vny = ny;
            let vnz = nz;
            if (len > f.radius) {
                const s = f.radius / len;
                px += dx * (s - 1);
                py += dy * (s - 1);
                pz += dz * (s - 1);
                if (onHull) {
                    vnx = dx / len;
                    vny = dy / len;
                    vnz = dz / len;
                }
            }
            b.positions.push(px, py, pz);
            b.normals.push(vnx, vny, vnz);
            b.uvs.push(u, v);
            b.materials.push(material);
            b.tints.push(tint);
        }
    }
    for(let j = 0; j < $1b9c19121388f259$var$SUBDIV; j++)for(let i = 0; i < $1b9c19121388f259$var$SUBDIV; i++){
        const a = base + j * ($1b9c19121388f259$var$SUBDIV + 1) + i;
        const bb = a + 1;
        const d = a + $1b9c19121388f259$var$SUBDIV + 2;
        const e = a + $1b9c19121388f259$var$SUBDIV + 1;
        b.indices.push(a, bb, d, a, d, e);
    }
}
/** Whether a neighbour cell lies outside the grid — i.e. the face is on the skin. */ function $1b9c19121388f259$var$outOfBounds(ax, ay, az, n) {
    return ax < 0 || ax >= n || ay < 0 || ay >= n || az < 0 || az >= n;
}
function $1b9c19121388f259$export$401f269a66ef9e4b(grid, size = 1) {
    const n = grid.size;
    const f = $1b9c19121388f259$var$frameFor(size, n);
    const b = $1b9c19121388f259$var$builder();
    for(let z = 0; z < n; z++){
        for(let y = 0; y < n; y++)for(let x = 0; x < n; x++){
            if (grid.state(x, y, z) !== "ghost") continue;
            for (const face of $1b9c19121388f259$var$FACES){
                const [nx, ny, nz] = face.n;
                const ax = x + nx;
                const ay = y + ny;
                const az = z + nz;
                if (grid.isSolid(ax, ay, az)) continue;
                $1b9c19121388f259$var$emitFace(b, f, face, x, y, z, $1b9c19121388f259$var$outOfBounds(ax, ay, az, n), 0, 0);
            }
        }
    }
    return $1b9c19121388f259$var$finish(b);
}
function $1b9c19121388f259$export$655eb29cb0ebd81a(grid, size = 1) {
    const n = grid.size;
    const f = $1b9c19121388f259$var$frameFor(size, n);
    const b = $1b9c19121388f259$var$builder();
    for(let z = 0; z < n; z++){
        for(let y = 0; y < n; y++)for(let x = 0; x < n; x++){
            if (!grid.isSolid(x, y, z)) continue;
            for (const face of $1b9c19121388f259$var$FACES){
                const [nx, ny, nz] = face.n;
                const ax = x + nx;
                const ay = y + ny;
                const az = z + nz;
                if (grid.isSolid(ax, ay, az)) continue; // hidden interior face
                // Tile follows depth + orientation, so a face uncovered by eating
                // shows the layer beneath rather than generic stone.
                const material = (0, $0e557cd41ed89955$export$955f9e56ed2fff6b)((0, $0e557cd41ed89955$export$d106d802c349fa0e)(x, y, z, n), (0, $0e557cd41ed89955$export$2fed5d47bfeb6d49)(x, y, z, n, face.n));
                const tint = grid.state(x, y, z) === "infected" ? 1 : 0;
                $1b9c19121388f259$var$emitFace(b, f, face, x, y, z, $1b9c19121388f259$var$outOfBounds(ax, ay, az, n), material, tint);
            }
        }
    }
    return $1b9c19121388f259$var$finish(b);
}



function $feb973a4c3cee1a6$export$f7c62c73429afa28(gl, seed, opts = {}) {
    const world = (0, $1dc1f21cb4eacbbb$export$bd9127779b59056d)(seed, opts);
    let cube = (0, $1659abb3fbe87a4d$export$d959e36e59c59ccb)(gl, (0, $1b9c19121388f259$export$655eb29cb0ebd81a)(world.blocks));
    let ghosts = (0, $1659abb3fbe87a4d$export$d959e36e59c59ccb)(gl, (0, $1b9c19121388f259$export$401f269a66ef9e4b)(world.blocks));
    // A state change only marks dirty, so a burst of changes costs one rebuild
    // in the next frame (same policy main.ts had when it owned the meshes).
    let dirty = false;
    const unsubscribe = world.events.on("blockChanged", ()=>{
        dirty = true;
    });
    return {
        world: world,
        get cube () {
            return cube;
        },
        get ghosts () {
            return ghosts;
        },
        rebuildIfDirty () {
            if (!dirty) return;
            cube.dispose();
            ghosts.dispose();
            cube = (0, $1659abb3fbe87a4d$export$d959e36e59c59ccb)(gl, (0, $1b9c19121388f259$export$655eb29cb0ebd81a)(world.blocks));
            ghosts = (0, $1659abb3fbe87a4d$export$d959e36e59c59ccb)(gl, (0, $1b9c19121388f259$export$401f269a66ef9e4b)(world.blocks));
            dirty = false;
        },
        dispose () {
            unsubscribe();
            cube.dispose();
            ghosts.dispose();
        }
    };
}








const $ad2bcec7a0192558$var$canvas = document.getElementById("gl");
const $ad2bcec7a0192558$var$fadeEl = document.getElementById("fade");
const $ad2bcec7a0192558$var$gl = (0, $1659abb3fbe87a4d$export$fd42f52fd3ae1109)($ad2bcec7a0192558$var$canvas);
const $ad2bcec7a0192558$var$program = (0, $1659abb3fbe87a4d$export$327d24a04cd0dc17)($ad2bcec7a0192558$var$gl, (0, $545dedaa33bafe10$export$e8c2250f9a0c6e24), (0, $545dedaa33bafe10$export$e5cd4f3fc4aedb53));
const $ad2bcec7a0192558$var$uMvp = $ad2bcec7a0192558$var$gl.getUniformLocation($ad2bcec7a0192558$var$program, "uMvp");
const $ad2bcec7a0192558$var$uModel = $ad2bcec7a0192558$var$gl.getUniformLocation($ad2bcec7a0192558$var$program, "uModel");
$ad2bcec7a0192558$var$gl.useProgram($ad2bcec7a0192558$var$program);
$ad2bcec7a0192558$var$gl.uniform1i($ad2bcec7a0192558$var$gl.getUniformLocation($ad2bcec7a0192558$var$program, "uAtlas"), 0);
// Same vertex shader and attribute layout, untextured translucent fragment.
const $ad2bcec7a0192558$var$ghostProgram = (0, $1659abb3fbe87a4d$export$327d24a04cd0dc17)($ad2bcec7a0192558$var$gl, (0, $545dedaa33bafe10$export$e8c2250f9a0c6e24), (0, $545dedaa33bafe10$export$bdbe9d546ec08ec1));
const $ad2bcec7a0192558$var$uGhostMvp = $ad2bcec7a0192558$var$gl.getUniformLocation($ad2bcec7a0192558$var$ghostProgram, "uMvp");
const $ad2bcec7a0192558$var$uGhostModel = $ad2bcec7a0192558$var$gl.getUniformLocation($ad2bcec7a0192558$var$ghostProgram, "uModel");
// Original block texture atlas; a flat green placeholder shows until it loads.
const $ad2bcec7a0192558$var$atlasTex = $ad2bcec7a0192558$var$gl.createTexture();
if (!$ad2bcec7a0192558$var$atlasTex) throw new Error("Failed to create texture");
$ad2bcec7a0192558$var$gl.bindTexture($ad2bcec7a0192558$var$gl.TEXTURE_2D, $ad2bcec7a0192558$var$atlasTex);
$ad2bcec7a0192558$var$gl.texImage2D($ad2bcec7a0192558$var$gl.TEXTURE_2D, 0, $ad2bcec7a0192558$var$gl.RGBA, 1, 1, 0, $ad2bcec7a0192558$var$gl.RGBA, $ad2bcec7a0192558$var$gl.UNSIGNED_BYTE, new Uint8Array([
    70,
    120,
    45,
    255
]));
$ad2bcec7a0192558$var$gl.texParameteri($ad2bcec7a0192558$var$gl.TEXTURE_2D, $ad2bcec7a0192558$var$gl.TEXTURE_MIN_FILTER, $ad2bcec7a0192558$var$gl.LINEAR);
var $c1fd13b6fa0c4fa7$exports = {};
$c1fd13b6fa0c4fa7$exports = import.meta.resolve("42716");


(0, $0688557bb19ac86e$export$19de0130f5adf0ae)($ad2bcec7a0192558$var$gl, new URL($c1fd13b6fa0c4fa7$exports), $ad2bcec7a0192558$var$atlasTex).catch((err)=>console.error("failed to load block atlas:", err));
const $ad2bcec7a0192558$var$input = new (0, $8efa37a468313d04$export$19de138317f754cf)($ad2bcec7a0192558$var$canvas);
// Audio (plan 18): the engine owns Web Audio, the director subscribes to the
// current session's event bus. The AudioContext is created lazily on the
// first genuine gesture (browser autoplay policy) — until then every play
// call is a cheap no-op that still lands in the director's log, which is what
// keeps the headless `?debug=` fleet silent-safe. The seed is salted so the
// director's clip picks draw from their own stream and can never perturb the
// sim's (the plan's `0xaud10` is not valid hex; `0xa0d10` is the closest
// legal spelling).
const $ad2bcec7a0192558$var$audioBackend = (0, $5c740b034d9a72cc$export$240c5961a6b362f4)();
const $ad2bcec7a0192558$var$audio = new (0, $5c740b034d9a72cc$export$2e9600652eafa17)($ad2bcec7a0192558$var$audioBackend);
$ad2bcec7a0192558$var$audioBackend.warm((0, $767c47259de57029$export$a4ec390ab1134e30));
const $ad2bcec7a0192558$var$director = new (0, $d0f636a8c8508f12$export$d19a239ee329dfc)($ad2bcec7a0192558$var$audio, (0, $8c55fc03ff988801$export$d22a027dada4117a)(Date.now() >>> 0 ^ 0xa0d10), ()=>performance.now() / 1000);
$ad2bcec7a0192558$var$canvas.addEventListener("pointerdown", ()=>$ad2bcec7a0192558$var$audio.unlock());
window.addEventListener("keydown", ()=>$ad2bcec7a0192558$var$audio.unlock());
// The whole model roster, cooked from the original FBXs by tools/cook_models.sh;
// run the cook + texture scripts if these assets are missing. One loading gate:
// nothing draws models until every one of them is on the GPU.
const $ad2bcec7a0192558$var$modelRenderer = new (0, $0688557bb19ac86e$export$b51677472c947333)($ad2bcec7a0192558$var$gl);
let $ad2bcec7a0192558$var$models = null;
let $ad2bcec7a0192558$var$bugRenderer = null;
let $ad2bcec7a0192558$var$spawnerRenderer = null;
let $ad2bcec7a0192558$var$powerUpRenderer = null;
let $ad2bcec7a0192558$var$menuRenderer = null;
(0, $25b4b9b6aebf4516$export$1702f9080daf04fe)($ad2bcec7a0192558$var$gl).then((loaded)=>{
    $ad2bcec7a0192558$var$models = loaded;
    $ad2bcec7a0192558$var$bugRenderer = new (0, $1886a5c91af384ee$export$af96eb7f175bb266)(loaded);
    $ad2bcec7a0192558$var$spawnerRenderer = new (0, $6427347c70638553$export$874673e2a3f118d0)(loaded.apple);
    $ad2bcec7a0192558$var$powerUpRenderer = new (0, $9a84109e593b2195$export$ba3b4e3a34e58a53)(loaded);
    $ad2bcec7a0192558$var$menuRenderer = new (0, $5f961402d6b14b96$export$98f0ff58f9536fe8)(loaded);
}, (err)=>console.error("failed to load models:", err));
// Boot routing (plan 17): the default page boots to the MENU. Every `?debug=`
// scenario skips it and lands straight in a running world (easy difficulty,
// main table) so the shot fleet keeps working — except `?debug=menu`, which
// boots the menu *with* instrumentation (`buggsDebug.menu()`).
const $ad2bcec7a0192558$var$debug = new URLSearchParams(location.search).get("debug");
// `?debug=roster` swaps the demo population for one bug of every kind, spread
// over the front and top faces, plus a standing apple — the visual check that
// each kind draws with its own model (tools/shot-roster.mjs).
const $ad2bcec7a0192558$var$roster = $ad2bcec7a0192558$var$debug === "roster";
// `?debug=collision` crowds six bugs onto the front face on courses that cross,
// the visual check that they deflect rather than overlap (tools/shot-collision.mjs).
const $ad2bcec7a0192558$var$collision = $ad2bcec7a0192558$var$debug === "collision";
// `?debug=behaviours` is plan 14's check: four kinds on four parallel lanes of
// the front face, each doing its own thing to the cube as it walks
// (tools/shot-behaviours.mjs).
const $ad2bcec7a0192558$var$behaviours = $ad2bcec7a0192558$var$debug === "behaviours";
// `?debug=powerups` is plan 16 §1's check: all ten plants standing on the front
// face in slot order, view-only props like the roster apple — the visual audit
// of each model's skin, orientation, lift and idle cycle before any power-up
// logic exists (tools/shot-powerups.mjs).
const $ad2bcec7a0192558$var$powerups = $ad2bcec7a0192558$var$debug === "powerups";
// Level 1's two apples (`AmountOfSpawnPoints = 2`), on two different faces so
// the wandering is visible from the resting camera and the population spreads
// around the cube. Face ids are canonical (00-architecture.md §7).
const $ad2bcec7a0192558$var$APPLE_SPAWNS = [
    {
        face: 4,
        u: 1.5,
        v: 3.5,
        dir: 3
    },
    {
        face: 2,
        u: 3.5,
        v: 2.5,
        dir: 3
    }
];
// All seven kinds walking, in two rows across the front face — the resting
// camera is face-on, so anything on another face is out of frame and could not
// be checked. They all walk down (dir 3) at their own speeds, which keeps the
// rows from piling into each other; plan 05 brings collision, until then they
// would simply pass through each other anyway.
const $ad2bcec7a0192558$var$ROSTER_SPAWNS = [
    [
        "standard",
        {
            face: 4,
            u: 0.6,
            v: 4.4,
            dir: 3
        }
    ],
    [
        "lady",
        {
            face: 4,
            u: 2,
            v: 4.4,
            dir: 3
        }
    ],
    [
        "fat",
        {
            face: 4,
            u: 3.4,
            v: 4.4,
            dir: 3
        }
    ],
    [
        "speed",
        {
            face: 4,
            u: 4.6,
            v: 4.4,
            dir: 3
        }
    ],
    [
        "spy",
        {
            face: 4,
            u: 1,
            v: 3,
            dir: 3
        }
    ],
    [
        "spike",
        {
            face: 4,
            u: 2.6,
            v: 3,
            dir: 3
        }
    ],
    [
        "black",
        {
            face: 4,
            u: 4.2,
            v: 3,
            dir: 3
        }
    ]
];
// View-only prop: no spawner logic yet (plan 06), just the apple model standing
// on the front face so the roster shot proves it loads and sits upright.
const $ad2bcec7a0192558$var$ROSTER_APPLE = {
    face: 4,
    u: 2.2,
    v: 0.9,
    dir: 3
};
// The ten plants in slot order, mushrooms (slots 0-4) across the upper row and
// flowers (slots 5-9) across the lower — view-only props like the roster
// apple; the sim has no power-up system yet (plan 16 §1 is assets only). The
// resting camera is face-on, so only the front face can be audited.
const $ad2bcec7a0192558$var$POWERUP_SPAWNS = (0, $0f82c75150dd11fd$export$a19430ecf44d09b9).map((_, slot)=>({
        face: 4,
        u: 0.5 + slot % 5,
        v: slot < 5 ? 3.5 : 1.5,
        dir: 3
    }));
// Three pairs walking straight at each other down the middle three lanes of the
// front face — they meet around v = 2.5, in full view of the resting camera,
// and the deflections then push them into each other's lanes for a second round.
const $ad2bcec7a0192558$var$COLLISION_SPAWNS = [
    {
        face: 4,
        u: 1.5,
        v: 4.5,
        dir: 3
    },
    {
        face: 4,
        u: 1.5,
        v: 0.5,
        dir: 1
    },
    {
        face: 4,
        u: 2.5,
        v: 4.5,
        dir: 3
    },
    {
        face: 4,
        u: 2.5,
        v: 0.5,
        dir: 1
    },
    {
        face: 4,
        u: 3.5,
        v: 4.5,
        dir: 3
    },
    {
        face: 4,
        u: 3.5,
        v: 0.5,
        dir: 1
    }
];
// Plan 14's roster behaviours, one kind per lane so their damage patterns can
// be compared side by side under the face-on resting camera. They all walk +u
// across the front face and never share a lane, so nothing here is a collision
// test. Top to bottom: spike (stops on *every* healthy block, so it chews a
// solid trail), standard (the same speed budget but a coin flip, so it leaves
// gaps), fat (its 8-star bite widens each stop into a blob), spy (walks three
// blocks marking them, then eats all three at once — from a standstill three
// blocks back, which is the giveaway).
const $ad2bcec7a0192558$var$BEHAVIOUR_SPAWNS = [
    [
        "spike",
        {
            face: 4,
            u: 0.5,
            v: 4.5,
            dir: 0
        }
    ],
    [
        "standard",
        {
            face: 4,
            u: 0.5,
            v: 3.5,
            dir: 0
        }
    ],
    [
        "fat",
        {
            face: 4,
            u: 0.5,
            v: 2.5,
            dir: 0
        }
    ],
    [
        "spy",
        {
            face: 4,
            u: 0.5,
            v: 0.5,
            dir: 0
        }
    ]
];
// `?debug=crater` digs a crater into the front face at startup — the visual
// check for plan 11's depth texturing (tools/shot-onion.mjs). Random decay is
// no good for this: over 90 s the apples infect plenty of blocks but almost
// never land the six bites one block needs to be eaten through, so the magma
// layer never gets uncovered. Digging it directly is deterministic and takes a
// frame. Cells are (x, y, z) with z = 4 the front face (00-architecture.md §7).
const $ad2bcec7a0192558$var$CRATER_HOLES = [
    // A 2x2 bite out of the crust: its floor is the depth-1 magma layer.
    [
        1,
        3,
        4
    ],
    [
        2,
        3,
        4
    ],
    [
        1,
        2,
        4
    ],
    [
        2,
        2,
        4
    ],
    // One column dug a layer deeper, to expose the molten core cell behind it.
    [
        3,
        1,
        4
    ],
    [
        3,
        1,
        3
    ]
];
// Left part-eaten, so the scorch tint sits next to the holes for comparison.
const $ad2bcec7a0192558$var$CRATER_INFECTED = [
    [
        0,
        4,
        4
    ],
    [
        4,
        4,
        4
    ],
    [
        0,
        0,
        4
    ]
];
// Orientation is always a snapped axis-aligned matrix; a step animates a
// quarter-turn around the *world* X or Y axis on top of it, then bakes in.
// The shell's menu turns feed the same machinery a synthetic Step, with a
// completion callback so the page flips exactly when the turn lands.
const $ad2bcec7a0192558$var$TURN_DURATION = 0.3; // seconds per quarter-turn
let $ad2bcec7a0192558$var$base = (0, $1669b27dd88d6d65$export$f0954fd7d5368655)();
let $ad2bcec7a0192558$var$anim = null;
let $ad2bcec7a0192558$var$model = $ad2bcec7a0192558$var$base; // model matrix of the last rendered frame, for picking
// The one place a quarter-turn begins — the in-game swipe/key queue and the
// shell's menu turns both land here, so one hook covers the rotate sound for
// game and menu, as in the original (`Cube.cs:3387`, `:3478-3536`).
function $ad2bcec7a0192558$var$startTurn(step, done) {
    $ad2bcec7a0192558$var$anim = {
        step: step,
        t: 0,
        done: done
    };
    $ad2bcec7a0192558$var$director.cubeRotateStarted();
}
// The current run. Everything per-run (world, cube meshes, dirty-subscription)
// lives in the session so a menu-started game replaces it wholesale (plan 17).
let $ad2bcec7a0192558$var$session = null;
function $ad2bcec7a0192558$var$addDefaultApples(world) {
    for (const pose of $ad2bcec7a0192558$var$APPLE_SPAWNS)world.spawners.add(pose);
}
const $ad2bcec7a0192558$var$seed = ()=>Date.now() >>> 0;
// How long the DOM overlay's CSS opacity transition runs (index.html #fade);
// the port's `InOutFader`. The timeout adds a small margin so the callback
// never beats the transition's end.
const $ad2bcec7a0192558$var$FADE_SECONDS = 0.4;
let $ad2bcec7a0192558$var$shell = null;
if ($ad2bcec7a0192558$var$debug === null || $ad2bcec7a0192558$var$debug === "menu") $ad2bcec7a0192558$var$shell = new (0, $04f247d236263f2f$export$794614fa392eb573)({
    buildMenuSession: ()=>{
        // The unattended demo game behind the text: the demo ramp and the
        // standard two apples; no command is ever pushed into it. It decays
        // forever, as the original's menu cube does — nothing repairs it and
        // its gameLost/gameWon are ignored (nothing subscribes).
        $ad2bcec7a0192558$var$session = (0, $feb973a4c3cee1a6$export$f7c62c73429afa28)($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$seed(), {
            levels: (0, $0f82c75150dd11fd$export$fd8ee1a0605533d4)
        });
        $ad2bcec7a0192558$var$addDefaultApples($ad2bcec7a0192558$var$session.world);
        // Summer.mp3 once over the demo game, as the menu scene boots
        // (`MainMenu.unity:8552`); the demo's bug SFX play, its block decay
        // stays silent (the director's in-game gate).
        $ad2bcec7a0192558$var$director.attachMenu($ad2bcec7a0192558$var$session.world);
        return $ad2bcec7a0192558$var$session;
    },
    buildGameSession: (difficulty)=>{
        $ad2bcec7a0192558$var$session = (0, $feb973a4c3cee1a6$export$f7c62c73429afa28)($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$seed(), {
            difficulty: difficulty
        });
        $ad2bcec7a0192558$var$addDefaultApples($ad2bcec7a0192558$var$session.world);
        $ad2bcec7a0192558$var$director.attachGame($ad2bcec7a0192558$var$session.world, difficulty);
        return $ad2bcec7a0192558$var$session;
    },
    rotate: (dir, done)=>{
        $ad2bcec7a0192558$var$startTurn({
            axis: "y",
            dir: dir
        }, done);
    },
    fadeOut: (done)=>{
        $ad2bcec7a0192558$var$fadeEl.style.opacity = "1";
        window.setTimeout(done, $ad2bcec7a0192558$var$FADE_SECONDS * 1000 + 50);
    },
    fadeIn: ()=>{
        $ad2bcec7a0192558$var$fadeEl.style.opacity = "0";
    },
    resetOrientation: ()=>{
        $ad2bcec7a0192558$var$base = (0, $1669b27dd88d6d65$export$f0954fd7d5368655)();
        $ad2bcec7a0192558$var$anim = null;
    }
});
else {
    // Direct boot: the pre-17 behaviour every shot tool depends on — a running
    // world at easy difficulty on the main table, populated per scenario.
    const boot = (0, $feb973a4c3cee1a6$export$f7c62c73429afa28)($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$seed(), {
        difficulty: (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[0]
    });
    $ad2bcec7a0192558$var$session = boot;
    const world = boot.world;
    // Debug scenarios are running games, so they get the in-game mapping; with
    // no unlock gesture in a headless run the context stays suspended and the
    // director only logs.
    $ad2bcec7a0192558$var$director.attachGame(world, (0, $0f82c75150dd11fd$export$47c13fcaa458852e)[0]);
    if ($ad2bcec7a0192558$var$roster) for (const [kind, pose] of $ad2bcec7a0192558$var$ROSTER_SPAWNS)world.bugs.spawn(kind, pose);
    else if ($ad2bcec7a0192558$var$behaviours) for (const [kind, pose] of $ad2bcec7a0192558$var$BEHAVIOUR_SPAWNS)// Start every lane hungry. `tilesUntilInfect` is normally a 1..19 draw, so
    // the slow fat bug would otherwise walk off the front face before its first
    // bite and the shot would miss its blob entirely. Zeroing it does not skip
    // any decision — the coin flip still runs per tile, which is exactly what
    // keeps the standard lane visibly patchier than the spike's.
    world.bugs.spawn(kind, pose).tilesUntilInfect = 0;
    else if ($ad2bcec7a0192558$var$collision) for (const pose of $ad2bcec7a0192558$var$COLLISION_SPAWNS)world.bugs.spawn("standard", pose);
    else if ($ad2bcec7a0192558$var$powerups) ;
    else $ad2bcec7a0192558$var$addDefaultApples(world);
    if ($ad2bcec7a0192558$var$debug === "crater") {
        // One touch infects; the block then decays to `ghost` on its own countdown,
        // so the holes appear INFECTED_SECONDS into the run rather than instantly.
        for (const [x, y, z] of $ad2bcec7a0192558$var$CRATER_HOLES)world.blocks.infect(x, y, z);
        // Re-infected on a timer so the scorch tint is on screen when the shot is
        // taken — a single touch would have decayed away long before.
        const keepInfected = ()=>{
            for (const [x, y, z] of $ad2bcec7a0192558$var$CRATER_INFECTED)world.blocks.infect(x, y, z);
        };
        keepInfected();
        setInterval(keepInfected, 200);
    }
    // `?debug=occupancy` dumps the tile claims once a second — the cheap window
    // into the collision system (the plan explicitly picks logging over shader work).
    if ($ad2bcec7a0192558$var$debug === "occupancy") {
        let logged = 0;
        world.events.on("bugRemoved", ({ id: id })=>console.log(`bug ${id} removed, claims dropped`));
        setInterval(()=>{
            const claims = [
                ...world.bugs.occupancy.tiles
            ].map(([tile, id])=>`${tile}:${id}`);
            // Population is the number plan 06 cares about: it must climb to the
            // level's MaxAmountOfBugs and hold there.
            console.log(`t=${logged += 1}s bugs=${world.bugs.bugs.size} apples=${world.spawners.spawners.size}` + ` claims [${claims.join(" ")}]`);
        }, 1000);
    }
    if ($ad2bcec7a0192558$var$debug === "interaction") {
        // The score/combo events have no HUD yet, so the log is where they show up.
        world.events.on("scored", (e)=>console.info(`scored ${e.points} x${e.multiplier}`));
        world.events.on("bugHit", (e)=>console.info(`bug ${e.id} hit ${e.hitsTaken}x, still alive`));
        world.events.on("combo", (e)=>console.info(`combo of ${e.length}`));
        world.events.on("repairBlocked", (e)=>console.info(`repair blocked at ${e.cell.join(",")}`));
        world.events.on("powerUpTaken", ({ id: id, slot: slot, points: points })=>{
            console.info(`power-up ${id} taken slot=${slot} (${(0, $0f82c75150dd11fd$export$8c930e56a7009e81)[slot].effect}) +${points}`);
        });
        globalThis.buggsDebug = {
            bugs: ()=>{
                const out = [];
                for (const bug of world.bugs.bugs.values()){
                    if (bug.state.kind !== "running" && bug.state.kind !== "infecting") continue;
                    const frame = (0, $267a74da153328b6$export$93c280f99766aeb9)(bug);
                    if (!$ad2bcec7a0192558$var$facesCamera(frame.normal)) continue;
                    const at = $ad2bcec7a0192558$var$toScreen(frame.pos);
                    if (at) out.push({
                        ...at,
                        id: bug.id,
                        kind: bug.kindDef
                    });
                }
                return out;
            },
            damaged: ()=>{
                const out = [];
                for (const face of (0, $733d112f9a19cd2b$export$6349a8ed6fe8449e)){
                    for(let v = 0; v < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); v++)for(let u = 0; u < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); u++){
                        const pose = {
                            face: face,
                            u: u + 0.5,
                            v: v + 0.5,
                            dir: 0
                        };
                        const c = (0, $733d112f9a19cd2b$export$1a4cb2db2ce65a31)(pose);
                        if (world.blocks.state(c.x, c.y, c.z) === "healthy") continue;
                        const frame = (0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)(pose);
                        if (!$ad2bcec7a0192558$var$facesCamera(frame.normal)) continue;
                        const at = $ad2bcec7a0192558$var$toScreen(frame.pos);
                        if (at) out.push({
                            ...at,
                            cell: [
                                c.x,
                                c.y,
                                c.z
                            ]
                        });
                    }
                }
                return out;
            },
            powerups: ()=>{
                const out = [];
                for (const p of world.powerups.powerups.values()){
                    if (!(0, $08dfdfd795a13c32$export$ceb0abbf2403c9de)(p.state)) continue;
                    const frame = (0, $08dfdfd795a13c32$export$94b2b7bc7de2d125)(p);
                    if (!$ad2bcec7a0192558$var$facesCamera(frame.normal)) continue;
                    const at = $ad2bcec7a0192558$var$toScreen(frame.pos);
                    if (at) out.push({
                        ...at,
                        id: p.id,
                        slot: p.slot
                    });
                }
                return out;
            },
            spawnPowerUp: (slot)=>{
                for(let v = 0; v < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); v++)for(let u = 0; u < (0, $733d112f9a19cd2b$export$a07804094c3ecf2d); u++){
                    const placed = world.powerups.place(slot, 4, u, v);
                    if (placed) return placed.id;
                }
                return null;
            },
            audioLog: ()=>({
                    locked: $ad2bcec7a0192558$var$audio.locked,
                    entries: $ad2bcec7a0192558$var$director.audioLog()
                })
        };
    }
    // The behaviours scenario has nothing to *see* for the spy's marks — the
    // original draws a `FieldMarker` prefab per mark and this port deliberately
    // draws nothing (plan 14, out of scope) — so the sim narrates them instead.
    // That, plus a line per bite, is what makes the shot readable: the trail on
    // screen shows what each kind ate, the log shows why.
    if ($ad2bcec7a0192558$var$behaviours) {
        world.events.on("bugMarksChanged", ({ id: id, marks: marks })=>{
            const kind = world.bugs.bugs.get(id)?.kindDef ?? "?";
            const where = marks.map((c)=>`(${c.join(",")})`).join(" ");
            console.info(`t=${world.time.toFixed(1)}s ${kind} ${id} marks=${marks.length} ${where}`.trimEnd());
        });
        world.events.on("blockChanged", ({ x: x, y: y, z: z, state: state })=>{
            if (state === "infected") console.info(`t=${world.time.toFixed(1)}s bitten (${x},${y},${z})`);
        });
    }
    // `?debug=sim` reports the run's vital signs once a second: how much of the
    // cube is still healthy (plan 07, and what the ≤8% lose check reads), plus the
    // level, its speed factor and the per-kind population — the numbers that make
    // plan 15's difficulty ramp legible in `tools/shot-levels.mjs`.
    if ($ad2bcec7a0192558$var$debug === "sim") {
        world.events.on("levelChanged", ({ level: level, def: def })=>{
            console.info(`t=${world.time.toFixed(1)}s level=${level} speedFactor=${def.speedFactor}`);
        });
        // Plan 16: power-up spawns, deaths-by-bug and collections, plus the active
        // modifiers in the 1 Hz line below — what makes the system legible in
        // `tools/shot-powerups.mjs`'s default-page run.
        world.events.on("powerUpSpawned", ({ id: id, slot: slot })=>{
            console.info(`t=${world.time.toFixed(1)}s power-up ${id} spawned slot=${slot} (${(0, $0f82c75150dd11fd$export$8c930e56a7009e81)[slot].effect})`);
        });
        world.events.on("powerUpStateChanged", ({ id: id, state: state })=>{
            if (state === "killed") console.info(`t=${world.time.toFixed(1)}s power-up ${id} trampled by a bug`);
        });
        world.events.on("powerUpTaken", ({ id: id, slot: slot, points: points })=>{
            console.info(`t=${world.time.toFixed(1)}s power-up ${id} taken slot=${slot} (${(0, $0f82c75150dd11fd$export$8c930e56a7009e81)[slot].effect}) +${points}`);
        });
        world.events.on("gameWon", ({ level: level })=>console.info(`t=${world.time.toFixed(1)}s GAME WON after level ${level}`));
        world.events.on("gameLost", ({ healthyFraction: healthyFraction })=>{
            console.info(`t=${world.time.toFixed(1)}s GAME LOST at healthyFraction=${healthyFraction.toFixed(3)}`);
        });
        setInterval(()=>{
            const healthy = world.blocks.healthyFraction().toFixed(3);
            const counts = new Map();
            for (const bug of world.bugs.bugs.values())counts.set(bug.kindDef, (counts.get(bug.kindDef) ?? 0) + 1);
            const pop = [
                ...counts
            ].map(([kind, n])=>`${kind}:${n}`).join(",");
            const mods = world.modifiers;
            console.info(`t=${world.time.toFixed(1)}s level=${world.levels.level}` + ` speed=${world.levels.speedFactor.toFixed(2)} healthyFraction=${healthy}` + ` bugs=${world.bugs.bugs.size} [${pop}]` + ` powerups=${world.powerups.powerups.size}` + ` mods[x${mods.speedFactor} spawn x${mods.spawnRate}${mods.frozen ? " FROZEN" : ""}]`);
        }, 1000);
    }
}
if ($ad2bcec7a0192558$var$debug === "menu" && $ad2bcec7a0192558$var$shell !== null) {
    const liveShell = $ad2bcec7a0192558$var$shell;
    globalThis.buggsDebug = {
        menu: ()=>{
            const page = liveShell.activePage;
            const rects = page !== null && $ad2bcec7a0192558$var$menuRenderer !== null && $ad2bcec7a0192558$var$anim === null ? $ad2bcec7a0192558$var$menuRenderer.itemRects(page, $ad2bcec7a0192558$var$model, $ad2bcec7a0192558$var$canvas.getBoundingClientRect()) : [];
            return {
                state: liveShell.state.kind,
                page: page,
                items: rects
            };
        },
        audioLog: ()=>({
                locked: $ad2bcec7a0192558$var$audio.locked,
                entries: $ad2bcec7a0192558$var$director.audioLog()
            })
    };
}
/** Cube-local point to client-space pixels, or null if it is behind the camera. */ function $ad2bcec7a0192558$var$toScreen(point) {
    return (0, $3cc3165f22f27637$export$b230084cdcd5952a)(point, $ad2bcec7a0192558$var$model, $ad2bcec7a0192558$var$canvas.getBoundingClientRect());
}
/**
 * True while a surface normal points back at the camera. The cube is convex, so
 * for a point on its surface that is the whole visibility test — no occlusion
 * to worry about beyond the bugs themselves.
 */ function $ad2bcec7a0192558$var$facesCamera(normal) {
    const m = $ad2bcec7a0192558$var$model;
    return m[2] * normal[0] + m[6] * normal[1] + m[10] * normal[2] > 0.3;
}
// The player's two verbs, plus the menu's one. In menu states a tap hit-tests
// the active page's item rects (nothing is tappable while a press animation or
// a turn runs); in `running` a tap picks against the sim exactly as before: a
// tap on a bug hurts it, a tap on a damaged block repairs it — through the
// hole if the ray went through one. Healthy grass does nothing, as in the
// original (`Cube.MouseInteractionWithObjects` breaks out of its hit list on
// the first Healthy block). Sim taps go through the command queue: the sim
// applies them at the top of the next fixed step.
$ad2bcec7a0192558$var$input.onTap = ({ x: x, y: y })=>{
    if ($ad2bcec7a0192558$var$session === null) return;
    if ($ad2bcec7a0192558$var$shell !== null && !$ad2bcec7a0192558$var$shell.tapsToSim) {
        const page = $ad2bcec7a0192558$var$shell.activePage;
        if (page === null || $ad2bcec7a0192558$var$anim !== null || $ad2bcec7a0192558$var$menuRenderer === null) return;
        const rects = $ad2bcec7a0192558$var$menuRenderer.itemRects(page, $ad2bcec7a0192558$var$model, $ad2bcec7a0192558$var$canvas.getBoundingClientRect());
        const item = (0, $5f961402d6b14b96$export$32174ac2df02c060)(rects, x, y);
        if (item !== null) $ad2bcec7a0192558$var$shell.tapItem(item);
        return;
    }
    const world = $ad2bcec7a0192558$var$session.world;
    // `PickableWorld` is the narrow read-only view `picking.ts` needs; `World`
    // wraps its entities in managers, so hand over the maps themselves.
    const hit = (0, $e9eec882a992d2bf$export$357523c63a2253b9)(x, y, $ad2bcec7a0192558$var$canvas, $ad2bcec7a0192558$var$model, {
        blocks: world.blocks,
        bugs: world.bugs.bugs,
        powerups: world.powerups.powerups
    });
    if (hit === null) return;
    if (hit.kind === "bug") {
        world.commands.push({
            kind: "tapBug",
            id: hit.id
        });
        return;
    }
    if (hit.kind === "powerup") {
        world.commands.push({
            kind: "tapPowerUp",
            id: hit.id
        });
        return;
    }
    // `pick` only ever returns a damaged cell — it stops at intact grass, as the
    // original's hit-list loop does — so there is nothing left to filter here.
    world.commands.push({
        kind: "repairBlock",
        cell: hit.cell
    });
};
function $ad2bcec7a0192558$var$easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
// After a quarter-turn every entry is exactly -1, 0, or 1; rounding kills
// accumulated float drift so the resting cube is always perfectly face-on.
function $ad2bcec7a0192558$var$snap(m) {
    const out = new Float32Array(16);
    for(let i = 0; i < 16; i++)out[i] = Math.round(m[i]);
    return out;
}
function $ad2bcec7a0192558$var$currentModel(dt) {
    if (!$ad2bcec7a0192558$var$anim) {
        const step = $ad2bcec7a0192558$var$input.next();
        // Swipes only rotate the cube while a game is running; in menu states a
        // dequeued step is simply dropped (the original ignores swipes in menu
        // mode). Shell-initiated turns arrive via `rotate` above, not this queue.
        if (step && ($ad2bcec7a0192558$var$shell === null || $ad2bcec7a0192558$var$shell.swipesToSim)) $ad2bcec7a0192558$var$startTurn(step, null);
    }
    if (!$ad2bcec7a0192558$var$anim) return $ad2bcec7a0192558$var$base;
    $ad2bcec7a0192558$var$anim.t += dt / $ad2bcec7a0192558$var$TURN_DURATION;
    const { axis: axis, dir: dir } = $ad2bcec7a0192558$var$anim.step;
    if ($ad2bcec7a0192558$var$anim.t >= 1) {
        const full = axis === "x" ? (0, $1669b27dd88d6d65$export$446bfe4478ff8d71)(dir * Math.PI / 2) : (0, $1669b27dd88d6d65$export$122a03c894ebb63)(dir * Math.PI / 2);
        $ad2bcec7a0192558$var$base = $ad2bcec7a0192558$var$snap((0, $1669b27dd88d6d65$export$2060d2db72cce88f)(full, $ad2bcec7a0192558$var$base));
        const done = $ad2bcec7a0192558$var$anim.done;
        $ad2bcec7a0192558$var$anim = null;
        done?.();
        return $ad2bcec7a0192558$var$base;
    }
    const angle = $ad2bcec7a0192558$var$easeInOutCubic($ad2bcec7a0192558$var$anim.t) * (Math.PI / 2) * dir;
    const spin = axis === "x" ? (0, $1669b27dd88d6d65$export$446bfe4478ff8d71)(angle) : (0, $1669b27dd88d6d65$export$122a03c894ebb63)(angle);
    return (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(spin, $ad2bcec7a0192558$var$base);
}
function $ad2bcec7a0192558$var$resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.floor($ad2bcec7a0192558$var$canvas.clientWidth * dpr);
    const h = Math.floor($ad2bcec7a0192558$var$canvas.clientHeight * dpr);
    if ($ad2bcec7a0192558$var$canvas.width !== w || $ad2bcec7a0192558$var$canvas.height !== h) {
        $ad2bcec7a0192558$var$canvas.width = w;
        $ad2bcec7a0192558$var$canvas.height = h;
    }
}
$ad2bcec7a0192558$var$gl.enable($ad2bcec7a0192558$var$gl.DEPTH_TEST);
$ad2bcec7a0192558$var$gl.enable($ad2bcec7a0192558$var$gl.CULL_FACE);
$ad2bcec7a0192558$var$gl.clearColor(0, 0, 0, 0); // transparent — the page provides the sky gradient
let $ad2bcec7a0192558$var$acc = 0;
let $ad2bcec7a0192558$var$lastTime = performance.now();
function $ad2bcec7a0192558$var$frame(now) {
    const dt = Math.min((now - $ad2bcec7a0192558$var$lastTime) / 1000, 0.1);
    $ad2bcec7a0192558$var$lastTime = now;
    // The press-animation clock; on clip end the shell fires the item's action
    // (which may swap `session` or start a turn before the sim steps below).
    $ad2bcec7a0192558$var$shell?.update(dt);
    const live = $ad2bcec7a0192558$var$session;
    if (live === null) {
        requestAnimationFrame($ad2bcec7a0192558$var$frame);
        return;
    }
    // Clamped above, so a tab suspend costs at most 0.1 s of catch-up steps.
    // The simulation runs on a fixed timestep, decoupled from the render frame
    // rate; rendering reads the latest sim state each animation frame.
    $ad2bcec7a0192558$var$acc += dt;
    const drained = (0, $1dc1f21cb4eacbbb$export$bb93bf71f466a028)($ad2bcec7a0192558$var$acc, (0, $1dc1f21cb4eacbbb$export$9d55dbe2e6715cc9));
    $ad2bcec7a0192558$var$acc = drained.rest;
    for(let i = 0; i < drained.steps; i++)(0, $1dc1f21cb4eacbbb$export$a0e9724da95b17d)(live.world);
    live.rebuildIfDirty();
    $ad2bcec7a0192558$var$resize();
    $ad2bcec7a0192558$var$gl.viewport(0, 0, $ad2bcec7a0192558$var$canvas.width, $ad2bcec7a0192558$var$canvas.height);
    $ad2bcec7a0192558$var$gl.clear($ad2bcec7a0192558$var$gl.COLOR_BUFFER_BIT | $ad2bcec7a0192558$var$gl.DEPTH_BUFFER_BIT);
    const cam = (0, $238457a12dbae9c5$export$8b7b81ac271094c6)($ad2bcec7a0192558$var$canvas.width / $ad2bcec7a0192558$var$canvas.height);
    const proj = (0, $1669b27dd88d6d65$export$541149539f3a4684)(cam.fovY, cam.aspect, 0.1, 100);
    const view = (0, $1669b27dd88d6d65$export$a984e488de492859)(0, 0, -cam.dist);
    const viewProj = (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(proj, view);
    $ad2bcec7a0192558$var$model = $ad2bcec7a0192558$var$currentModel(dt);
    const mvp = (0, $1669b27dd88d6d65$export$2060d2db72cce88f)(viewProj, $ad2bcec7a0192558$var$model);
    const cube = live.cube;
    $ad2bcec7a0192558$var$gl.useProgram($ad2bcec7a0192558$var$program);
    $ad2bcec7a0192558$var$gl.uniformMatrix4fv($ad2bcec7a0192558$var$uMvp, false, mvp);
    $ad2bcec7a0192558$var$gl.uniformMatrix4fv($ad2bcec7a0192558$var$uModel, false, $ad2bcec7a0192558$var$model);
    $ad2bcec7a0192558$var$gl.activeTexture($ad2bcec7a0192558$var$gl.TEXTURE0);
    $ad2bcec7a0192558$var$gl.bindTexture($ad2bcec7a0192558$var$gl.TEXTURE_2D, $ad2bcec7a0192558$var$atlasTex);
    $ad2bcec7a0192558$var$gl.bindVertexArray(cube.vao);
    $ad2bcec7a0192558$var$gl.drawElements($ad2bcec7a0192558$var$gl.TRIANGLES, cube.indexCount, $ad2bcec7a0192558$var$gl.UNSIGNED_SHORT, 0);
    // Bugs are advanced by the fixed-step sim above; the renderer only reads.
    if ($ad2bcec7a0192558$var$bugRenderer) $ad2bcec7a0192558$var$bugRenderer.draw($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$modelRenderer, live.world.bugs.bugs, $ad2bcec7a0192558$var$model, viewProj);
    if ($ad2bcec7a0192558$var$spawnerRenderer) $ad2bcec7a0192558$var$spawnerRenderer.draw($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$modelRenderer, live.world.spawners.spawners, $ad2bcec7a0192558$var$model, viewProj, now / 1000);
    if ($ad2bcec7a0192558$var$powerUpRenderer) $ad2bcec7a0192558$var$powerUpRenderer.draw($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$modelRenderer, live.world.powerups.powerups, $ad2bcec7a0192558$var$model, viewProj, now / 1000);
    if ($ad2bcec7a0192558$var$roster && $ad2bcec7a0192558$var$models) {
        const apple = $ad2bcec7a0192558$var$models.apple;
        $ad2bcec7a0192558$var$modelRenderer.draw($ad2bcec7a0192558$var$gl, apple, (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(apple, "idle", now / 1000, true), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)($ad2bcec7a0192558$var$model, (0, $1886a5c91af384ee$export$ee41df9543d2bc3f)((0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)($ad2bcec7a0192558$var$ROSTER_APPLE), "apple", 1)), viewProj);
    }
    if ($ad2bcec7a0192558$var$powerups && $ad2bcec7a0192558$var$models) for(let slot = 0; slot < (0, $0f82c75150dd11fd$export$a19430ecf44d09b9).length; slot++){
        const name = (0, $0f82c75150dd11fd$export$a19430ecf44d09b9)[slot];
        const plant = $ad2bcec7a0192558$var$models[name];
        $ad2bcec7a0192558$var$modelRenderer.draw($ad2bcec7a0192558$var$gl, plant, (0, $0688557bb19ac86e$export$8971a1d39538b7c1)(plant, "idle", now / 1000, true), (0, $1669b27dd88d6d65$export$2060d2db72cce88f)($ad2bcec7a0192558$var$model, (0, $1886a5c91af384ee$export$ee41df9543d2bc3f)((0, $733d112f9a19cd2b$export$fae1f8cf88817f3a)($ad2bcec7a0192558$var$POWERUP_SPAWNS[slot]), name, 1)), viewProj);
    }
    // The menu pages and title, riding the cube's model matrix; drawn until the
    // shell reaches `running` (the fade overlay covers the `starting` swap).
    if ($ad2bcec7a0192558$var$shell !== null && $ad2bcec7a0192558$var$shell.state.kind !== "running" && $ad2bcec7a0192558$var$menuRenderer !== null) $ad2bcec7a0192558$var$menuRenderer.draw($ad2bcec7a0192558$var$gl, $ad2bcec7a0192558$var$modelRenderer, $ad2bcec7a0192558$var$shell.state, $ad2bcec7a0192558$var$model, viewProj);
    // Ghost shells last, blended. Depth *test* stays on so shells behind solid
    // blocks are hidden, but depth *writes* are off so overlapping shells do not
    // cull each other. Ghost-on-ghost blending order is therefore wrong from some
    // angles; at <= 125 shells that is not worth a per-face sort, so do not
    // "fix" it by sorting — it is a deliberate trade.
    const ghosts = live.ghosts;
    if (ghosts.indexCount > 0) {
        $ad2bcec7a0192558$var$gl.useProgram($ad2bcec7a0192558$var$ghostProgram);
        $ad2bcec7a0192558$var$gl.uniformMatrix4fv($ad2bcec7a0192558$var$uGhostMvp, false, mvp);
        $ad2bcec7a0192558$var$gl.uniformMatrix4fv($ad2bcec7a0192558$var$uGhostModel, false, $ad2bcec7a0192558$var$model);
        $ad2bcec7a0192558$var$gl.enable($ad2bcec7a0192558$var$gl.BLEND);
        $ad2bcec7a0192558$var$gl.blendFunc($ad2bcec7a0192558$var$gl.SRC_ALPHA, $ad2bcec7a0192558$var$gl.ONE_MINUS_SRC_ALPHA);
        $ad2bcec7a0192558$var$gl.depthMask(false);
        $ad2bcec7a0192558$var$gl.disable($ad2bcec7a0192558$var$gl.CULL_FACE); // a shell is hollow; both sides should show
        $ad2bcec7a0192558$var$gl.bindVertexArray(ghosts.vao);
        $ad2bcec7a0192558$var$gl.drawElements($ad2bcec7a0192558$var$gl.TRIANGLES, ghosts.indexCount, $ad2bcec7a0192558$var$gl.UNSIGNED_SHORT, 0);
        $ad2bcec7a0192558$var$gl.enable($ad2bcec7a0192558$var$gl.CULL_FACE);
        $ad2bcec7a0192558$var$gl.depthMask(true);
        $ad2bcec7a0192558$var$gl.disable($ad2bcec7a0192558$var$gl.BLEND);
    }
    requestAnimationFrame($ad2bcec7a0192558$var$frame);
}
requestAnimationFrame($ad2bcec7a0192558$var$frame);


//# sourceMappingURL=buggs.d0fc985f.js.map
